import { calendarDateKey } from './components/mobileCalendarCounts';
import { getDeliveriesInRange } from '../../lib/supabase/deliverySchedule';
import MobileJobActions from './components/MobileJobActions';
import useMobileSchedule from '../../hooks/useMobileSchedule';
import MobileScheduleMoveDialog from './components/MobileScheduleMoveDialog';
// src/pages/Schedule/DeliverySchedule.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    FiCalendar, FiChevronLeft, FiChevronRight, FiX, FiMove, FiHash, FiUser,
    FiPackage, FiTruck, FiUsers, FiClock, FiLayers, FiHome, FiCheckCircle,
    FiChevronsLeft, FiChevronsRight, FiMaximize, FiMinimize, FiEdit3, FiInfo,
    FiDollarSign, FiSearch, FiMessageSquare, FiPlus, FiEdit2, FiTrash2, FiMapPin,
    FiTool, FiEye
} from "react-icons/fi";

import {
    DndContext,
    DragOverlay,
    useSensor,
    useSensors,
    PointerSensor,
    pointerWithin,
    MeasuringStrategy,
    useDndContext
} from '@dnd-kit/core';
import { useDraggable, useDroppable, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';

import {
    startOfWeek,
    endOfWeek,
    format,
    addWeeks,
    addDays,
    startOfDay,
    isBefore,
    differenceInWeeks,
    isToday,
    subMonths,
} from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

import ConfirmationPopup from "../common/ConfirmationPopup";
import EditAssignment from "../assignments/EditAssignment";
import { DayColumnSkeleton } from "../common/SkeletonLoader";
import Toast from '../common/Toast';

import { supabase } from "../../config/supabase";

import {
    getDeliveriesForDate,
    getUnscheduledDeliveries,
    updateDeliveryDate,
    updateWaitingStatus,
    setDeliveryGroup,
    disbandDeliveryGroup,
    setGroupDeliveryDate,
    setGroupDriverBatch,
    commitGroupMember,
    setGroupWaiting,
    setGroupPlayground,
} from "../../lib/supabase/deliverySchedule";
import { setDeliveryPlayground, updateQuoteQuickNotes } from "../../lib/supabase/schedule";
import { getDeliveryPlaygroundAssignments, getOverdueDeliveries } from "../../lib/supabase/deliverySchedule";

import {
    getCompanyDrivers,
    assignDriverToDelivery,
    removeDriverFromDelivery,
    getDriverWorkload
} from "../../lib/supabase/schedule-driver";
import {
    subscribeToJobQuotesUpdates,
    unsubscribeFromJobUpdates,
    subscribeToJobUpdates,
    subscribeToJobDeliveriesUpdates,
    subscribeToProductionUpdates
} from "../../lib/supabase/realtime";
import { forceFinish, undoDeliveryCompletion } from '../../lib/supabase/force-finish';
import { toggleWaitingJob, toggleSentProduction } from '../../lib/supabase/assignments';
import { IoWarning } from "react-icons/io5";
import { CircleCheck, ClipboardCheck, Flag, MapIcon, Truck, PartyPopper, Layers, ChevronDown, SquareStack } from "lucide-react";
import BackButton from "../common/BackButton";
import { useHolidays } from '../../hooks/useHolidays';
import ScheduleActivityLog from "./ScheduleActivityLog";
import { createJobActionLog } from "../../lib/supabase/activityLog";


import { hasPermission } from "../../lib/permissions";
import { sendDriverSMS } from "../../lib/supabase/sms";
import AssignmentContextMenu from "./components/AssignmentContextMenu";
import OnHoldPanel from "./components/OnHoldPanel";
import TextTooltip from "../common/TextTooltip";
import { getJobStatusSummary } from "../../lib/common/jobStatusText";
import {
    createNotificationWithFallback,
    NOTIFICATION_TYPES,
    NOTIFICATION_PRIORITIES
} from '../../lib/supabase/notifications';
import { uuidToShortId } from "../../lib/common/shortId";

import {
    getEnabledExtraQuoteTypes,
    getQuoteLabel,
} from "../../lib/quoteTypes";
import { GiWoodPile } from "react-icons/gi";

// ─── Overdue deliveries ────────────────────────────────────────────────────
// Scheduled deliveries from a recent past window (up to yesterday) that haven't been
// delivered yet. Dates are compared as Melbourne YYYY-MM-DD strings.
// The widest option is what gets fetched; narrower ones filter on screen, so switching is instant.
const OVERDUE_RANGE_OPTIONS = [
    { key: '1w', label: '1 week', getStart: (today) => addDays(today, -7) },
    { key: '2w', label: '2 weeks', getStart: (today) => addDays(today, -14) },
    { key: '3w', label: '3 weeks', getStart: (today) => addDays(today, -21) },
    { key: '1m', label: '1 month', getStart: (today) => subMonths(today, 1) },
];
const OVERDUE_WIDEST_RANGE = '1m';

const getOverdueRange = (rangeKey = OVERDUE_WIDEST_RANGE) => {
    const today = startOfDay(new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" })));
    const option = OVERDUE_RANGE_OPTIONS.find(o => o.key === rangeKey) || OVERDUE_RANGE_OPTIONS[OVERDUE_RANGE_OPTIONS.length - 1];
    return {
        start: format(option.getStart(today), 'yyyy-MM-dd'),
        end: format(addDays(today, -1), 'yyyy-MM-dd'),
    };
};

// Membership test for the fetched (widest) window
const isInOverdueRange = (dateStr) => {
    if (!dateStr) return false;
    const day = dateStr.slice(0, 10);
    const { start, end } = getOverdueRange();
    return day >= start && day <= end;
};

// Stable per-quote card id (draggable ids and data-locate-key are built from it)
const getCardBaseId = (card) => {
    const quoteId = card.quote_info?.quoteId || null;
    return quoteId
        ? `${card.id}-${quoteId}`
        : `${card.id}-${card.quote_info?.quote_type}-${card.quote_info?.delivery_count ?? 0}`;
};

// Same delivery-record match the cards use to decide whether they're delivered
const findCardDelivery = (card) => {
    const quoteId = card.quote_info?.quoteId || null;
    const quoteType = card.quote_info?.quote_type;
    const deliveryCount = card.quote_info?.delivery_count ?? 0;
    return (card.job_deliveries || []).find(d =>
        quoteId && d.child_unique_id
            ? d.child_unique_id === quoteId
            : d.quote_type === quoteType && (d.delivery_count ?? 0) === deliveryCount
    ) || null;
};

const isCardDelivered = (card) => findCardDelivery(card)?.delivery_status === 'delivered';

const byOverdueDate = (a, b) =>
    (a.quote_info?.expected_finish_date || '').localeCompare(b.quote_info?.expected_finish_date || '');

// Text sorts put blanks (no driver / no customer) last, then fall back to oldest first
const byTextThenDate = (getText) => (a, b) => {
    const ta = getText(a) || '';
    const tb = getText(b) || '';
    if (!ta !== !tb) return ta ? -1 : 1;
    return ta.localeCompare(tb, undefined, { sensitivity: 'base' }) || byOverdueDate(a, b);
};

const OVERDUE_SORT_OPTIONS = [
    { key: 'oldest', label: 'Oldest first', compare: byOverdueDate },
    { key: 'newest', label: 'Newest first', compare: (a, b) => byOverdueDate(b, a) },
    { key: 'driver', label: 'Driver (A–Z)', compare: byTextThenDate(card => findCardDelivery(card)?.assigned_driver_name) },
    { key: 'customer', label: 'Customer (A–Z)', compare: byTextThenDate(card => card.customer_name) },
];

const formatNoteDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) +
        ' at ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const QuickNotesButton = ({ assignment, userProfile, isOpen, onToggle, bubblePos, setScheduleData, setUnscheduledAssignments, setPlaygroundAssignments, noteDraftText = '', setNoteDraftText = () => { } }) => {
    const quoteId = assignment?.quote_info?.quoteId || assignment?.quote_info?.id;
    const notes = assignment?.quote_info?.quick_notes || [];
    const hasNotes = notes.length > 0;

    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [saving, setSaving] = useState(false);
    const btnRef = useRef(null);
    const bubbleRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (bubbleRef.current && !bubbleRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                onToggle(false, null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const openBubble = (e) => {
        e.stopPropagation();
        if (isOpen) { onToggle(false, null); return; }
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const bubbleWidth = 320;
        const bubbleHeight = 400;
        const flipLeft = rect.left + bubbleWidth > window.innerWidth - 12;
        const spaceBelow = window.innerHeight - rect.bottom;
        const showAbove = spaceBelow < bubbleHeight && rect.top > bubbleHeight;
        const pos = { x: flipLeft ? rect.right : rect.left, y: showAbove ? rect.top - 8 : rect.bottom + 8, showAbove, flipLeft };
        onToggle(true, pos);
    };

    const saveNotes = async (updated) => {
        if (!quoteId) return;
        setSaving(true);
        try {
            await updateQuoteQuickNotes(quoteId, updated);
            const patchItem = (item) => {
                const itemQuoteId = item.quote_info?.quoteId || item.quote_info?.id;
                if (itemQuoteId !== quoteId) return item;
                return { ...item, quote_info: { ...item.quote_info, quick_notes: updated } };
            };
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].map(patchItem);
                return next;
            });
            setUnscheduledAssignments(prev => prev.map(patchItem));
            setPlaygroundAssignments(prev => prev.map(patchItem));
        } catch (err) {
            console.error('Failed to save quick notes:', err);
            Toast.error('Failed to save note');
        } finally {
            setSaving(false);
        }
    };

    const handleAddNote = async () => {
        const text = noteDraftText.trim();
        if (!text) return;
        const newNote = {
            id: crypto.randomUUID(),
            note: text,
            type: assignment?.quote_info?.quote_type || null,
            added_by_id: userProfile?.id || null,
            added_by_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
            added_at: new Date().toISOString(),
            edited_at: null,
        };
        setNoteDraftText('');
        await saveNotes([...notes, newNote]);
    };

    const handleEditSave = async (noteId) => {
        const text = editingText.trim();
        if (!text) return;
        const updated = notes.map(n => n.id === noteId ? { ...n, note: text, edited_at: new Date().toISOString() } : n);
        setEditingId(null);
        await saveNotes(updated);
    };

    const handleDelete = async (noteId) => {
        await saveNotes(notes.filter(n => n.id !== noteId));
    };

    const bubbleJsx = isOpen ? (
        <div
            ref={bubbleRef}
            className="fixed z-[9990] w-80 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
            style={{
                [bubblePos.flipLeft ? 'right' : 'left']: bubblePos.flipLeft ? window.innerWidth - bubblePos.x : bubblePos.x,
                [bubblePos.showAbove ? 'bottom' : 'top']: bubblePos.showAbove ? window.innerHeight - bubblePos.y : bubblePos.y,
            }}
            onClick={e => e.stopPropagation()}
        >
            <div className="px-3 pt-3 pb-2">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Job Info</span>
                    <button type="button" onClick={() => onToggle(false, null)} className="text-gray-400 hover:text-gray-600">
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-gray-800">{assignment.unique_id}</span>
                        {assignment.quote_info?.childUniqueId && (
                            <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {assignment.quote_info.childUniqueId}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <FiUser className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-700 font-medium">{assignment.customer_name}</span>
                    </div>
                    {assignment.customer_address && (
                        <div className="flex items-start gap-2">
                            <FiMapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-xs text-gray-600">{assignment.customer_address}</span>
                        </div>
                    )}
                </div>
            </div>
            <div className="border-t border-gray-100" />
            <div className="px-3 pt-2 pb-3 max-h-64 overflow-y-auto">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Quick Notes</span>
                {notes.length === 0 && <p className="text-xs text-gray-400 italic mt-1 mb-2">No notes yet</p>}
                <div className="space-y-2 mt-1">
                    {notes.map(n => (
                        <div key={n.id} className="bg-gray-50 rounded-lg p-2 text-xs">
                            {editingId === n.id ? (
                                <div className="space-y-1">
                                    <textarea
                                        autoFocus
                                        className="w-full text-xs border border-blue-300 rounded p-1 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                        rows={2}
                                        value={editingText}
                                        onChange={e => setEditingText(e.target.value)}
                                    />
                                    <div className="flex gap-1">
                                        <button type="button" onClick={() => handleEditSave(n.id)} disabled={saving}
                                            className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px] font-medium disabled:opacity-50">Save</button>
                                        <button type="button" onClick={() => setEditingId(null)}
                                            className="px-2 py-0.5 text-gray-500 hover:text-gray-700 text-[10px]">Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-start justify-between gap-1">
                                        <p className="text-gray-800 flex-1 leading-snug">{n.note}</p>
                                        <div className="flex gap-1 flex-shrink-0">
                                            <button type="button" onClick={() => { setEditingId(n.id); setEditingText(n.note); }}
                                                className="text-gray-400 hover:text-blue-600 p-0.5">
                                                <FiEdit2 className="w-3 h-3" />
                                            </button>
                                            <button type="button" onClick={() => handleDelete(n.id)} disabled={saving}
                                                className="text-gray-400 hover:text-red-500 p-0.5 disabled:opacity-50">
                                                <FiTrash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-gray-400 mt-1">
                                        {n.added_by_name} · {formatNoteDate(n.edited_at || n.added_at)}
                                        {n.edited_at && <span className="ml-1 italic">(edited)</span>}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                <div className="mt-2 space-y-1">
                    <textarea
                        className="w-full text-xs border border-gray-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                        rows={2}
                        placeholder="Add a quick note…"
                        value={noteDraftText}
                        onChange={e => setNoteDraftText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                    />
                    <button type="button" onClick={handleAddNote} disabled={!noteDraftText.trim() || saving}
                        className="flex items-center gap-1 text-[11px] px-2 py-1 bg-blue-600 text-white rounded-lg disabled:opacity-40 hover:bg-blue-700 font-medium">
                        <FiPlus className="w-3 h-3" /> Add Note
                    </button>
                </div>
            </div>
        </div>
    ) : null;

    return (
        <>
            <button
                ref={btnRef}
                aria-label="Quick notes"
                type="button"
                className={`p-1.5 rounded-lg border transition-all shadow-md ${hasNotes
                    ? 'bg-blue-500 text-white border-blue-600 hover:bg-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                onClick={openBubble}
            >
                <FiMessageSquare className="w-3.5 h-3.5" />
            </button>
            {createPortal(bubbleJsx, document.body)}
        </>
    );
};

// Custom collision detection.
// Day columns have overflow-y scroll so their getBoundingClientRect() extends well
// below the visible area. pointerWithin always hits a day column even when the pointer
// is physically over the waiting/playground panel below.
// Fix: check the actual rects of the two zone droppables first — if the pointer is
// inside either zone's real rect, return that zone immediately.
const deliveryScheduleCollision = (args) => {
    const px = args.pointerCoordinates?.x ?? 0;
    const py = args.pointerCoordinates?.y ?? 0;

    // overdue-zone is a no-op target: it only stops drops over it from landing on a day column
    const zoneIds = ['unscheduled-zone', 'playground-zone', 'overdue-zone'];
    for (const id of zoneIds) {
        const container = args.droppableContainers.find(c => c.id === id && !c.disabled);
        if (!container) continue;
        const rect = container.rect.current;
        if (!rect) continue;
        if (px >= rect.left && px <= rect.right && py >= rect.top && py <= rect.bottom) {
            return [{ id }];
        }
    }

    return pointerWithin(args);
};

// Wraps the overdue section so drops over it are ignored (see deliveryScheduleCollision)
const OverdueDropGuard = ({ className, disabled, children }) => {
    const { setNodeRef } = useDroppable({ id: 'overdue-zone', disabled });
    return <div ref={setNodeRef} className={className}>{children}</div>;
};

// ─── GroupContainer ────────────────────────────────────────────────────────
// Defined at MODULE LEVEL so React reconciles it in-place across parent
// re-renders instead of unmounting/remounting. When it was defined inside the
// parent function, every parent render created a new function reference → React
// saw a different component type → unmount+remount → CSS transitions had no
// "before" state → instant snap (no animation). At module level the function
// reference is stable and the DOM elements persist, so grid-template-rows
// transitions work correctly.
const GroupContainer = ({
    groupId, groupName, items, isOpen, onToggleOpen, onEditGroup,
    isEditing, isAdmin, viewingMode, drivers,
    onDateSet, onDriverSet, onDisband, onCommit, onToggleCard,
    renderCard, idPrefix = '', compact = false, onLocate = null, locateLabel = '',
}) => {
    const isMobile = useMobileSchedule();
    const [showDatePicker, setShowDatePicker] = React.useState(false);
    const [showDriverPicker, setShowDriverPicker] = React.useState(false);
    const [groupDate, setGroupDate] = React.useState('');
    const datePickerRef = React.useRef(null);
    const driverPickerRef = React.useRef(null);

    const contentRef = React.useRef(null);
    const [contentHeight, setContentHeight] = React.useState(0);

    const [shouldAnimate, setShouldAnimate] = React.useState(false);

    const groupDateDraggable = useDraggable({
        id: `${idPrefix}group-date-${groupId}`,
        data: { handleType: 'group-date', groupId, groupMembers: items },
        disabled: isMobile || !isAdmin || !!viewingMode,
    });
    const groupDriverDraggable = useDraggable({
        id: `${idPrefix}group-driver-${groupId}`,
        data: { handleType: 'group-driver', groupId, groupMembers: items },
        disabled: isMobile || !isAdmin || !!viewingMode,
    });

    React.useEffect(() => {
        const handler = (e) => {
            if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false);
            if (driverPickerRef.current && !driverPickerRef.current.contains(e.target)) setShowDriverPicker(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    React.useEffect(() => {
        if (!contentRef.current) return;

        if (isOpen) {
            // Opening → measure and animate
            const h = contentRef.current.scrollHeight;
            setContentHeight(h);

            requestAnimationFrame(() => {
                setShouldAnimate(true);
            });
        } else {
            // Closing → lock current height first
            const h = contentRef.current.scrollHeight;
            setContentHeight(h);

            requestAnimationFrame(() => {
                setContentHeight(0);
                setShouldAnimate(true);
            });
        }
    }, [isOpen]);

    const repDate = items[0]?.quote_info?.expected_finish_date;
    const repQuoteId = items[0]?.quote_info?.quoteId;
    const repDelivery = items[0]?.job_deliveries?.find(d =>
        repQuoteId && d.child_unique_id ? d.child_unique_id === repQuoteId : d.quote_type === items[0]?.quote_info?.quote_type
    );
    const repDriverName = repDelivery?.assigned_driver_name;
    const hasDate = !!repDate;
    const hasDriver = !!repDelivery?.assigned_driver_id;

    const handleDateApply = () => {
        if (!groupDate) return;
        setShowDatePicker(false);
        onDateSet(groupId, items, new Date(groupDate));
    };

    // Compact mode summarises the members like a single card: one customer/address
    // when they all match, otherwise a "multiple" count
    const summarise = (values, pluralLabel) => {
        const unique = new Map();
        values.forEach(v => {
            const text = v == null ? '' : String(v).trim();
            if (text && !unique.has(text.toLowerCase())) unique.set(text.toLowerCase(), text);
        });
        if (unique.size <= 1) return { text: [...unique.values()][0] || '', multiple: false };
        return { text: `Multiple ${pluralLabel} (${unique.size})`, multiple: true };
    };
    const customerSummary = compact ? summarise(items.map(a => a.customer_name), 'customers') : null;
    const addressSummary = compact ? summarise(items.map(a => a.customer_address), 'addresses') : null;

    return (
        <div
            data-locate-group={idPrefix ? undefined : groupId}
            className={`border-2 rounded-xl overflow-hidden transition-all ${compact ? 'h-full flex flex-col' : 'mb-2'} ${isEditing ? 'border-indigo-500 shadow-lg shadow-indigo-100' : 'border-indigo-200'} bg-indigo-50/20`}>
            {/* Group header */}
            <div
                className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-indigo-100 border-b border-indigo-200 cursor-pointer select-none"
                onClick={onToggleOpen}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    <Layers className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                    <span className="text-xs font-bold text-indigo-800 truncate max-w-[110px]">{groupName}</span>
                    <span className="text-[10px] text-indigo-400 flex-shrink-0">({items.length})</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {isAdmin && !viewingMode && (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDisband(groupId, items); }}
                                        className="text-[10px] px-2 py-0.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 border border-red-300 font-medium"
                                    >
                                        Disband
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onCommit(); }}
                                        className="text-[10px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300 font-medium"
                                    >
                                        Done
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onEditGroup(); }}
                                    className="text-[10px] px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-300 font-medium"
                                >
                                    Edit Group
                                </button>
                            )}
                        </div>
                    )}
                    <ChevronDown className={`w-3.5 h-3.5 text-indigo-400 transition-transform duration-300 ml-0.5 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {compact && (
                <div className="flex-1 px-2 pt-1.5 pb-1 bg-white">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <FiUser className="w-2.5 h-2.5 text-blue-700 flex-shrink-0" />
                        <span className={`text-xs truncate font-medium ${customerSummary.multiple ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                            {customerSummary.text || 'No customer'}
                        </span>
                    </div>
                    {addressSummary.text && (
                        <p className="text-xs flex items-center gap-1 mt-1 min-w-0">
                            <MapIcon className="w-3 h-3 mr-1 text-blue-700 shrink-0" />
                            <span className={`truncate ${addressSummary.multiple ? 'text-gray-500 italic' : ''}`} title={addressSummary.text}>
                                {addressSummary.text}
                            </span>
                        </p>
                    )}
                </div>
            )}

            {/* Shared date + driver controls */}
            {((isAdmin && !viewingMode) || onLocate) && (
                <div className={`flex justify-end gap-1.5 px-3 py-1.5 border-b border-indigo-100 bg-indigo-50/30 ${compact ? 'mt-auto border-t' : ''}`}>
                    {isAdmin && !viewingMode && (
                        <>
                            <div className="relative flex-1" ref={datePickerRef}>
                                <div
                                    ref={groupDateDraggable.setNodeRef}
                                    {...groupDateDraggable.attributes}
                                    {...groupDateDraggable.listeners}
                                    onClick={(e) => { e.stopPropagation(); setShowDatePicker(v => !v); setShowDriverPicker(false); }}
                                    className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all cursor-grab active:cursor-grabbing ${hasDate ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    <FiCalendar className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">
                                        {hasDate
                                            ? new Date(repDate).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
                                            : 'Set Date'}
                                    </span>
                                </div>
                                {showDatePicker && (
                                    <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64">
                                        <p className="text-[11px] font-semibold text-gray-600 mb-2">Set date for all {items.length} deliveries</p>
                                        <input
                                            type="date"
                                            value={groupDate}
                                            onChange={(e) => setGroupDate(e.target.value)}
                                            className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
                                        />
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={handleDateApply}
                                                disabled={!groupDate}
                                                className="flex-1 text-[11px] px-2 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-40 font-medium"
                                            >
                                                Apply to all
                                            </button>
                                            <button
                                                onClick={() => setShowDatePicker(false)}
                                                className="text-[11px] px-2 py-1 text-gray-500 hover:text-gray-700"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
        
                            <div className="relative flex-1" ref={driverPickerRef}>
                                <div
                                    ref={groupDriverDraggable.setNodeRef}
                                    {...groupDriverDraggable.attributes}
                                    {...groupDriverDraggable.listeners}
                                    onClick={(e) => { e.stopPropagation(); setShowDriverPicker(v => !v); setShowDatePicker(false); }}
                                    className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all cursor-grab active:cursor-grabbing ${hasDriver ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    <FiUsers className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{repDriverName || 'Set Driver'}</span>
                                </div>
                                {showDriverPicker && (
                                    <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden w-48">
                                        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                                            <p className="text-[11px] font-semibold text-gray-600">Assign driver to all ({items.length})</p>
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {drivers.length === 0 ? (
                                                <p className="text-xs text-gray-400 px-3 py-2">No drivers available</p>
                                            ) : (
                                                drivers.map(d => (
                                                    <button
                                                        key={d.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowDriverPicker(false);
                                                            onDriverSet(groupId, items, d.id, d.full_name || d.username);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 transition-colors"
                                                    >
                                                        {d.full_name || d.username}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {onLocate && (
                        <TextTooltip text={locateLabel}>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onLocate(items[0], groupId); }}
                                aria-label={locateLabel}
                                className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                            >
                                <FiEye className="w-3.5 h-3.5" />
                            </button>
                        </TextTooltip>
                    )}
                </div>
            )}

            {/* Accordion body — inline styles so grid-template-rows transition is guaranteed */}
            <div
                className="transition-all duration-300 ease-in-out"
                style={{
                    display: 'grid',
                    gridTemplateRows: isOpen ? '1fr' : '0fr',
                }}
            >
                <div
                    style={{
                        maxHeight: `${contentHeight}px`,
                        overflow: 'hidden',
                        transition: shouldAnimate ? 'max-height 300ms ease, opacity 200ms ease' : 'none',
                        opacity: isOpen ? 1 : 0,
                    }}
                >
                    <div ref={contentRef} className="p-1.5 space-y-1.5">
                        {items.map(a => {
                            const cardKey = a.quote_info?.quoteId || `${a.id}-${a.quote_info?.quote_type}`;
                            return (
                                <div key={cardKey} className="relative">
                                    {isEditing && (
                                        <div
                                            className="absolute inset-0 z-20 rounded-lg cursor-pointer flex items-center justify-center bg-red-500/10 hover:bg-red-500/20 border-2 border-dashed border-red-400 transition-all"
                                            onClick={(e) => { e.stopPropagation(); onToggleCard(a); }}
                                        >
                                            <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium shadow">
                                                Remove
                                            </span>
                                        </div>
                                    )}
                                    {renderCard(a)}
                                </div>
                            );
                        })}
                        {isEditing && (
                            <div className="text-center py-2 text-[10px] text-indigo-400 border border-dashed border-indigo-200 rounded-lg">
                                Click cards outside to add them to this group
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DeliveryDraggableAssignmentCard = React.memo(function DeliveryDraggableAssignmentCard({
    assignment,
    isUnscheduled = false,
    playgroundMode = false,
    inGroup = false,
    idPrefix = '',
    openNotesBubble,
    userProfile,
    viewingMode,
    groupingMode,
    setOpenNotesBubble,
    setScheduleData,
    setUnscheduledAssignments,
    setPlaygroundAssignments,
    handleOpenNoteModal,
    handleOpenTimeSlotModal,
    handleOpenDeliveryTagModal,
    handleToggleCardInGroup,
    handleCreateGroup,
    getQuoteTypeStyle,
    getEffectiveProductionStatus,
    CardGeneralStatusStyle,
    getMainStatusColor,
    CardGeneralStatusText,
    InfoTooltip,
    handleMobileMove,
    navigate,
    companySlug,
    isMobile = false,
    noteDraftText = '',
    setNoteDraftText = () => { },
    onLocate = null,
    locateLabel = '',
    bypassAssignDriverRequirements = false,
}) {
    const location = useLocation();
    const quoteType = assignment.quote_info?.quote_type;
    const quoteDeliveryCount = assignment.quote_info?.delivery_count ?? 0;
    const quoteId = assignment.quote_info?.quoteId || null;

    const baseCardId = getCardBaseId(assignment);
    const uniqueCardId = `${idPrefix}${playgroundMode ? `playground-${baseCardId}` : baseCardId}`;

    const notesOpen = openNotesBubble?.quoteId === quoteId && (openNotesBubble?.idPrefix ?? '') === idPrefix;
    const notesBubblePos = notesOpen ? openNotesBubble : { x: 0, y: 0, showAbove: false, flipLeft: false };

    const dateHandle = useDraggable({
        id: `${uniqueCardId}-date`,
        data: { assignment, handleType: 'date', ...(playgroundMode && { fromPlayground: true }) },
        disabled: isMobile || (userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin'),
    });

    const driverHandle = useDraggable({
        id: `${uniqueCardId}-driver`,
        data: { assignment, handleType: 'driver', ...(playgroundMode && { fromPlayground: true }) },
        disabled: isMobile || (userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin'),
    });

    const isDragging = dateHandle.isDragging || driverHandle.isDragging;
    const hasDate = !!assignment.quote_info?.expected_finish_date;
    const delivery = assignment.job_deliveries?.find(d =>
        quoteId && d.child_unique_id
            ? d.child_unique_id === quoteId
            : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
    );
    const hasDriver = !!delivery?.assigned_driver_id;
    const driverName = delivery?.assigned_driver_name || 'Set Driver';
    const isDeliveryCompleted = delivery?.delivery_status === 'delivered';
    const childUniqueId = assignment.quote_info?.childUniqueId || null;
    const quoteStyle = getQuoteTypeStyle(quoteType);
    const QuoteIcon = quoteStyle.icon;
    // const isDesignCompleted =
    //     assignment?.quote_info?.design_status === 'completed' ||
    //     assignment?.quote_info?.design_status === 'not_required';
    const hasAddress =
        assignment?.customer_address &&
        Object.keys(assignment.customer_address).length > 0;

    const isFileOnlyQuote =
        quoteType === "beams" ||
        quoteType === "flooring_sheets";

    const effectiveProductionStatus = getEffectiveProductionStatus(assignment);

    const isProductionStarted =
        isFileOnlyQuote ||
        assignment?.job_type === "Timber" ||
        effectiveProductionStatus === "in_cutting" ||
        effectiveProductionStatus === "cutting_ended" ||
        effectiveProductionStatus === "in_production" ||
        effectiveProductionStatus === "completed" ||
        assignment?.production?.is_started_cutting === true ||
        assignment?.production?.is_started_production === true ||
        !!assignment?.production?.start_cutting_at ||
        !!assignment?.production?.start_production_at;

    const isProductionFinished =
        isFileOnlyQuote ||
        assignment?.job_type === "Timber" ||
        effectiveProductionStatus === "completed";

    const fileOnlyCompleted =
        assignment?.quote_info?.department_file_status === "completed" ||
        assignment?.quote_info?.departmentFileStatus === "completed" ||
        !!assignment?.quote_info?.department_files_completed_at ||
        !!assignment?.quote_info?.departmentFilesCompletedAt;

    const isDesignCompleted =
        isFileOnlyQuote
            ? fileOnlyCompleted
            : (
                assignment?.quote_info?.design_status === "completed" ||
                assignment?.quote_info?.design_status === "not_required"
            );

    // company.settings.bypassAssignDriverRequirements → only the delivery address is required
    const canAssignDriver = bypassAssignDriverRequirements
        ? !!hasAddress
        : isDesignCompleted && hasAddress && isProductionStarted;

    const isReadyToDeliver = isDesignCompleted && hasAddress && isProductionFinished;

    const notReadyReasons = [
        !isDesignCompleted && (
            isFileOnlyQuote
                ? `${getQuoteLabel?.(quoteType) || "Files"} - files waiting`
                : `Design not completed (${assignment?.quote_info?.design_status?.split("_").join(" ")})`
        ),
        !hasAddress && "Delivery address is not set",
        !isProductionStarted && !isFileOnlyQuote && "Production not started",
        !isProductionFinished && isProductionStarted && !isFileOnlyQuote && "Production started but not finished",
    ].filter(Boolean);

    const deliveryRecord = (assignment.job_deliveries || []).find(d =>
        quoteId
            ? d.child_unique_id === quoteId
            : (d.quote_type === assignment?.quote_info?.quote_type && !d.child_unique_id)
    );
    const deliveredStatus = deliveryRecord?.delivery_status === "delivered";

    return (
        <div
            // Lets the overdue section's eye button find this card in the week view
            data-locate-key={!idPrefix && !playgroundMode ? baseCardId : undefined}
            className={`relative group flex flex-col bg-white border rounded-lg p-2 shadow-sm hover:shadow-md transition-all h-full overflow-hidden ${deliveredStatus
                ? 'border-green-300 !bg-green-50'
                : 'border-gray-200'
                } ${isDragging ? 'opacity-50' : ''}`}
        >


            <div className="w-full">
                {(() => {
                    const isFileOnlyQuote =
                        quoteType === "beams" ||
                        quoteType === "flooring_sheets";

                    const effectiveProdStatus = isFileOnlyQuote
                        ? "not_required"
                        : getEffectiveProductionStatus(assignment);

                    const enrichedQuote = assignment.quote_info
                        ? { ...assignment.quote_info, production_status: effectiveProdStatus }
                        : {};
                    return (
                        <div className={`w-full h-[2px] self-center items-center rounded-full mb-2 relative ${CardGeneralStatusStyle(delivery || {}, enrichedQuote, "bar")} ${getMainStatusColor(assignment.main_status)}`}>
                            <div className="absolute -top-[0.5rem] -left-[0.5rem] text-[11px] font-medium items-center self-center content-center flex-shrink-0 bg-white pr-1 flex flex-row">
                                <span className={`flex flex-row gap-1 px-2 pr-3 py-[1px] rounded-xl text-[11px] font-medium items-center self-center content-center ${quoteStyle.bgColor} ${quoteStyle.textColor} flex-shrink-0 rounded-tl-none rounded-bl-none rounded-tr-none z-20`}>
                                    <QuoteIcon className="w-[11px] h-[11px] self-center" />
                                    {quoteStyle.label}
                                </span>
                                {assignment.quote_info?.delivery_count > 0 && (
                                    <span className="inline-flex px-2 py-[1px] self-center content-end justify-end items-center rounded-xl text-[11px] font-medium bg-orange-100 text-orange-800 rounded-bl-none rounded-tr-none rounded-tl-none w-12 -ml-2 z-10 ">
                                        Extra
                                    </span>
                                )}
                            </div>
                            <span className={`absolute -top-[6.75px] right-0 text-[11px] pl-2 font-semibold ${CardGeneralStatusStyle(delivery || {}, enrichedQuote, "text")} ${deliveredStatus ? "bg-green-50" : "bg-white"}`}>
                                {CardGeneralStatusText(assignment)}
                            </span>
                        </div>
                    );
                })()}

                <div className="flex items-start justify-between mb-1 mt-3 bg-zinc-200 rounded">
                    <div className="flex items-center content-center self-center gap-1.5 min-w-0 flex-1 ">
                        <FiHash className="w-3 h-3 text-gray-600 flex-shrink-0 ml-1" />
                        <TextTooltip text={childUniqueId && `Parent: ${assignment.unique_id}`}>
                            <span
                                className="font-bold text-gray-900 text-sm truncate cursor-pointer hover:text-teal-600 hover:underline transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (childUniqueId && assignment.quote_info?.quoteId) {
                                        navigate(`/${companySlug}/assignment/${assignment.id}/${assignment.quote_info.quoteId}`, {
                                            state: { from: location.pathname }
                                        });
                                    } else {
                                        navigate(`/${companySlug}/assignment/${assignment.id}`, {
                                            state: { from: location.pathname }
                                        });
                                    }
                                }}
                            >
                                {childUniqueId ? childUniqueId : assignment.unique_id}
                            </span>
                        </TextTooltip>
                    </div>
                    <div className="flex items-center self-center gap-1">
                        {!isReadyToDeliver && (
                            <>
                                {(() => {
                                    let notReadyText = ``;
                                    notReadyReasons.forEach((reason) => {
                                        notReadyText += `${reason}\n`;
                                    });
                                    return (
                                        <TextTooltip text={notReadyText}>
                                            <IoWarning className="w-2.5 h-2.5 text-red-400 flex-shrink-0" />
                                        </TextTooltip>
                                    );
                                })()}
                            </>
                        )}

                        {assignment.quote_info?.delivery_time_slot && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200 flex-shrink-0 self-center">
                                <FiClock className="w-2.5 h-2.5" />
                                {assignment.quote_info.delivery_time_slot}
                            </span>
                        )}


                    </div>
                </div>

                <hr className="mb-1" />

                <div className="delivery-mobile-card-details">
                    {!viewingMode && <MobileJobActions job={assignment} triggerClassName="delivery-mobile-actions-trigger" className="absolute top-1 left-[50%] -translate-x-[50%] flex flex-row gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 bg-white py-0.5 px-0.5 border rounded-lg">
                    <TextTooltip text={assignment.quote_info?.delivery_note ? 'Edit note' : 'Add note'}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenNoteModal(assignment, quoteType, assignment.quote_info?.delivery_note || '', quoteDeliveryCount);
                            }}
                            className={`p-1.5 rounded-lg transition-all shadow-md ${assignment.quote_info?.delivery_note
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                }`}
                        >
                            <FiEdit3 className="w-3.5 h-3.5" />
                        </button>
                    </TextTooltip>

                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                        <TextTooltip text={assignment.quote_info?.delivery_time_slot ? `Time slot: ${assignment.quote_info.delivery_time_slot}` : 'Set time slot'}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenTimeSlotModal(assignment, quoteType, assignment.quote_info?.delivery_time_slot, quoteDeliveryCount);
                                }}
                                className={`p-1.5 rounded-lg transition-all shadow-md ${assignment.quote_info?.delivery_time_slot
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                    }`}
                            >
                                <FiClock className="w-3.5 h-3.5" />
                            </button>
                        </TextTooltip>
                    )}

                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                        <TextTooltip text={assignment.quote_info?.delivery_tag ? `Delivery Tag: ${assignment.quote_info.delivery_tag}` : 'Set delivery tag'}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDeliveryTagModal(assignment, quoteType, assignment.quote_info?.delivery_tag, quoteDeliveryCount);
                                }}
                                className={`p-1.5 rounded-lg transition-all shadow-md ${assignment.quote_info?.delivery_tag
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                    }`}
                            >
                                <ClipboardCheck className="w-3.5 h-3.5" />
                            </button>
                        </TextTooltip>
                    )}

                    {hasPermission(userProfile?.role, ["super_admin", "admin"]) && !inGroup && !assignment.quote_info?.group_id && (
                        <TextTooltip text={groupingMode ? `Add to "${groupingMode.groupName}"` : 'Create a group'}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (groupingMode) {
                                        handleToggleCardInGroup(assignment);
                                    } else {
                                        handleCreateGroup(assignment);
                                    }
                                }}
                                className="p-1.5 rounded-lg bg-white text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-300 hover:border-indigo-300 transition-all shadow-md"
                            >
                                <Layers className="w-3.5 h-3.5" />
                            </button>
                        </TextTooltip>
                    )}

                    <InfoTooltip aria-label="Job information"
                        assignment={assignment}
                        quoteType={quoteType}
                        hasDate={hasDate}
                        hasDriver={hasDriver}
                        driverName={driverName}
                        isDeliveryCompleted={isDeliveryCompleted}
                        delivery={delivery}
                    />

                    {isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                        <button
                            onClick={(e) => handleMobileMove({ ...assignment, mobileCanAssignDriver: canAssignDriver }, e)}
                            type="button" aria-label="Move job" title="Move job" className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-all shadow-md"
                        >
                            <FiMove className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <QuickNotesButton aria-label="Quick notes"
                        assignment={assignment}
                        userProfile={userProfile}
                        isOpen={notesOpen}
                        onToggle={(val, pos) => setOpenNotesBubble(val ? { quoteId, idPrefix, ...pos } : null)}
                        bubblePos={notesBubblePos}
                        setScheduleData={setScheduleData}
                        setUnscheduledAssignments={setUnscheduledAssignments}
                        setPlaygroundAssignments={setPlaygroundAssignments}
                        noteDraftText={noteDraftText}
                        setNoteDraftText={setNoteDraftText}
                    />
                </MobileJobActions>}
                    <div className="flex items-center justify-between flex-row w-full">
                    <div className="flex items-center flex-row gap-1.5">
                        <FiUser className="w-2.5 h-2.5 text-blue-700 flex-shrink-0" />
                        <span className="text-xs text-gray-700 truncate font-medium cursor-default">
                            {assignment.customer_name}
                        </span>
                    </div>
                    {assignment.quote_info?.delivery_tag && (
                        <span className={`inline-flex capitalize items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border  flex-shrink-0 ${assignment?.quote_info?.delivery_tag === "confirmed" ? "bg-green-50 text-green-700 border-green-200" : "bg-orange-50 text-orange-600 border-orange-200"}`}>
                            {assignment?.quote_info?.delivery_tag === "confirmed" ?
                                <CircleCheck className="w-2.5 h-2.5" />
                                :
                                <Flag className="w-2.5 h-2.5" />
                            }
                            {assignment.quote_info.delivery_tag}
                        </span>
                    )}
                </div>

                {assignment?.customer_address && (
                    <div className="flex flex-row items-center min-w-0">
                        <p className="text-xs flex items-center gap-1 mt-1 truncate w-full min-w-0">
                            <MapIcon className="w-3 h-3 mr-1 text-blue-700 shrink-0" />
                            <span className="truncate" title={assignment?.customer_address}>
                                {assignment?.customer_address}
                            </span>
                        </p>
                    </div>
                )}
                </div>

                <>
                    {!inGroup && !viewingMode &&
                        <div className="flex items-center gap-1.5 pt-1.5 border-t border-gray-100 mt-1.5">
                            <div
                                ref={dateHandle.setNodeRef}
                                {...(isMobile ? {} : dateHandle.attributes)}
                                {...(notesOpen ? {} : dateHandle.listeners)}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs ${hasDate ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                    } cursor-grab active:cursor-grabbing hover:shadow-sm transition-all ${(deliveredStatus && userProfile?.role !== "super_admin") && "!pointer-events-none !select-none"}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <FiCalendar className="w-3 h-3 flex-shrink-0" />
                                <span className="font-medium truncate">
                                    {hasDate
                                        ? new Date(assignment.quote_info.expected_finish_date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
                                        : 'Set Date'
                                    }
                                </span>
                            </div>

                            <div
                                ref={canAssignDriver ? driverHandle.setNodeRef : null}
                                {...(!isMobile && canAssignDriver ? driverHandle.attributes : {})}
                                {...(canAssignDriver && !notesOpen ? driverHandle.listeners : {})}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs ${!canAssignDriver
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                                    : hasDriver
                                        ? 'bg-blue-100 text-blue-700 cursor-grab active:cursor-grabbing hover:shadow-sm'
                                        : 'bg-gray-100 text-gray-600 cursor-grab active:cursor-grabbing hover:shadow-sm'
                                    } transition-all ${(deliveredStatus && userProfile?.role !== "super_admin") && "!pointer-events-none !select-none"}`}
                                onClick={(e) => e.stopPropagation()}
                                title={
                                    !hasAddress
                                        ? 'Address must be set before assigning driver'
                                        : bypassAssignDriverRequirements
                                            ? ''
                                            : !isDesignCompleted
                                                ? 'Design must be completed before assigning driver'
                                                : !isProductionStarted && !isFileOnlyQuote
                                                    ? 'Production must be started before assigning driver'
                                                    : ''
                                }
                            >
                                <FiUsers className="w-3 h-3 flex-shrink-0" />
                                <span className="font-medium truncate">
                                    {driverName}
                                </span>
                            </div>

                            {onLocate && (
                                <TextTooltip text={locateLabel}>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onLocate(assignment); }}
                                        aria-label={locateLabel}
                                        className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                                    >
                                        <FiEye className="w-3.5 h-3.5" />
                                    </button>
                                </TextTooltip>
                            )}
                        </div>
                    }

                    {!inGroup && !viewingMode &&
                        <div className="mt-1 space-y-1">
                            {assignment.quote_info?.selected_units?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                    {assignment.quote_info.selected_units.map(unitNum => (
                                        <span
                                            key={unitNum}
                                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800"
                                        >
                                            Unit {unitNum}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {assignment.quote_info?.delivery_note && (
                                <div className="flex items-start gap-1.5 px-1.5 py-1 rounded bg-blue-50 border border-blue-200">
                                    <FiEdit3 className="w-2.5 h-2.5 flex-shrink-0 mt-0.5 text-blue-600" />
                                    <span className="text-[12px] line-clamp-2 text-blue-900">
                                        {assignment.quote_info.delivery_note}
                                    </span>
                                </div>
                            )}
                        </div>
                    }
                </>
            </div>
        </div>
    );
});

const DeliveryDroppableDay = React.memo(function DeliveryDroppableDay({
    dayKey,
    dayLabel,
    dayDate,
    dayIsToday,
    dayIsPast,
    dayHolidays,
    isExpanded,
    isCollapsed,
    onToggleExpand,
    assignmentCount,
    completedCount,
    disabled,
    children,
}) {
    const { isOver, setNodeRef } = useDroppable({
        id: `day-${dayKey}`,
        disabled,
    });

    const isHoliday = dayHolidays.length > 0;

    if (isCollapsed) {
        return (
            <div
                ref={setNodeRef}
                data-day-key={dayKey}
                className={`group relative bg-white border-2 ${isOver ? 'border-teal-400 shadow-lg shadow-teal-100' : isHoliday ? 'border-amber-400' : dayIsPast ? 'border-gray-300' : 'border-gray-200'
                    } rounded-2xl overflow-hidden hover:border-teal-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 ${dayIsPast ? 'opacity-75' : ''}`}
                onClick={() => onToggleExpand(dayKey)}
            >
                <div className={`absolute inset-0 bg-gradient-to-br ${dayIsPast ? 'from-gray-50 to-gray-100' : dayIsToday ? 'from-green-50 via-teal-50 to-blue-50' : 'from-blue-50 via-indigo-50 to-purple-50'} opacity-40`}></div>
                <div className="relative p-4">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                            <h3 className={`font-bold text-lg mb-1 ${dayIsToday ? 'text-teal-700' : 'text-gray-800'}`}>{dayLabel}</h3>
                            <p className="text-sm text-gray-500 font-medium">{dayDate}</p>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleExpand(dayKey);
                            }}
                            className="p-2 hover:bg-white/80 rounded-xl transition-all duration-200 group-hover:scale-110 shadow-sm"
                            title="Expand day"
                        >
                            <FiMaximize className="w-4 h-4 text-teal-600" />
                        </button>
                    </div>
                    <div className="flex items-center justify-center">
                        <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm shadow-md ${assignmentCount > 0
                            ? dayIsToday
                                ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                                : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                            : 'bg-gray-100 text-gray-500'
                            }`}>
                            <FiTruck className="w-4 h-4" />
                            <span>{assignmentCount}</span>
                        </div>
                    </div>
                    {dayIsToday && (
                        <div className="absolute bottom-2 left-2">
                            <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md animate-pulse">TODAY</span>
                        </div>
                    )}
                    {dayIsPast && !dayIsToday && (
                        <div className="absolute bottom-2 left-2">
                            <span className="bg-gray-400 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md">PAST</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={setNodeRef}
            data-day-key={dayKey}
            className={`relative ${isExpanded ? 'bg-white' : 'bg-gray-50'} border-2 ${isOver ? 'border-teal-400 shadow-lg shadow-teal-100/50' : isHoliday ? 'border-amber-400' : dayIsPast ? 'border-gray-300' : 'border-gray-200'
                } rounded-xl overflow-hidden ${isExpanded ? 'overflow-x-auto shadow-xl' : 'overflow-y-auto'} hover:border-teal-300 transition-all duration-200 ${isExpanded ? '' : 'min-h-[180px] max-h-[80vh]'} ${dayIsPast ? 'opacity-80' : ''}`}
        >
            <div className={`flex items-center justify-between px-2 py-1.5 border-b ${dayIsToday ? 'bg-teal-50 border-teal-200' : isHoliday ? 'bg-amber-50 border-amber-200' : dayIsPast ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-100'} sticky top-0 z-50`}>
                <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                    <h3 className={`font-semibold text-sm ${dayIsToday ? 'text-teal-700' : 'text-gray-800'}`}>{dayLabel}</h3>
                    <span className={`text-[12px] self-end font-medium ${dayIsToday ? 'text-teal-500' : 'text-gray-400'}`}>{dayDate}</span>
                    {dayIsToday && <span className="bg-teal-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">TODAY</span>}
                    {dayIsPast && !dayIsToday && <span className="bg-gray-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full self-end">PAST</span>}
                    <div className="h-[17px] px-2 min-w-[20px] self-end flex items-center justify-center rounded-full text-[10px] font-bold shadow-md bg-blue-600 text-white">
                        {assignmentCount > 0 ? `${completedCount} / ${assignmentCount}` : '0'}
                    </div>
                    {isHoliday && (
                        <span className="flex items-center gap-0.5 text-[9px] font-bold text-amber-700 truncate">
                            <PartyPopper className="w-2.5 h-2.5 flex-shrink-0" />
                            {dayHolidays[0]}{dayHolidays.length > 1 ? ` +${dayHolidays.length - 1}` : ''}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => onToggleExpand(isExpanded ? null : dayKey)}
                    className="p-1 hover:bg-gray-100 rounded transition-all"
                    title={isExpanded ? "Collapse day" : "Expand day"}
                >
                    {isExpanded ? (
                        <FiMinimize className="w-3.5 h-3.5 text-gray-500" />
                    ) : (
                        <FiMaximize className="w-3.5 h-3.5 text-gray-400" />
                    )}
                </button>
            </div>
            <div className={`p-1.5 ${isExpanded ? 'flex flex-wrap gap-2 pb-3' : 'space-y-1.5'}`}>
                {children}
            </div>
        </div>
    );
});

const DeliverySchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();
    const isMobile = useMobileSchedule();

    const [weekOffset, setWeekOffset] = useState(() => {
        try { const v = localStorage.getItem('delivery_weekOffset'); return v !== null ? JSON.parse(v) : 0; } catch { return 0; }
    }); // 0 = current week, -1 = last week, 1 = next week
    const [scheduleData, setScheduleData] = useState({}); // Structure: { Monday: [...], Tuesday: [...], etc. }
    const [unscheduledAssignments, setUnscheduledAssignments] = useState([]);

    const [activeId, setActiveId] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingUndoCompletion, setPendingUndoCompletion] = useState(null); // { assignment, deliveryRecord }
    const [isUndoingCompletion, setIsUndoingCompletion] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);

    // Edit modal state
    const [editOpen, setEditOpen] = useState(false);
    const [editJobId, setEditJobId] = useState(null);

    // Mobile-specific states
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [mobileViewMode, setMobileViewMode] = useState('grid');
    const [selectedDay, setSelectedDay] = useState(null);
    const [expandedDay, setExpandedDay] = useState(null); // Track which day is expanded (null = none expanded)

    // Week switching states for drag and drop
    const [dragOverWeek, setDragOverWeek] = useState(null);
    const [switchProgress, setSwitchProgress] = useState(0);
    const [draggedItemCache, setDraggedItemCache] = useState(null);

    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [availableDrivers, setAvailableDrivers] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    // Driver panel states
    const [drivers, setDrivers] = useState([]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [currentDropTarget, setCurrentDropTarget] = useState(null);
    const [dragHandleType, setDragHandleType] = useState(null); // 'date' or 'driver'
    const [dropTargetRect, setDropTargetRect] = useState(null);
    const [driverWorkloads, setDriverWorkloads] = useState({});
    const panelRef = useRef(null);

    // Date picker states
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datePickerValue, setDatePickerValue] = useState(null);
    const [showDragDatePicker, setShowDragDatePicker] = useState(false);
    const [draggedForDatePicker, setDraggedForDatePicker] = useState(null);
    const [selectedDateFromPicker, setSelectedDateFromPicker] = useState(null);
    // Group drag-to-date-picker state
    const [draggedGroupForDatePicker, setDraggedGroupForDatePicker] = useState(null); // { groupId, groupName, groupMembers }
    const [selectedGroupDateFromPicker, setSelectedGroupDateFromPicker] = useState(null);

    // Note and time slot states
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
    const [currentEditingQuote, setCurrentEditingQuote] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

    // Delivery Tag states
    const [showDeliveryTagModal, setShowDeliveryTagModal] = useState(false);
    const [selectedDeliveryTag, setSelectedDeliveryTag] = useState(null);

    // Grouping feature
    const [groupingMode, setGroupingMode] = useState(null); // null | { groupId: string, groupName: string }
    // Pending grouping changes: Map<quoteId, { originalCard, originalLocation, originalDayKey, targetGroupId, targetGroupName, targetDate, targetDriverId, targetDriverName, targetDeliveryId, isFirstCard }>
    // Changes are only committed to DB when the user clicks "Done"
    const groupingPendingRef = useRef(new Map());
    const groupingRemovedRef = useRef(new Map());
    // Which group containers have their accordion open (keyed by groupId)
    // Lifted to parent so it survives GroupContainer re-mounts caused by parent re-renders
    const [openGroups, setOpenGroups] = useState(new Set());

    const [viewingMode, setViewingMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState(() => {
        try { return localStorage.getItem('delivery_searchQuery') || ''; } catch { return ''; }
    });
    const [showSaturday, setShowSaturday] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_showSaturday');
            return stored !== null ? JSON.parse(stored) : false;
        } catch { return false; }
    });
    const [unscheduledPage, setUnscheduledPage] = useState(1);
    const unscheduledContainerRef = useRef(null);
    const isLoadingMoreUnscheduled = useRef(false);

    // Always-current snapshot of the state values that realtime handlers need.
    // Set synchronously during render so handlers never read stale closures.
    const latestRef = useRef({});
    const fetchPlaygroundRef = useRef(null);
    const fetchOverdueRef = useRef(null);
    const overdueRefetchTimerRef = useRef(null);
    const overdueFetchSeqRef = useRef(0);
    const locateTimerRef = useRef(null);

    const [isOverdueOpen, setIsOverdueOpen] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_overdueOpen');
            return stored !== null ? JSON.parse(stored) : true;
        } catch { return true; }
    });
    const [overdueSort, setOverdueSort] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_overdueSort');
            return OVERDUE_SORT_OPTIONS.some(o => o.key === stored) ? stored : 'oldest';
        } catch { return 'oldest'; }
    });
    const [overdueRange, setOverdueRange] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_overdueRange');
            return OVERDUE_RANGE_OPTIONS.some(o => o.key === stored) ? stored : '3w';
        } catch { return '3w'; }
    });
    // 'all' | 'none' (no driver assigned) | a driver's user id
    const [overdueDriver, setOverdueDriver] = useState(() => {
        try { return localStorage.getItem('delivery_overdueDriver') || 'all'; } catch { return 'all'; }
    });
    const [overdueGroupsFirst, setOverdueGroupsFirst] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_overdueGroupsFirst');
            return stored !== null ? JSON.parse(stored) : false;
        } catch { return false; }
    });
    const [showOverdueGroups, setShowOverdueGroups] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_overdueShowGroups');
            return stored !== null ? JSON.parse(stored) : true;
        } catch { return true; }
    });


    const enabledExtraQuoteTypes = React.useMemo(() => {
        return getEnabledExtraQuoteTypes(company);
    }, [company?.settings]);

    const quoteTypeOptions = React.useMemo(() => {
        const base = [
            { key: "roof", icon: FiHome, label: "Roof" },
            { key: "wall", icon: FiLayers, label: "Wall" },
            { key: "floor", icon: FiLayers, label: "Floor" },
            { key: "timber", icon: FiPackage, label: "Timber" },
        ];

        const extras = enabledExtraQuoteTypes.map(type => ({
            key: type,
            icon: type === "beams" ? GiWoodPile : SquareStack,
            label: getQuoteLabel(type),
        }));

        return [...extras, ...base];
    }, [enabledExtraQuoteTypes]);

    // Add these new state declarations
    const [productionFilter, setProductionFilter] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_productionFilter');
            return stored ? JSON.parse(stored) : 'all'; // 'all', 'finished', 'not_finished'
        } catch {
            return 'all';
        }
    });

    const [quoteTypeFilters, setQuoteTypeFilters] = useState(() => {
        try {
            const stored = localStorage.getItem('delivery_quoteTypeFilters');
            return stored ? JSON.parse(stored) : {
                roof: true,
                wall: true,
                floor: true,
                timber: true,
                beams: true,
                flooring_sheets: true,
            };
        } catch {
            return {
                roof: true,
                wall: true,
                floor: true,
                timber: true,
                beams: true,
                flooring_sheets: true,
            };
        }
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    // Unscheduled droppable zone — called here (not inside a sub-component) so the
    // div is never remounted on parent re-renders, preserving scroll position.
    const { isOver: isUnscheduledOver, setNodeRef: setUnscheduledNodeRef } = useDroppable({
        id: 'unscheduled-zone',
        disabled: isPanelOpen,
    });


    const dropAnimationConfig = {
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };



    // Component to remeasure droppable containers after panel animation
    const PanelRemeasurer = () => {
        const { measureDroppableContainers } = useDndContext();

        useEffect(() => {
            if (isPanelOpen && measureDroppableContainers) {
                // Remeasure after panel animation completes (500ms transition)
                const timer = setTimeout(() => {
                    measureDroppableContainers();
                }, 600); // 500ms animation + 100ms buffer

                return () => clearTimeout(timer);
            }
        }, [isPanelOpen, measureDroppableContainers]);

        return null;
    };

    const UNSCHEDULED_PAGE_SIZE = 50;

    const handleUnscheduledScroll = (e) => {
        if (isLoadingMoreUnscheduled.current) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + clientHeight) < 400) {
            isLoadingMoreUnscheduled.current = true;
            setUnscheduledPage(prev => prev + 1);
        }
    };

    const useDebounce = (value, delay) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            return () => clearTimeout(handler);
        }, [value, delay]);

        return debouncedValue;
    };

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const matchesSearch = (assignment) => {
        if (!debouncedSearchQuery.trim()) return true;

        const query = debouncedSearchQuery.toLowerCase();

        return (
            assignment.unique_id?.toLowerCase().includes(query) ||
            assignment.quote_info?.childUniqueId?.toLowerCase().includes(query) ||
            assignment.customer_name?.toLowerCase().includes(query) ||
            assignment.customer_address?.toLowerCase().includes(query) ||
            assignment.quote_info?.quote_type?.toLowerCase().includes(query) ||
            assignment.job_deliveries?.some(d =>
                d.assigned_driver_name?.toLowerCase().includes(query)
            ) ||
            assignment.id?.toLowerCase().includes(query)
        );
    };

    // Filter assignments based on search
    const filterAssignments = (assignments) => {
        if (!debouncedSearchQuery.trim()) return assignments;
        return assignments.filter(matchesSearch);
    };

    // Add these useEffect hooks
    useEffect(() => {
        localStorage.setItem('delivery_productionFilter', JSON.stringify(productionFilter));
        setUnscheduledPage(1);
    }, [productionFilter]);

    useEffect(() => {
        localStorage.setItem('delivery_quoteTypeFilters', JSON.stringify(quoteTypeFilters));
        setUnscheduledPage(1);
    }, [quoteTypeFilters]);

    useEffect(() => {
        // Reset the gate after each page increment so the next scroll can trigger
        isLoadingMoreUnscheduled.current = false;
    }, [unscheduledPage]);

    useEffect(() => {
        localStorage.setItem('delivery_searchQuery', searchQuery);
        setUnscheduledPage(1);
    }, [searchQuery]);

    useEffect(() => {
        localStorage.setItem('delivery_showSaturday', JSON.stringify(showSaturday));
    }, [showSaturday]);

    useEffect(() => {
        try { localStorage.setItem('delivery_weekOffset', JSON.stringify(weekOffset)); } catch { }
    }, [weekOffset]);

    useEffect(() => {
        try { localStorage.setItem('delivery_overdueOpen', JSON.stringify(isOverdueOpen)); } catch { }
    }, [isOverdueOpen]);

    useEffect(() => {
        try { localStorage.setItem('delivery_overdueSort', overdueSort); } catch { }
    }, [overdueSort]);

    useEffect(() => {
        try { localStorage.setItem('delivery_overdueRange', overdueRange); } catch { }
    }, [overdueRange]);

    useEffect(() => {
        try { localStorage.setItem('delivery_overdueShowGroups', JSON.stringify(showOverdueGroups)); } catch { }
    }, [showOverdueGroups]);

    useEffect(() => {
        try { localStorage.setItem('delivery_overdueGroupsFirst', JSON.stringify(overdueGroupsFirst)); } catch { }
    }, [overdueGroupsFirst]);

    useEffect(() => {
        try { localStorage.setItem('delivery_overdueDriver', overdueDriver); } catch { }
    }, [overdueDriver]);

    useEffect(() => {
        return () => {
            setActiveId(null);
            setDragOverWeek(null);
            setSwitchProgress(0);
        };
    }, []);

    useEffect(() => {
        if (company?.id) {
            fetchAssignments();
        }
    }, [company, showOnlyMine, selectedUser, weekOffset, productionFilter, quoteTypeFilters]);

    // Playground is independent of week/filters — fetch once on mount
    useEffect(() => {
        if (company?.id) {
            fetchPlaygroundAssignments();
        }
    }, [company?.id]);

    // Overdue deliveries follow the filters but not the visible week.
    // Loaded up front so opening the section never triggers a fetch.
    useEffect(() => {
        if (company?.id) {
            fetchOverdueAssignments();
        }
    }, [company, showOnlyMine, selectedUser, productionFilter, quoteTypeFilters]);

    useEffect(() => () => {
        clearTimeout(overdueRefetchTimerRef.current);
        clearTimeout(locateTimerRef.current);
    }, []);

    // Fetch available drivers for filtering
    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            fetchAvailableDrivers();
        }
    }, [userProfile]);

    const fetchAvailableDrivers = async () => {
        try {
            // Fetch drivers from your users table
            // This is a placeholder - implement based on your actual query
            setAvailableDrivers([]);
        } catch (error) {
            console.error('Error fetching drivers:', error);
            Toast.error('Error fetching drivers');
        }
    };

    // Fetch drivers for driver assignment panel
    useEffect(() => {
        if (company?.id) {
            fetchDrivers();
        }
    }, [company]);

    // Realtime subscription for job_quotes changes
    useEffect(() => {
        if (!company?.id) return;

        const handleJobQuotesUpdate = (payload) => {
            if (payload.eventType !== 'UPDATE') return;

            const updated = payload.new;

            // Match a card in any list to this update row
            const matchesItem = (item) => {
                const itemQuoteId = item.quote_info?.quoteId;
                const updatedDeliveryCount = updated.delivery_count ?? 0;
                return itemQuoteId
                    ? itemQuoteId === updated.id
                    : (item.id === updated.job_id &&
                        item.quote_info?.quote_type === updated.quote_type &&
                        (item.quote_info?.delivery_count ?? 0) === updatedDeliveryCount);
            };

            // Merge latest DB values into an existing quote_info object
            const patchedQuoteInfo = (existing) => ({
                ...existing,
                delivery_note: updated.delivery_note,
                delivery_time_slot: updated.delivery_time_slot,
                expected_finish_date: updated.expected_delivery_date,
                expected_delivery_date: updated.expected_delivery_date,
                selected_units: updated.selected_units,
                delivery_count: updated.delivery_count,
                production_status: updated.production_status,
                delivery_status: updated.delivery_status,
                design_status: updated.design_status,
                estimation_status: updated.estimation_status,
                delivery_tag: updated.delivery_tag,
                layout_only: updated.layout_only,
                waiting_job: updated.waiting_job,
                sent_production: updated.sent_production,
                quote_playground: updated.quote_playground || false,
                delivery_playground: updated.delivery_playground || false,
                quick_notes: updated.quick_notes || [],
                // Group membership — must be included so other clients see grouping changes
                group_id: updated.group_id || null,
                group_name: updated.group_name || null,
            });

            const {
                weekOffset: wo,
                days: currentDays,
                scheduleData: currentSchedule,
                unscheduledAssignments: currentUnscheduled,
                playgroundAssignments: currentPlayground,
                overdueAssignments: currentOverdue,
                company: co,
                showOnlyMine: som,
                selectedUser: su,
                productionFilter: pf,
                quoteTypeFilters: qtf,
                userProfile: up,
            } = latestRef.current;

            // Work out which day column the card belongs in after this update
            const newDateStr = updated.expected_delivery_date?.slice(0, 10) || null;
            const _today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
            const _weekStart = addWeeks(startOfWeek(_today, { weekStartsOn: 1 }), wo);
            let targetDayKey = null;
            if (newDateStr) {
                const [dy, dm, dd] = newDateStr.split('-').map(Number);
                currentDays.forEach((day, idx) => {
                    const d = addDays(_weekStart, idx);
                    if (d.getFullYear() === dy && d.getMonth() + 1 === dm && d.getDate() === dd) {
                        targetDayKey = day.key;
                    }
                });
            }

            // Find the card in its current location
            let existingCard = null;
            for (const dayKey in currentSchedule) {
                const card = currentSchedule[dayKey].find(matchesItem);
                if (card) { existingCard = card; break; }
            }
            if (!existingCard) existingCard = currentUnscheduled?.find(matchesItem) || null;
            if (!existingCard) existingCard = currentPlayground?.find(matchesItem) || null;
            if (!existingCard) existingCard = currentOverdue?.find(matchesItem) || null;

            const belongsInOverdue =
                updated.layout_only !== true &&
                updated.delivery_playground !== true &&
                updated.quote_playground !== true &&
                isInOverdueRange(newDateStr);

            // Card not found in any list — could be layout_only just turned off (card was hidden)
            if (!existingCard) {
                // e.g. rescheduled into the past from a week that isn't on screen
                if (belongsInOverdue) scheduleOverdueRefetch();
                if (updated.layout_only === false && targetDayKey && co?.id) {
                    const [dy, dm, dd] = newDateStr.split('-').map(Number);
                    const utcDate = new Date(Date.UTC(dy, dm - 1, dd, 12, 0, 0));
                    getDeliveriesForDate(co.id, utcDate, {
                        showOnlyMine: som ? up?.id : null,
                        userId: su || null,
                        productionFilter: pf,
                        quoteTypeFilters: qtf,
                    }).then(result => {
                        if (!result.error) {
                            setScheduleData(prev => ({ ...prev, [targetDayKey]: result.data || [] }));
                        }
                    });
                }
                return;
            }

            const updatedCard = { ...existingCard, quote_info: patchedQuoteInfo(existingCard.quote_info) };

            // Overdue is independent of the lists below — re-evaluate membership on its own
            setOverdueAssignments(prev => {
                const rest = prev.filter(item => !matchesItem(item));
                return belongsInOverdue && !isCardDelivered(updatedCard) ? [...rest, updatedCard] : rest;
            });

            // Remove from all lists unconditionally
            setScheduleData(prev => {
                const next = {};
                for (const dayKey in prev) {
                    next[dayKey] = prev[dayKey].filter(item => !matchesItem(item));
                }
                return next;
            });
            setUnscheduledAssignments(prev => prev.filter(item => !matchesItem(item)));
            setPlaygroundAssignments(prev => prev.filter(item => !matchesItem(item)));

            // layout_only on → card should not appear anywhere
            if (updated.layout_only === true) return;

            // Place in the correct destination
            if (
                updated.delivery_playground === true ||
                updated.quote_playground === true
            ) {
                setPlaygroundAssignments(prev => [...prev, updatedCard]);
            } else if (targetDayKey) {
                setScheduleData(prev => ({
                    ...prev,
                    [targetDayKey]: [...(prev[targetDayKey] || []), updatedCard],
                }));
            } else if (newDateStr) {
                // Date is set but outside the current week view — card is off-screen, don't show it
            } else {
                // No date → unscheduled
                setUnscheduledAssignments(prev => [...prev, updatedCard]);
            }
        };

        const handleJobsUpdate = (payload) => {
            if (payload.eventType !== 'UPDATE') return;

            const updatedJob = payload.new;

            if (updatedJob?.is_cancelled === true) {
                setScheduleData(prev => {
                    const next = {};
                    for (const dayKey in prev) {
                        next[dayKey] = prev[dayKey].filter(item => item.id !== updatedJob.id);
                    }
                    return next;
                });

                setUnscheduledAssignments(prev =>
                    prev.filter(item => item.id !== updatedJob.id)
                );

                setPlaygroundAssignments(prev =>
                    prev.filter(item => item.id !== updatedJob.id)
                );

                setOverdueAssignments(prev =>
                    prev.filter(item => item.id !== updatedJob.id)
                );

                return;
            }

            if (payload.old?.is_cancelled === true && updatedJob?.is_cancelled !== true) {
                fetchAssignments();
                fetchPlaygroundAssignments();
                scheduleOverdueRefetch();
                return;
            }

            // Update scheduleData
            setScheduleData(prev => {
                const newData = {};
                let found = false;

                for (const dayKey in prev) {
                    newData[dayKey] = prev[dayKey].map(item => {
                        if (item.id === updatedJob.id) {
                            found = true;

                            return {
                                ...item,
                                customer_address: updatedJob.customer_address,
                                customer_name: updatedJob.customer_name,
                                job_status: updatedJob.status,
                                // add any other job-level fields you use
                            };
                        }
                        return item;
                    });
                }

                return found ? newData : prev;
            });

            // Update unscheduledAssignments
            setUnscheduledAssignments(prev =>
                prev.map(item => {
                    if (item.id === updatedJob.id) {
                        return {
                            ...item,
                            customer_address: updatedJob.customer_address,
                            customer_name: updatedJob.customer_name,
                            job_status: updatedJob.status,
                        };
                    }
                    return item;
                })
            );

            // Update playgroundAssignments
            setPlaygroundAssignments(prev =>
                prev.map(item => {
                    if (item.id !== updatedJob.id) return item;
                    return {
                        ...item,
                        customer_address: updatedJob.customer_address,
                        customer_name: updatedJob.customer_name,
                        job_status: updatedJob.status,
                    };
                })
            );

            setOverdueAssignments(prev => {
                if (!prev.some(item => item.id === updatedJob.id)) return prev;
                return prev.map(item => {
                    if (item.id !== updatedJob.id) return item;
                    return {
                        ...item,
                        customer_address: updatedJob.customer_address,
                        customer_name: updatedJob.customer_name,
                        job_status: updatedJob.status,
                    };
                });
            });
        };

        const handleJobDeliveriesUpdate = (payload) => {
            if (payload.eventType !== 'UPDATE' && payload.eventType !== 'INSERT') return;

            const updated = payload.new;

            // INSERT — a brand-new delivery record was created (e.g. when assigning a driver to a
            // group member that had no delivery record yet). Add it to that card's job_deliveries.
            if (payload.eventType === 'INSERT') {
                const addDelivery = (items) => items.map(item => {
                    if (item.id !== updated.job_id) return item;
                    const existing = item.job_deliveries || [];
                    // Guard against duplicates (unlikely but safe)
                    if (existing.some(d => d.id === updated.id)) return item;
                    return { ...item, job_deliveries: [...existing, updated] };
                });

                setScheduleData(prev => {
                    const next = {};
                    for (const dayKey in prev) next[dayKey] = addDelivery(prev[dayKey]);
                    return next;
                });
                setUnscheduledAssignments(prev => addDelivery(prev));
                setPlaygroundAssignments(prev => addDelivery(prev));
                setOverdueAssignments(prev =>
                    addDelivery(prev).filter(item => item.id !== updated.job_id || !isCardDelivered(item))
                );
                return;
            }

            // UPDATE — patch the specific delivery record in place.

            // Helper: match the specific delivery record.
            // New-system: match by child_unique_id (= job_quotes UUID).
            // Old-system: match by quote_type + delivery_count.
            const deliveryMatches = (delivery) => {
                // Prefer matching by primary key when available
                if (updated.id && delivery.id) return delivery.id === updated.id;
                if (updated.child_unique_id && delivery.child_unique_id) {
                    return delivery.child_unique_id === updated.child_unique_id;
                }
                return (
                    delivery.quote_type === updated.quote_type &&
                    (delivery.delivery_count ?? 0) === (updated.delivery_count ?? 0)
                );
            };

            const patchDeliveries = (deliveries) =>
                (deliveries || []).map(d =>
                    deliveryMatches(d)
                        ? {
                            ...d,
                            delivery_status: updated.delivery_status,
                            assigned_driver_id: updated.assigned_driver_id,
                            assigned_driver_name: updated.assigned_driver_name,
                            delivery_count: updated.delivery_count,
                            delivery_date: updated.delivery_date,
                            delivered_at: updated.delivered_at
                        }
                        : d
                );

            setScheduleData(prev => {
                const newData = {};
                let found = false;

                for (const dayKey in prev) {
                    newData[dayKey] = prev[dayKey].map(item => {
                        if (item.id === updated.job_id) {
                            found = true;
                            return { ...item, job_deliveries: patchDeliveries(item.job_deliveries) };
                        }
                        return item;
                    });
                }

                return found ? newData : prev;
            });

            // Update unscheduledAssignments
            setUnscheduledAssignments(prev =>
                prev.map(item => {
                    if (item.id === updated.job_id) {
                        return { ...item, job_deliveries: patchDeliveries(item.job_deliveries) };
                    }
                    return item;
                })
            );

            setPlaygroundAssignments(prev =>
                prev.map(item => {
                    if (item.id !== updated.job_id) return item;
                    return { ...item, job_deliveries: patchDeliveries(item.job_deliveries) };
                })
            );

            // Completed deliveries drop out of the overdue section
            setOverdueAssignments(prev => {
                if (!prev.some(item => item.id === updated.job_id)) return prev;
                return prev
                    .map(item => item.id === updated.job_id
                        ? { ...item, job_deliveries: patchDeliveries(item.job_deliveries) }
                        : item)
                    .filter(item => item.id !== updated.job_id || !isCardDelivered(item));
            });
        };



        // Handler for job_production changes — updates assignment.production which is the
        // authoritative source used by getEffectiveProductionStatus().
        const handleJobProductionUpdate = (payload) => {
            if (payload.eventType !== 'UPDATE') return;
            const updated = payload.new;

            // Match cards by the production row UUID stored in assignment.production.id
            const patchItem = (item) => {
                if (item.production?.id !== updated.id) return item;
                return {
                    ...item,
                    production: {
                        ...item.production,
                        phase: updated.phase,
                        is_completed: updated.is_completed,
                        is_started_cutting: updated.is_started_cutting,
                        is_started_production: updated.is_started_production,
                        start_cutting_at: updated.start_cutting_at,
                        start_production_at: updated.start_production_at,
                    }
                };
            };

            setScheduleData(prev => {
                const newData = {};
                let found = false;
                for (const dayKey in prev) {
                    newData[dayKey] = prev[dayKey].map(item => {
                        const patched = patchItem(item);
                        if (patched !== item) found = true;
                        return patched;
                    });
                }
                return found ? newData : prev;
            });

            setUnscheduledAssignments(prev => {
                let found = false;
                const next = prev.map(item => {
                    const patched = patchItem(item);
                    if (patched !== item) found = true;
                    return patched;
                });
                return found ? next : prev;
            });

            setPlaygroundAssignments(prev => {
                let found = false;
                const next = prev.map(item => {
                    const patched = patchItem(item);
                    if (patched !== item) found = true;
                    return patched;
                });
                return found ? next : prev;
            });

            setOverdueAssignments(prev => {
                let found = false;
                const next = prev.map(item => {
                    const patched = patchItem(item);
                    if (patched !== item) found = true;
                    return patched;
                });
                return found ? next : prev;
            });
        };

        const quotesSubscription = subscribeToJobQuotesUpdates(company.id, handleJobQuotesUpdate);
        const deliveriesSubscription = subscribeToJobDeliveriesUpdates(company.id, handleJobDeliveriesUpdate);
        const jobsSubscription = subscribeToJobUpdates(company.id, handleJobsUpdate);
        const productionSubscription = subscribeToProductionUpdates(company.id, handleJobProductionUpdate);

        return () => {
            unsubscribeFromJobUpdates(quotesSubscription);
            unsubscribeFromJobUpdates(jobsSubscription);
            unsubscribeFromJobUpdates(deliveriesSubscription);
            unsubscribeFromJobUpdates(productionSubscription);
        };
    }, [company?.id]);

    const fetchDrivers = async () => {
        try {
            const driversData = await getCompanyDrivers(company.id);
            setDrivers(driversData || []);

            // Fetch workload for each driver
            const workloads = {};
            await Promise.all(
                driversData.map(async (driver) => {
                    const workload = await getDriverWorkload(driver.id, company.id);
                    workloads[driver.id] = workload;
                })
            );
            setDriverWorkloads(workloads);
        } catch (error) {
            console.error('Error fetching drivers:', error);
            Toast.error('Error fetching drivers');
        }
    };

    // Sort assignments by production status (completed first)
    const sortByProductionStatus = (assignments) => {
        return [...assignments].sort((a, b) => {
            const aProductionCompleted = getEffectiveProductionStatus(a) === 'completed';
            const bProductionCompleted = getEffectiveProductionStatus(b) === 'completed';

            // Completed production comes first
            if (aProductionCompleted && !bProductionCompleted) return -1;
            if (!aProductionCompleted && bProductionCompleted) return 1;

            // If both have same production status, maintain original order (or sort by time slot)
            return 0;
        });
    };

    const fetchPlaygroundAssignments = async () => {
        if (!company?.id) return;
        const { data } = await getDeliveryPlaygroundAssignments(company.id);
        setPlaygroundAssignments(data || []);
    };

    const fetchOverdueAssignments = async () => {
        if (!company?.id) return;
        // Drop stale responses when filters change faster than the query returns
        const seq = ++overdueFetchSeqRef.current;
        const { data, error } = await getOverdueDeliveries(company.id, {
            showOnlyMine: showOnlyMine ? userProfile.id : null,
            userId: selectedUser || null,
            productionFilter,
            quoteTypeFilters,
        }, getOverdueRange());
        if (seq !== overdueFetchSeqRef.current) return;
        if (error) {
            console.error('Error fetching overdue deliveries:', error);
            return;
        }
        setOverdueAssignments(data || []);
    };

    // Coalesces bursts of realtime events into a single overdue refetch
    const scheduleOverdueRefetch = () => {
        clearTimeout(overdueRefetchTimerRef.current);
        overdueRefetchTimerRef.current = setTimeout(() => fetchOverdueRef.current?.(), 500);
    };

    const fetchAssignments = async () => {
        try {
            setIsLoading(true);

            const weekStart = getCurrentWeekStart();

            // Fetch deliveries for each day of the week
            const dayPromises = days.map((day, dayIndex) => {
                const dayDate = getDayDateObject(dayIndex);
                return getDeliveriesForDate(company.id, dayDate, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null,
                    productionFilter, // Add this
                    quoteTypeFilters // Add this
                });
            });

            // Also fetch unscheduled
            const [unscheduledRes, ...dayResults] = await Promise.all([
                getUnscheduledDeliveries(company.id, {
                    showOnlyMine: showOnlyMine ? userProfile.id : null,
                    userId: selectedUser || null,
                    productionFilter, // Add this
                    quoteTypeFilters // Add this
                }),
                ...dayPromises
            ]);

            if (unscheduledRes.error) {
                throw unscheduledRes.error;
            }

            // Check for errors in day results
            dayResults.forEach((result, index) => {
                if (result.error) {
                    console.error(`Error fetching ${days[index].key}:`, result.error);
                }
            });

            // Organize by day
            const organized = {};
            days.forEach((day, index) => {
                organized[day.key] = dayResults[index]?.data || [];
            });

            setScheduleData(organized);
            setUnscheduledAssignments(unscheduledRes.data || []);
        } catch (error) {
            console.error('Error fetching deliveries:', error);
            Toast.error('Error fetching deliveries');
            setScheduleData({});
            setUnscheduledAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper functions
    const getAustralianDate = () => {
        return new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
    };

    // Get the start of the week based on offset
    const getCurrentWeekStart = () => {
        const today = getAustralianDate();
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 }); // Monday
        return addWeeks(currentWeekStart, weekOffset);
    };

    // Get week label for display
    const getWeekLabel = () => {
        const weekStart = getCurrentWeekStart();
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        return `${format(weekStart, 'dd MMM')} – ${format(weekEnd, 'dd MMM yyyy')}`;
    };

    // Get month/year label for the current week
    const getMonthLabel = () => {
        const weekStart = getCurrentWeekStart();
        return format(weekStart, 'MMMM yyyy');
    };

    // No longer needed - we fetch data directly for each day
    // const getCurrentWeekKey = () => {
    //     const weekNum = weekOffset + 1;
    //     if (weekNum >= 1 && weekNum <= 4) {
    //         return `week${weekNum}`;
    //     }
    //     return 'week1';
    // };

    // Get date for specific day index
    const getDayDate = (dayIndex) => {
        const weekStart = getCurrentWeekStart();
        const targetDate = addDays(weekStart, dayIndex);
        return format(targetDate, 'dd/MM/yyyy');
    };

    // Get date object for specific day index
    const getDayDateObject = (dayIndex) => {
        const weekStart = getCurrentWeekStart();
        const targetDate = addDays(weekStart, dayIndex);

        const utcTargetDate = new Date(Date.UTC(
            targetDate.getFullYear(),
            targetDate.getMonth(),
            targetDate.getDate(),
            12, 0, 0, 0
        ));

        return utcTargetDate;
    };

    const days = [
        { label: "Mon", key: "Monday" },
        { label: "Tue", key: "Tuesday" },
        { label: "Wed", key: "Wednesday" },
        { label: "Thu", key: "Thursday" },
        { label: "Fri", key: "Friday" },
        ...(showSaturday ? [{ label: "Sat", key: "Saturday" }] : []),
    ];

    // Keep latestRef in sync on every render so realtime handlers always read current values
    latestRef.current = { weekOffset, showOnlyMine, selectedUser, productionFilter, quoteTypeFilters, userProfile, showSaturday, company, days, scheduleData, unscheduledAssignments };
    fetchPlaygroundRef.current = fetchPlaygroundAssignments;
    fetchOverdueRef.current = fetchOverdueAssignments;

    // Playground: separate state — persists regardless of which week is visible.
    const [playgroundAssignments, setPlaygroundAssignments] = useState([]);
    // Playground is declared after latestRef is set, so patch it in separately every render
    latestRef.current.playgroundAssignments = playgroundAssignments;

    // Overdue: a read-only view over past weeks — the same card can also sit in a
    // visible day column, so it's kept separate from the lists above.
    const [overdueAssignments, setOverdueAssignments] = useState([]);
    latestRef.current.overdueAssignments = overdueAssignments;
    // Which card's quick-notes bubble is open + its position
    // null = closed; { quoteId, x, y, showAbove, flipLeft } = open
    const [openNotesBubble, setOpenNotesBubble] = useState(null);
    const [quickNoteDrafts, setQuickNoteDrafts] = useState({});

    const getMainStatusColor = (status) => {
        if (status === "ready_for_delivery") return 'bg-teal-500';
        if (status === "completed") return 'bg-gray-500';
        return 'bg-gray-400';
    };

    const getQuoteTypeStyle = (quoteType) => {
        switch (quoteType) {
            case 'roof':
                return { icon: FiHome, bgColor: 'bg-blue-100', textColor: 'text-blue-700', label: 'Roof' };
            case 'wall':
                return { icon: FiLayers, bgColor: 'bg-red-100', textColor: 'text-red-700', label: 'Wall' };
            case 'floor':
                return { icon: FiLayers, bgColor: 'bg-green-100', textColor: 'text-green-700', label: 'Floor' };
            case 'timber':
                return { icon: FiPackage, bgColor: 'bg-amber-100', textColor: 'text-amber-700', label: 'Timber' };
            case "beams":
                return {
                    icon: GiWoodPile,
                    bgColor: "bg-orange-100",
                    textColor: "text-orange-700",
                    label: "Beams",
                };

            case "flooring_sheets":
                return {
                    icon: SquareStack,
                    bgColor: "bg-violet-100",
                    textColor: "text-violet-700",
                    label: "Flooring Sheets",
                };
            default:
                return { icon: FiPackage, bgColor: 'bg-gray-100', textColor: 'text-gray-700', label: quoteType };
        }
    };

    const todayLabel = getAustralianDate().toLocaleDateString("en-AU", {
        weekday: "short",
        timeZone: "Australia/Melbourne",
    });

    // Date picker helper functions
    const handleOpenDatePicker = () => {
        setDatePickerValue(getCurrentWeekStart());
        setShowDatePicker(true);
    };

    // Note modal handlers
    const handleOpenNoteModal = (assignment, quoteType, currentNote = '', deliveryCount = 0) => {
        setCurrentEditingQuote({ assignment, quoteType, deliveryCount, quoteId: assignment.quote_info?.quoteId || null });
        setNoteText(currentNote);
        setShowNoteModal(true);
    };

    const handleSaveNote = async () => {
        if (!currentEditingQuote) return;

        const { assignment, quoteType, deliveryCount = 0, quoteId } = currentEditingQuote;

        // Close modal immediately for better UX
        setShowNoteModal(false);
        setCurrentEditingQuote(null);
        const savedNote = noteText;
        setNoteText('');

        try {
            let query = supabase.from('job_quotes').update({ delivery_note: savedNote });

            if (quoteId) {
                // Use UUID directly — works for both old and new system, no ambiguity
                query = query.eq('id', quoteId);
            } else {
                query = query.eq('job_id', assignment.id).eq('quote_type', quoteType);
                if (deliveryCount === 0) {
                    query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
                } else {
                    query = query.eq('delivery_count', deliveryCount);
                }
            }

            const { error } = await query;

            if (error) throw error;

            Toast.success('Note saved successfully');
        } catch (error) {
            console.error('Error saving note:', error);
            Toast.error('Failed to save note');
        }
    };

    // Time slot handlers
    const handleOpenTimeSlotModal = (assignment, quoteType, currentSlot = null, deliveryCount = 0) => {
        setCurrentEditingQuote({ assignment, quoteType, deliveryCount, quoteId: assignment.quote_info?.quoteId || null });
        setSelectedTimeSlot(currentSlot);
        setShowTimeSlotModal(true);
    };

    const handleSaveTimeSlot = async (slot) => {
        if (!currentEditingQuote) return;

        const { assignment, quoteType, deliveryCount = 0, quoteId } = currentEditingQuote;

        // Close modal immediately for better UX
        setShowTimeSlotModal(false);
        setCurrentEditingQuote(null);
        setSelectedTimeSlot(null);

        try {
            let query = supabase.from('job_quotes').update({ delivery_time_slot: slot });

            if (quoteId) {
                query = query.eq('id', quoteId);
            } else {
                query = query.eq('job_id', assignment.id).eq('quote_type', quoteType);
                if (deliveryCount === 0) {
                    query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
                } else {
                    query = query.eq('delivery_count', deliveryCount);
                }
            }

            const { error } = await query;

            if (error) throw error;

            if (slot === null) {
                Toast.success('Time slot cleared');
            } else {
                Toast.success(`Time slot set to ${slot}`);
            }
        } catch (error) {
            console.error('Error saving time slot:', error);
            Toast.error('Failed to save time slot');
        }
    };


    const handleOpenDeliveryTagModal = (assignment, quoteType, currentSlot = null, deliveryCount = 0) => {
        setCurrentEditingQuote({ assignment, quoteType, deliveryCount, quoteId: assignment.quote_info?.quoteId || null });
        setSelectedDeliveryTag(currentSlot);
        setShowDeliveryTagModal(true);
    };

    const handleSaveDeliveryTag = async (slot) => {
        if (!currentEditingQuote) return;

        const { assignment, quoteType, deliveryCount = 0, quoteId } = currentEditingQuote;

        // Close modal immediately for better UX
        setShowDeliveryTagModal(false);
        setCurrentEditingQuote(null);
        setSelectedDeliveryTag(null);

        try {
            let query = supabase.from('job_quotes').update({ delivery_tag: slot });

            if (quoteId) {
                query = query.eq('id', quoteId);
            } else {
                query = query.eq('job_id', assignment.id).eq('quote_type', quoteType);
                if (deliveryCount === 0) {
                    query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
                } else {
                    query = query.eq('delivery_count', deliveryCount);
                }
            }

            const { error } = await query;

            if (error) throw error;

            if (slot === null) {
                Toast.success('Delivery tag cleared');
            } else {
                Toast.success(`Delivery tag set to ${slot}`);
            }
        } catch (error) {
            console.error('Error saving delivery tag:', error);
            Toast.error('Failed to save delivery tag');
        }
    };

    const handleDatePickerSelect = (date) => {
        if (!date) return;

        const today = getAustralianDate();
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const selectedWeekStart = startOfWeek(date, { weekStartsOn: 1 });

        // Calculate the week offset
        const offset = differenceInWeeks(selectedWeekStart, currentWeekStart);
        setWeekOffset(offset);
        setShowDatePicker(false);
        setDatePickerValue(null);
    };

    const handleDragDatePickerSelect = (date) => {
        if (!date || !draggedForDatePicker) return;
        setSelectedDateFromPicker(date);
    };

    const handleDropOnDatePickerDay = () => {
        if (!selectedDateFromPicker || !draggedForDatePicker) return;

        const localDate = new Date(selectedDateFromPicker);
        const newDate = new Date(Date.UTC(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            10, 0, 0, 0
        ));

        const isPast = isDateInPast(newDate);
        setPendingMove({
            assignment: draggedForDatePicker,
            newDate: newDate,
            isPastDate: isPast
        });

        setShowDragDatePicker(false);
        setDraggedForDatePicker(null);
        setSelectedDateFromPicker(null);
        setActiveId(null);
        setShowConfirmation(true);
    };

    const handleGroupDragDatePickerSelect = (date) => {
        if (!date || !draggedGroupForDatePicker) return;
        setSelectedGroupDateFromPicker(date);
    };

    const handleGroupDropOnDatePickerDay = () => {
        if (!selectedGroupDateFromPicker || !draggedGroupForDatePicker) return;

        const localDate = new Date(selectedGroupDateFromPicker);
        const newDate = new Date(Date.UTC(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            10, 0, 0, 0
        ));

        const { groupId, groupMembers } = draggedGroupForDatePicker;
        handleGroupDateSet(groupId, groupMembers, newDate);

        setDraggedGroupForDatePicker(null);
        setSelectedGroupDateFromPicker(null);
    };

    const isDateInPast = (date) => {
        const today = startOfDay(getAustralianDate());
        return isBefore(startOfDay(date), today);
    };

    const isDayToday = (dayIndex) => {
        const targetDate = getDayDateObject(dayIndex);
        return isToday(targetDate);
    };

    const isDateDisabled = (date) => {
        return date.getDay() !== 0;
    };

    const holidayMap = useHolidays(company?.state);

    const toDateKey = (date) => {
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const getHolidaysForDate = (date) => {
        return holidayMap.get(toDateKey(date)) || [];
    };

    const getDragDatePickerDayClass = (date) => {
        const isHoliday = getHolidaysForDate(date).length > 0;
        const holidayClass = isHoliday ? 'react-datepicker__day--holiday' : '';

        if (!selectedDateFromPicker) return holidayClass;

        const isSameDate = date.getDate() === selectedDateFromPicker.getDate() &&
            date.getMonth() === selectedDateFromPicker.getMonth() &&
            date.getFullYear() === selectedDateFromPicker.getFullYear();

        return [isSameDate ? 'react-datepicker__day--selected' : '', holidayClass].filter(Boolean).join(' ');
    };

    const getDayClassName = (date) => {
        const isHoliday = getHolidaysForDate(date).length > 0;
        return isHoliday ? 'react-datepicker__day--holiday' : '';
    };

    const renderDayContents = (day, date) => {
        const holidays = getHolidaysForDate(date);
        if (holidays.length === 0) return <>{day}</>;
        return (
            <TextTooltip text={holidays.join(' · ')}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '2.5rem', height: '2.5rem' }}>
                    {day}
                </span>
            </TextTooltip>
        );
    };

    const handleOpenAssignment = (assignmentId) => {
        window.open(`/${company.slug || companySlug}/assignment/${assignmentId}`, '_blank');
    };

    const handleEditAssignment = (assignmentId) => {
        setEditJobId(assignmentId);
        setEditOpen(true);
    };

    const handleForceFinish = async (assignmentId, maybeAssignmentOrType, maybeType = null, quoteType = null) => {
        try {
            let assignment = null;
            let type = maybeType;

            // delivery call shape:
            // handleForceFinish(jobId, assignment, 'delivery')
            if (typeof maybeAssignmentOrType === 'object' && maybeAssignmentOrType !== null) {
                assignment = maybeAssignmentOrType;
            } else {
                // design / estimation / all call shape:
                // handleForceFinish(jobId, 'design', 'roof')
                type = maybeAssignmentOrType;
            }

            const result = await forceFinish(
                assignmentId,
                userProfile,
                type,
                quoteType,
                assignment
            );

            if (result?.success) {
                Toast.success(result.message || 'Force finish completed successfully');
                window.location.reload();
            } else {
                Toast.error(result?.error || 'Failed to force finish');
            }
        } catch (error) {
            console.error('Error in handleForceFinish:', error);
            Toast.error('Failed to force finish');
        }
    };

    const handleRemoveFromPlayground = async (quoteId) => {
        const removed = playgroundAssignments.find(p => (p.quote_info?.quoteId || p.id) === quoteId);
        setPlaygroundAssignments(prev => prev.filter(p => (p.quote_info?.quoteId || p.id) !== quoteId));
        setDeliveryPlayground(quoteId, false).catch(err => {
            console.error('Failed to remove from delivery playground:', err);
            if (removed) setPlaygroundAssignments(prev => [...prev, removed]);
        });
    };

    // Opens the confirmation popup; the actual undo runs in handleConfirmUndoCompletion
    const handleUndoDeliveryCompletion = (assignment, deliveryRecord) => {
        setPendingUndoCompletion({ assignment, deliveryRecord });
    };

    const handleConfirmUndoCompletion = async () => {
        const deliveryRecord = pendingUndoCompletion?.deliveryRecord;
        if (!deliveryRecord?.id) {
            setPendingUndoCompletion(null);
            return;
        }

        setIsUndoingCompletion(true);
        try {
            const result = await undoDeliveryCompletion(deliveryRecord.id, userProfile);

            if (!result?.success) {
                Toast.error(result?.error || 'Failed to undo completion');
                return;
            }

            // Optimistically patch the delivery row on every card list; realtime will confirm
            const patchCard = (item) => {
                if (item.id !== deliveryRecord.job_id) return item;
                return {
                    ...item,
                    job_deliveries: (item.job_deliveries || []).map(d =>
                        d.id === deliveryRecord.id
                            ? { ...d, delivery_status: result.status, completed: false, delivered_at: null }
                            : d
                    ),
                };
            };
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].map(patchCard);
                return next;
            });
            setUnscheduledAssignments(prev => prev.map(patchCard));
            setPlaygroundAssignments(prev => prev.map(patchCard));
            // An undone past delivery may belong back in the overdue section
            scheduleOverdueRefetch();

            Toast.success(result.message || 'Delivery completion undone');
        } catch (error) {
            console.error('Error undoing delivery completion:', error);
            Toast.error('Failed to undo completion');
        } finally {
            setIsUndoingCompletion(false);
            setPendingUndoCompletion(null);
        }
    };

    const handleToggleWaiting = async (assignmentId, quoteType, currentStatus, quoteId = null) => {
        try {
            await toggleWaitingJob(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            Toast.success(currentStatus ? 'Waiting removed' : 'Marked as Waiting');
        } catch (error) {
            console.error('Error toggling waiting job:', error);
            Toast.error('Failed to update Waiting status');
        }
    };

    const handleToggleSentProduction = async (assignmentId, quoteType, currentStatus, quoteId = null) => {
        try {
            await toggleSentProduction(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            Toast.success(currentStatus ? 'Removed from Production' : 'Sent to Production');
            // Optimistically patch sent_production on matching cards in both lists
            const patchCard = (item) => {
                const matches = quoteId
                    ? item.quote_info?.quoteId === quoteId
                    : (item.id === assignmentId && item.quote_info?.quote_type === quoteType);
                if (!matches) return item;
                return { ...item, quote_info: { ...item.quote_info, sent_production: !currentStatus } };
            };
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].map(patchCard);
                return next;
            });
            setUnscheduledAssignments(prev => prev.map(patchCard));
        } catch (error) {
            console.error('Error toggling sent_production:', error);
            Toast.error('Failed to update Production status');
        }
    };

    const getGroupDisplayIds = (members = []) =>
        members
            .map(m => m?.quote_info?.childUniqueId || m?.unique_id || '')
            .filter(Boolean);

    const logGroupedAction = ({
        members,
        action,
        details,
        quoteType = null,
    }) => {
        const representative = members?.[0];
        if (!representative) return;

        createJobActionLog({
            job_id: representative?.id,
            user_id: userProfile?.id,
            user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
            user_role: userProfile?.role,
            action,
            details,
            quote_type: quoteType || representative?.quote_info?.quote_type || null,
            action_origin: 'schedule-delivery',
            company_id: company.id,
        }).catch(() => { });
    };

    // Optimistically patch group_id/group_name on a card across all lists
    const patchCardGroup = (quoteId, groupId, groupName) => {
        const patch = (item) => {
            const itemQuoteId = item.quote_info?.quoteId || item.quote_info?.id;
            if (itemQuoteId !== quoteId) return item;
            return { ...item, quote_info: { ...item.quote_info, group_id: groupId, group_name: groupName } };
        };
        setScheduleData(prev => {
            const next = {};
            for (const dk in prev) next[dk] = prev[dk].map(patch);
            return next;
        });
        setUnscheduledAssignments(prev => prev.map(patch));
        setPlaygroundAssignments(prev => prev.map(patch));
        setOverdueAssignments(prev => prev.map(patch));
    };

    const getQuoteKey = (item) =>
        item?.quote_info?.quoteId ||
        item?.quote_info?.id ||
        `${item?.id}-${item?.quote_info?.quote_type}-${item?.quote_info?.delivery_count ?? 0}`;

    const getEffectiveGroupMembers = (groupId) => {
        const { scheduleData: sd, unscheduledAssignments: ua, playgroundAssignments: pa } = latestRef.current;

        const allCards = [
            ...Object.values(sd).flat(),
            ...ua,
            ...pa,
        ];

        const memberMap = new Map();

        // 1) Start with cards currently shown as belonging to this group
        for (const item of allCards) {
            if (item?.quote_info?.group_id === groupId) {
                memberMap.set(getQuoteKey(item), item);
            }
        }

        // 2) Apply pending additions for this group
        for (const [key, pending] of groupingPendingRef.current.entries()) {
            if (pending?.targetGroupId === groupId) {
                const pendingCard = {
                    ...pending.originalCard,
                    quote_info: {
                        ...pending.originalCard.quote_info,
                        group_id: pending.targetGroupId,
                        group_name: pending.targetGroupName,
                    }
                };
                memberMap.set(key, pendingCard);
            }
        }

        // 3) Apply removals for this group
        for (const [key, removed] of groupingRemovedRef.current.entries()) {
            if (removed?.groupId === groupId) {
                memberMap.delete(key);
            }
        }

        return [...memberMap.values()];
    };

    const getEffectiveGroupCountAfterRemoving = (groupId, quoteId) => {
        const members = getEffectiveGroupMembers(groupId);
        const remaining = members.filter(item => getQuoteKey(item) !== quoteId);
        return { members, remaining };
    };

    const logGroupChange = ({ member, action, details }) => {
        createJobActionLog({
            job_id: member?.id,
            user_id: userProfile?.id,
            user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
            user_role: userProfile?.role,
            action,
            details,
            quote_type: member?.quote_info?.quote_type || null,
            action_origin: 'schedule-delivery',
            company_id: company?.id,
        }).catch(() => { });
    };

    const handleCreateGroup = (assignment) => {
        const groupId = crypto.randomUUID();
        const displayId = assignment.quote_info?.childUniqueId || assignment.unique_id;
        const groupName = `${displayId} Group`;
        const quoteId = assignment.quote_info?.quoteId || assignment.quote_info?.id;

        // Find the card's current location before patching (so we can restore if needed)
        const { scheduleData: sd, unscheduledAssignments: ua, playgroundAssignments: pa } = latestRef.current;
        let firstCardLocation = 'unscheduled';
        let firstCardDayKey = null;
        if (ua.find(a => (a.quote_info?.quoteId || a.quote_info?.id) === quoteId)) {
            firstCardLocation = 'unscheduled';
        } else if (pa.find(a => (a.quote_info?.quoteId || a.quote_info?.id) === quoteId)) {
            firstCardLocation = 'playground';
        } else {
            for (const [dk, dayCards] of Object.entries(sd)) {
                if (dayCards.find(a => (a.quote_info?.quoteId || a.quote_info?.id) === quoteId)) {
                    firstCardLocation = 'schedule';
                    firstCardDayKey = dk;
                    break;
                }
            }
        }

        // Store in pending ref — no DB call until the user hits "Done"
        groupingPendingRef.current.set(quoteId, {
            originalCard: assignment,
            originalLocation: firstCardLocation,
            originalDayKey: firstCardDayKey,
            targetGroupId: groupId,
            targetGroupName: groupName,
            targetDate: null,
            targetDriverId: null,
            targetDriverName: null,
            targetDeliveryId: null,
            isFirstCard: true,
        });

        // Optimistically update UI only
        patchCardGroup(quoteId, groupId, groupName);
        // Enter grouping mode and open the new group's accordion
        setGroupingMode({ groupId, groupName });
        setOpenGroups(prev => new Set([...prev, groupId]));
    };

    const handleToggleCardInGroup = async (assignment) => {
        if (!groupingMode) return;
        const quoteId = assignment.quote_info?.quoteId || assignment.quote_info?.id;
        const currentGroupId = assignment.quote_info?.group_id;

        if (currentGroupId === groupingMode.groupId) {
            const pendingKey = quoteId || `${assignment.id}-${assignment.quote_info?.quote_type}-${assignment.quote_info?.delivery_count ?? 0}`;
            const pending = groupingPendingRef.current.get(pendingKey);

            const { members, remaining } = getEffectiveGroupCountAfterRemoving(groupingMode.groupId, pendingKey);

            // If removing this card leaves 0 or 1 member, disband the whole group.
            if (remaining.length < 1) {
                await handleDisbandGroup(groupingMode.groupId, members);
                return;
            }

            if (pending) {
                // Added this session — restore card to its original position (no DB call)
                const { originalCard, originalLocation, originalDayKey } = pending;
                const restoredCard = { ...originalCard };
                const cardMatchesKey = (item) => (item.quote_info?.quoteId || item.quote_info?.id) === pendingKey;

                setPlaygroundAssignments(prev => prev.filter(item => !cardMatchesKey(item)));
                setUnscheduledAssignments(prev => prev.filter(item => !cardMatchesKey(item)));
                setScheduleData(prev => {
                    const next = {};
                    for (const dk in prev) next[dk] = prev[dk].filter(item => !cardMatchesKey(item));

                    if (originalLocation === 'schedule' && originalDayKey) {
                        next[originalDayKey] = [...(next[originalDayKey] || []), restoredCard];
                    }
                    return next;
                });

                if (originalLocation === 'unscheduled') {
                    setUnscheduledAssignments(prev => [...prev, restoredCard]);
                } else if (originalLocation === 'playground') {
                    setPlaygroundAssignments(prev => [...prev, restoredCard]);
                }

                groupingPendingRef.current.delete(pendingKey);
            } else {
                // Persisted member removed during edit session
                groupingRemovedRef.current.set(pendingKey, {
                    member: assignment,
                    groupId: groupingMode.groupId,
                    groupName: groupingMode.groupName,
                });

                patchCardGroup(pendingKey, null, null);

                setDeliveryGroup(pendingKey, null, null).catch(() => {
                    groupingRemovedRef.current.delete(pendingKey);
                    patchCardGroup(pendingKey, groupingMode.groupId, groupingMode.groupName);
                });
            }

            return;
        }

        // ── ADD to group ───────────────────────────────────────────────────────
        // Snapshot current state synchronously via latestRef so we don't read stale closures
        const { scheduleData: sd, unscheduledAssignments: ua, playgroundAssignments: pa } = latestRef.current;

        // Step 1: Find where the group's existing members live (to determine the "home" location)
        let repMember = null;
        let repDayKey = null;
        let repLocation = null; // 'schedule' | 'unscheduled' | 'playground'

        outer: for (const [dayKey, dayCards] of Object.entries(sd)) {
            for (const a of dayCards) {
                if (a.quote_info?.group_id === groupingMode.groupId) {
                    repMember = a; repDayKey = dayKey; repLocation = 'schedule';
                    break outer;
                }
            }
        }
        if (!repMember) {
            const m = ua.find(a => a.quote_info?.group_id === groupingMode.groupId);
            if (m) { repMember = m; repLocation = 'unscheduled'; }
        }
        if (!repMember) {
            const m = pa.find(a => a.quote_info?.group_id === groupingMode.groupId);
            if (m) { repMember = m; repLocation = 'playground'; }
        }

        // Step 2: Pull representative date/driver from the group's home card
        const repDate = repMember?.quote_info?.expected_finish_date ?? null;
        const repQuoteId = repMember?.quote_info?.quoteId || repMember?.quote_info?.id;
        const repDelivery = repMember?.job_deliveries?.find(d =>
            repQuoteId && d.child_unique_id
                ? d.child_unique_id === repQuoteId
                : d.quote_type === repMember?.quote_info?.quote_type
        );
        const repDriverId = repDelivery?.assigned_driver_id ?? null;
        const repDriverName = repDelivery?.assigned_driver_name ?? null;

        // Step 3: Find the card's current location so we can restore it on unselect
        const addQuoteType = assignment.quote_info?.quote_type;
        const addDeliveryCount = assignment.quote_info?.delivery_count ?? 0;
        const cardMatches = (item) => quoteId
            ? item.quote_info?.quoteId === quoteId
            : (item.id === assignment.id &&
                item.quote_info?.quote_type === addQuoteType &&
                (item.quote_info?.delivery_count ?? 0) === addDeliveryCount);

        let currentCard = null;
        let addOriginalLocation = 'unscheduled';
        let addOriginalDayKey = null;

        const foundInUa = ua.find(cardMatches);
        if (foundInUa) {
            currentCard = foundInUa;
            addOriginalLocation = 'unscheduled';
        } else {
            const foundInPa = pa.find(cardMatches);
            if (foundInPa) {
                currentCard = foundInPa;
                addOriginalLocation = 'playground';
            } else {
                for (const [dk, dayCards] of Object.entries(sd)) {
                    const found = dayCards.find(cardMatches);
                    if (found) {
                        currentCard = found;
                        addOriginalLocation = 'schedule';
                        addOriginalDayKey = dk;
                        break;
                    }
                }
            }
        }
        const sourceCard = currentCard || assignment;

        // Find delivery record (needed for driver assignment when committed to DB)
        const addDelivery = sourceCard.job_deliveries?.find(d =>
            quoteId && d.child_unique_id
                ? d.child_unique_id === quoteId
                : d.quote_type === addQuoteType && (d.delivery_count ?? 0) === addDeliveryCount
        );

        // Build updated card with group info + group's date (visual only — no DB yet).
        // delivery_playground must match the group's home so the subscription handler
        // doesn't put the card back in the wrong section when realtime fires after commit.
        const groupHomeIsPlayground = repLocation === 'playground';
        const groupHomeIsScheduled = repLocation === 'schedule';
        const groupHomeIsUnscheduled = repLocation === 'unscheduled';

        const updatedCard = {
            ...sourceCard,
            quote_info: {
                ...sourceCard.quote_info,
                group_id: groupingMode.groupId,
                group_name: groupingMode.groupName,
                expected_finish_date: groupHomeIsScheduled ? repDate : null,
                expected_delivery_date: groupHomeIsScheduled ? repDate : null,
                delivery_playground: groupHomeIsPlayground,
            },
        };

        // Step 4: Optimistic state — remove from all locations
        setPlaygroundAssignments(prev => prev.filter(item => !cardMatches(item)));
        setUnscheduledAssignments(prev => prev.filter(item => !cardMatches(item)));
        setScheduleData(prev => {
            const next = {};
            for (const dk in prev) next[dk] = prev[dk].filter(item => !cardMatches(item));
            return next;
        });

        // Step 5: Insert at the group's home location
        if (repLocation === 'schedule' && repDayKey) {
            setScheduleData(prev => ({
                ...prev,
                [repDayKey]: [...(prev[repDayKey] || []), updatedCard],
            }));
        } else if (repLocation === 'unscheduled') {
            setUnscheduledAssignments(prev => [...prev, updatedCard]);
        } else if (repLocation === 'playground') {
            setPlaygroundAssignments(prev => [...prev, updatedCard]);
        } else {
            // Edge case: no existing group members yet — patch in place
            patchCardGroup(quoteId, groupingMode.groupId, groupingMode.groupName);
        }

        // Step 6: Store in pending ref (DB writes deferred until "Done")
        const pendingKey = quoteId || `${assignment.id}-${addQuoteType}-${addDeliveryCount}`;
        groupingPendingRef.current.set(pendingKey, {
            originalCard: sourceCard,
            originalLocation: addOriginalLocation,
            originalDayKey: addOriginalDayKey,
            targetGroupId: groupingMode.groupId,
            targetGroupName: groupingMode.groupName,
            targetDate: groupHomeIsScheduled ? repDate : null,
            targetDriverId: repDriverId,
            targetDriverName: repDriverName,
            targetDeliveryId: addDelivery?.id ?? null,
            targetLocation: repLocation,
            targetPlayground: groupHomeIsPlayground,
            isFirstCard: false,
        });

        const displayId = assignment?.quote_info?.childUniqueId || assignment?.unique_id || '';


    };

    const handleDisbandGroup = async (groupId, members) => {
        // First: restore any cards that were added this session (not yet in DB)
        for (const [pendingKey, p] of groupingPendingRef.current.entries()) {
            if (!p.isFirstCard) {
                const restoredCard = { ...p.originalCard };
                const otherMatches = (item) => (item.quote_info?.quoteId || item.quote_info?.id) === pendingKey;
                setPlaygroundAssignments(prev => prev.filter(item => !otherMatches(item)));
                setUnscheduledAssignments(prev => prev.filter(item => !otherMatches(item)));
                setScheduleData(prev => {
                    const next = {};
                    for (const dk in prev) next[dk] = prev[dk].filter(item => !otherMatches(item));
                    if (p.originalLocation === 'schedule' && p.originalDayKey) {
                        next[p.originalDayKey] = [...(next[p.originalDayKey] || []), restoredCard];
                    }
                    return next;
                });
                if (p.originalLocation === 'unscheduled') {
                    setUnscheduledAssignments(prev => [...prev, restoredCard]);
                } else if (p.originalLocation === 'playground') {
                    setPlaygroundAssignments(prev => [...prev, restoredCard]);
                }
            }
        }
        groupingPendingRef.current.clear();

        // Optimistic: clear group_ids from pre-existing members
        members.forEach(a => {
            const quoteId = a.quote_info?.quoteId || a.quote_info?.id;
            patchCardGroup(quoteId, null, null);
        });
        setGroupingMode(null);
        try {
            await disbandDeliveryGroup(groupId);
            Toast.success('Group disbanded');
        } catch {
            members.forEach(a => {
                const quoteId = a.quote_info?.quoteId || a.quote_info?.id;
                patchCardGroup(quoteId, groupId, members[0]?.quote_info?.group_name || 'Group');
            });
            Toast.error('Failed to disband group');
        }
    };

    // Commit all pending grouping changes to the DB (called when "Done" is clicked).
    //
    // Key design: for each added card we fire ONE update that sets group_id + date together
    // (via commitGroupMember). Two separate calls would produce two realtime events — the first
    // would fire with the old date still in the DB, making the card jump back to its original
    // day column for a frame before the second call corrected it.
    const handleCommitGrouping = async () => {
        const pending = new Map(groupingPendingRef.current);
        const removed = new Map(groupingRemovedRef.current);

        groupingPendingRef.current.clear();
        groupingRemovedRef.current.clear();
        setGroupingMode(null);

        if (pending.size === 0 && removed.size === 0) return;

        // Collect driver delivery IDs so we can batch-assign in one call at the end
        const driverDeliveryIds = [];
        let batchDriverId = null;
        let batchDriverName = null;

        // Determine the group's home location from the first card's original location.
        // This decides whether cards crossing the playground boundary need the flag updated.
        const firstCardPending = [...pending.values()].find(p => p.isFirstCard);
        const groupHomeIsPlayground = firstCardPending?.originalLocation === 'playground';

        try {
            for (const [pendingKey, p] of pending.entries()) {
                if (p.isFirstCard) {
                    // First card never moves — just stamp the group membership.
                    // Its playground flag is already correct (it stayed where it was).
                    await setDeliveryGroup(pendingKey, p.targetGroupId, p.targetGroupName);
                } else {
                    // Added card: combine group_id + new date + playground flag in ONE update
                    // so realtime always sees the card in its final location.
                    const targetDate = p.targetDate !== (p.originalCard.quote_info?.expected_finish_date ?? null)
                        ? p.targetDate   // date changed — include it
                        : undefined;     // unchanged — omit to avoid an unnecessary write

                    // Only write delivery_playground when the card crosses the playground boundary
                    let targetPlayground;
                    if (p.originalLocation === 'playground' && !groupHomeIsPlayground) {
                        targetPlayground = false; // card was in playground, group lives in schedule/waiting
                    } else if (p.originalLocation !== 'playground' && groupHomeIsPlayground) {
                        targetPlayground = true;  // card was in schedule/waiting, group lives in playground
                    }
                    // same location → undefined (no write needed)

                    await commitGroupMember(pendingKey, p.targetGroupId, p.targetGroupName, targetDate, targetPlayground);
                }

                // Collect driver info for the batch call below
                if (!p.isFirstCard && p.targetDriverId && p.targetDeliveryId) {
                    driverDeliveryIds.push(p.targetDeliveryId);
                    batchDriverId = p.targetDriverId;
                    batchDriverName = p.targetDriverName;
                }
            }

            // Assign driver to all collected deliveries in one UPDATE → one realtime event
            if (driverDeliveryIds.length > 0) {
                await setGroupDriverBatch(
                    driverDeliveryIds,
                    batchDriverId,
                    batchDriverName,
                    userProfile.id,
                    userProfile.full_name || userProfile.username
                );
            }

            const addedIds = [];
            const removedIds = [];

            // Collect added
            for (const [, p] of pending.entries()) {
                if (!p.isFirstCard) {
                    const member = p.originalCard;
                    const displayId = member?.quote_info?.childUniqueId || member?.unique_id || '';
                    if (displayId) addedIds.push(displayId);
                }
            }

            // Collect removed
            for (const [, r] of removed.entries()) {
                const member = r.member;
                const displayId = member?.quote_info?.childUniqueId || member?.unique_id || '';
                if (displayId) removedIds.push(displayId);
            }

            if (addedIds.length > 0) {
                const groupName = [...pending.values()][0]?.targetGroupName || 'Group';

                logGroupChange({
                    member: [...pending.values()][0]?.originalCard,
                    action: 'group_add_member',
                    details: `Added ${addedIds.length} deliveries (${addedIds.join(', ')}) to group "${groupName}"`
                });
            }

            if (removedIds.length > 0) {
                const groupName = [...removed.values()][0]?.groupName || 'Group';

                logGroupChange({
                    member: [...removed.values()][0]?.member,
                    action: 'group_remove_member',
                    details: `Removed ${removedIds.length} deliveries (${removedIds.join(', ')}) from group "${groupName}"`
                });
            }

            Toast.success('Group saved');
        } catch (err) {
            console.error('Failed to commit group changes:', err);
            Toast.error('Failed to save group');
            fetchAssignments();
        }
    };

    // Set date for all members in a group — single DB call to avoid N realtime events
    const handleGroupDateSet = async (groupId, members, date) => {
        const localDate = new Date(date);
        const newDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 10, 0, 0, 0));
        const newDateIso = newDate.toISOString();

        // Work out which day column the new date maps to (for optimistic UI)
        const { days: currentDays } = latestRef.current;
        const weekStart = getCurrentWeekStart();
        let targetDayKey = null;
        currentDays.forEach((day, dayIndex) => {
            const dayDate = addDays(weekStart, dayIndex);
            if (dayDate.getFullYear() === newDate.getUTCFullYear() &&
                dayDate.getMonth() === newDate.getUTCMonth() &&
                dayDate.getDate() === newDate.getUTCDate()) {
                targetDayKey = day.key;
            }
        });

        // Optimistic update — move all members to the target day column at once.
        // Members are pulled from all three lists (schedule, unscheduled, playground)
        // so that group date changes always work regardless of where the group lives.
        members.forEach(m => {
            const quoteId = m.quote_info?.quoteId || m.quote_info?.id;
            const quoteType = m.quote_info?.quote_type;
            const deliveryCount = m.quote_info?.delivery_count ?? 0;
            const cardMatches = (item) => quoteId
                ? item.quote_info?.quoteId === quoteId
                : (item.id === m.id && item.quote_info?.quote_type === quoteType && (item.quote_info?.delivery_count ?? 0) === deliveryCount);

            // Remove from unscheduled and playground
            setUnscheduledAssignments(prev => prev.filter(item => !cardMatches(item)));
            setPlaygroundAssignments(prev => prev.filter(item => !cardMatches(item)));
            setOverdueAssignments(prev => prev.filter(item => !cardMatches(item)));

            // Remove from all schedule columns, then insert into target day
            setScheduleData(prev => {
                let movedCard = null;
                const next = {};
                for (const dk in prev) {
                    next[dk] = prev[dk].filter(item => {
                        if (cardMatches(item)) { movedCard = item; return false; }
                        return true;
                    });
                }
                if (targetDayKey) {
                    const updated = {
                        ...(movedCard || m),
                        quote_info: {
                            ...(movedCard || m).quote_info,
                            expected_finish_date: newDateIso,
                            delivery_playground: false,
                            quote_playground: false,
                        },
                    };
                    next[targetDayKey] = [...(next[targetDayKey] || []), updated];
                }
                return next;
            });
        });

        // One DB call for the whole group — fires a single realtime event
        const { error } = await setGroupDeliveryDate(groupId, newDate);
        if (error) {
            Toast.error('Failed to update group date');
            fetchAssignments();
        } else {
            const displayIds = getGroupDisplayIds(members);
            const groupName = members?.[0]?.quote_info?.group_name || 'Group';

            logGroupedAction({
                members,
                action: 'reschedule',
                details: `Scheduled deliveries [${displayIds.join(', ')}] from group "${groupName}" to ${targetDayKey} (${format(newDate, 'd MMM')})`,
            });
            Toast.success(`Date set for ${members.length} ${members.length === 1 ? 'delivery' : 'deliveries'}`);
        }
    };

    // Move all group members to the unscheduled (waiting) area
    const handleGroupMoveToWaiting = async (groupId, members) => {
        const count = members.length;

        // Optimistic: pull all members out of every list and land them in unscheduled
        members.forEach(m => {
            const quoteId = m.quote_info?.quoteId || m.quote_info?.id;
            const quoteType = m.quote_info?.quote_type;
            const deliveryCount = m.quote_info?.delivery_count ?? 0;
            const cardMatches = (item) => quoteId
                ? item.quote_info?.quoteId === quoteId
                : (item.id === m.id && item.quote_info?.quote_type === quoteType && (item.quote_info?.delivery_count ?? 0) === deliveryCount);

            const waitingCard = {
                ...m,
                quote_info: {
                    ...m.quote_info,
                    expected_delivery_date: null,
                    expected_finish_date: null,
                    delivery_playground: false,
                    quote_playground: false,
                    waiting_job: true,
                },
            };

            setPlaygroundAssignments(prev => prev.filter(item => !cardMatches(item)));
            setOverdueAssignments(prev => prev.filter(item => !cardMatches(item)));
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].filter(item => !cardMatches(item));
                return next;
            });
            setUnscheduledAssignments(prev => {
                if (prev.some(item => cardMatches(item))) return prev;
                return [...prev, waitingCard];
            });
        });

        const { error } = await setGroupWaiting(groupId);
        if (error) {
            Toast.error('Failed to move group to waiting');
            fetchAssignments();
        } else {
            const displayIds = getGroupDisplayIds(members);
            const groupName = members?.[0]?.quote_info?.group_name || 'Group';

            logGroupedAction({
                members,
                action: 'unschedule',
                details: `Moved deliveries [${displayIds.join(', ')}] from group "${groupName}" to waiting`,
            });
            Toast.success(`${count} ${count === 1 ? 'delivery' : 'deliveries'} moved to waiting`);
        }
    };

    // Move all group members to the playground
    const handleGroupMoveToPlayground = async (groupId, members) => {
        const count = members.length;

        // Optimistic: pull all members out of every list and land them in playground
        members.forEach(m => {
            const quoteId = m.quote_info?.quoteId || m.quote_info?.id;
            const quoteType = m.quote_info?.quote_type;
            const deliveryCount = m.quote_info?.delivery_count ?? 0;
            const cardMatches = (item) => quoteId
                ? item.quote_info?.quoteId === quoteId
                : (item.id === m.id && item.quote_info?.quote_type === quoteType && (item.quote_info?.delivery_count ?? 0) === deliveryCount);

            const alreadyPinned = playgroundAssignments.some(item => cardMatches(item));
            if (alreadyPinned) return;

            const playgroundCard = {
                ...m,
                quote_info: {
                    ...m.quote_info,
                    expected_delivery_date: null,
                    expected_finish_date: null,
                    delivery_playground: true,
                },
            };

            setUnscheduledAssignments(prev => prev.filter(item => !cardMatches(item)));
            setOverdueAssignments(prev => prev.filter(item => !cardMatches(item)));
            setScheduleData(prev => {
                const next = {};
                for (const dk in prev) next[dk] = prev[dk].filter(item => !cardMatches(item));
                return next;
            });
            setPlaygroundAssignments(prev => [...prev, playgroundCard]);
        });

        const { error } = await setGroupPlayground(groupId, true);
        if (error) {
            Toast.error('Failed to move group to playground');
            fetchAssignments();
        } else {
            const displayIds = getGroupDisplayIds(members);
            const groupName = members?.[0]?.quote_info?.group_name || 'Group';

            logGroupedAction({
                members,
                action: 'set_playground',
                details: `Pinned deliveries [${displayIds.join(', ')}] from group "${groupName}" to playground`,
            });

            Toast.success(`${count} ${count === 1 ? 'delivery' : 'deliveries'} pinned to playground`);
        }
    };

    // Set driver for all members in a group (creates delivery records if missing, like assignDriver)
    // Assign driver to all group members — creates missing delivery records individually
    // (unavoidable), then does ONE batch UPDATE so realtime fires once for the whole group
    const handleGroupDriverSet = async (groupId, members, driverId, driverName) => {
        try {
            const deliveryIds = [];

            for (const m of members) {
                const quoteType = m.quote_info?.quote_type;
                const deliveryCount = m.quote_info?.delivery_count ?? 0;
                const quoteId = m.quote_info?.quoteId || null;

                // Look for an existing delivery record
                let delivery = m.job_deliveries?.find(d =>
                    quoteId && d.child_unique_id
                        ? d.child_unique_id === quoteId
                        : d.quote_type === quoteType && (d.delivery_count ?? 0) === deliveryCount
                );

                // Create a delivery record if none exists yet
                if (!delivery) {
                    const quote = m.job_quotes?.find(q =>
                        quoteId ? q.id === quoteId : (q.quote_type === quoteType && q.is_required)
                    );
                    if (!quote) {
                        console.warn('No matching quote for group member, skipping:', m.id);
                        continue;
                    }
                    const { data: newDelivery, error: insertError } = await supabase
                        .from('job_deliveries')
                        .insert({
                            job_id: m.id,
                            quote_type: quoteType,
                            is_required: true,
                            delivery_status: 'not_scheduled',
                            delivery_address: m.customer_address,
                            delivery_contact_name: m.customer_name,
                            delivery_contact_phone: m.customer_number,
                            delivery_count: deliveryCount,
                            child_unique_id: quoteId || null,
                            child_id: quoteId || null,
                        })
                        .select()
                        .single();
                    if (insertError || !newDelivery) {
                        console.error('Failed to create delivery record for group member:', insertError);
                        continue;
                    }
                    delivery = newDelivery;
                }

                deliveryIds.push(delivery.id);
            }

            // One UPDATE for every delivery record — single realtime event for the whole group
            const { error } = await setGroupDriverBatch(
                deliveryIds, driverId, driverName,
                userProfile.id, userProfile.full_name || userProfile.username
            );
            if (error) throw error;

            await fetchAssignments();
            await fetchDrivers();
            fetchPlaygroundRef.current?.();
            fetchOverdueRef.current?.();
            Toast.success(`Driver assigned to group`);
        } catch (err) {
            console.error('Error assigning group driver:', err);
            Toast.error('Failed to assign group driver');
        }
    };

    // Split assignments into groups (Map) and ungrouped (array)
    const splitByGroup = (sortedAssignments) => {
        const groups = new Map(); // groupId -> { name, items }
        const ungrouped = [];
        sortedAssignments.forEach(a => {
            const gid = a.quote_info?.group_id;
            if (gid) {
                if (!groups.has(gid)) {
                    groups.set(gid, { name: a.quote_info.group_name || 'Group', items: [] });
                }
                groups.get(gid).items.push(a);
            } else {
                ungrouped.push(a);
            }
        });
        return { groups, ungrouped };
    };

    // ── Eye button: jump from an overdue card to the same job in the week view ──
    const getJobWeekInfo = (assignment) => {
        const dateStr = assignment.quote_info?.expected_finish_date?.slice(0, 10);
        if (!dateStr) return null;
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        const thisWeek = startOfWeek(getAustralianDate(), { weekStartsOn: 1 });
        const jobWeek = startOfWeek(date, { weekStartsOn: 1 });
        // Round, not floor — a DST change makes the gap an hour off a whole number of weeks
        return { date, jobWeek, offset: Math.round((jobWeek - thisWeek) / (7 * 86400000)) };
    };

    const getLocateLabel = (assignment) => {
        const info = getJobWeekInfo(assignment);
        if (!info) return '';
        return info.offset === weekOffset
            ? 'Show this job in the week view'
            : `Go to week of ${format(info.jobWeek, 'd MMM')} and show this job`;
    };

    // Red pulse drawn inside the card (negative offset + inset shadow) so the day
    // column's overflow can't clip it; the outer glow is a bonus where there's room
    const flashLocated = (el) => {
        el.animate(
            [
                {
                    outline: '4px solid rgba(220, 38, 38, 1)',
                    outlineOffset: '-4px',
                    boxShadow: 'inset 0 0 0 4px rgba(220, 38, 38, 0.35), 0 0 22px 6px rgba(220, 38, 38, 0.55)',
                },
                {
                    outline: '4px solid rgba(220, 38, 38, 0.35)',
                    outlineOffset: '-4px',
                    boxShadow: 'inset 0 0 0 4px rgba(220, 38, 38, 0), 0 0 0 0 rgba(220, 38, 38, 0)',
                },
            ],
            { duration: 500, delay: 250, iterations: 8, direction: 'alternate', easing: 'ease-in-out' }
        );
    };

    // Switches week if needed, then waits for the card (or group) to render in the
    // week view, scrolls it to the centre of its column/page and flashes an outline
    const handleLocateOverdue = (assignment, groupId = null) => {
        const info = getJobWeekInfo(assignment);
        if (!info) return;

        const weekday = info.date.getDay();
        if (weekday === 0) {
            Toast.info("This delivery is on a Sunday, which the week view doesn't show");
            return;
        }
        if (weekday === 6 && !showSaturday) setShowSaturday(true);
        setExpandedDay(null);
        if (info.offset !== weekOffset) setWeekOffset(info.offset);

        const selector = groupId
            ? `[data-locate-group="${CSS.escape(groupId)}"]`
            : `[data-locate-key="${CSS.escape(getCardBaseId(assignment))}"]`;
        const startedAt = Date.now();

        clearTimeout(locateTimerRef.current);
        const tryFind = () => {
            const el = document.querySelector(selector);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                flashLocated(el);
                return;
            }
            if (Date.now() - startedAt > 8000) {
                Toast.info("Couldn't find this job in the week view");
                return;
            }
            locateTimerRef.current = setTimeout(tryFind, 100);
        };
        tryFind();
    };

    // One group container. idPrefix keeps draggable IDs unique when a group is rendered
    // in two sections; compact shows a card-sized summary that fills its grid cell.
    const renderGroupContainer = (gid, name, items, { idPrefix = '', compact = false, onLocate = null, locateLabel = '' } = {}) => (
        <GroupContainer
            key={gid}
            groupId={gid}
            groupName={name}
            items={items}
            isOpen={openGroups.has(gid)}
            onToggleOpen={() => setOpenGroups(prev => {
                const next = new Set(prev);
                next.has(gid) ? next.delete(gid) : next.add(gid);
                return next;
            })}
            onEditGroup={() => {
                setGroupingMode({ groupId: gid, groupName: name });
                setOpenGroups(prev => new Set([...prev, gid]));
            }}
            isEditing={groupingMode?.groupId === gid}
            isAdmin={userProfile?.role === 'admin' || userProfile?.role === 'super_admin'}
            viewingMode={viewingMode}
            drivers={drivers}
            onDateSet={handleGroupDateSet}
            onDriverSet={handleGroupDriverSet}
            onDisband={handleDisbandGroup}
            onCommit={handleCommitGrouping}
            onToggleCard={handleToggleCardInGroup}
            idPrefix={idPrefix}
            compact={compact}
            onLocate={onLocate}
            locateLabel={locateLabel}
            renderCard={(a) => (
                <AssignmentContextMenu
                    assignment={a}
                    onOpenAssignment={handleOpenAssignment}
                    onEditAssignment={handleEditAssignment}
                    onForceFinish={handleForceFinish}
                    onToggleWaiting={handleToggleWaiting}
                    onToggleSentProduction={handleToggleSentProduction}
                    onUndoDeliveryCompletion={handleUndoDeliveryCompletion}
                    page="delivery"
                    userRole={userProfile?.role}
                >
                    <div>
                        {renderStableAssignmentCard(a, { inGroup: true, idPrefix })}
                    </div>
                </AssignmentContextMenu>
            )}
        />
    );

    // One ungrouped card with the grouping-mode "Add to group" overlay.
    // header renders above the card; fill stretches the card to its grid cell.
    const renderUngroupedCard = (assignment, { idPrefix = '', header = null, fill = false, onLocate = null, locateLabel = '' } = {}) => {
        const isTargetable = groupingMode && !assignment.quote_info?.group_id;
        const cardKey = assignment.quote_info?.quoteId || `${assignment.id}-${assignment.quote_info?.quote_type}-${assignment.quote_info?.delivery_count ?? 0}`;
        const card = renderStableAssignmentCard(assignment, { idPrefix, onLocate, locateLabel });
        return (
            <div key={cardKey} className={`relative ${fill ? 'h-full' : ''}`}>
                {isTargetable && (
                    <div
                        className="absolute inset-0 z-20 rounded-lg cursor-pointer flex items-center justify-center bg-indigo-500/10 hover:bg-indigo-500/20 border-2 border-dashed border-indigo-400 transition-all"
                        onClick={(e) => { e.stopPropagation(); handleToggleCardInGroup(assignment); }}
                    >
                        <span className="bg-indigo-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium shadow opacity-0 hover:opacity-100 transition-opacity">Add to group</span>
                    </div>
                )}
                <AssignmentContextMenu
                    assignment={assignment}
                    onOpenAssignment={handleOpenAssignment}
                    onEditAssignment={handleEditAssignment}
                    onForceFinish={handleForceFinish}
                    onToggleWaiting={handleToggleWaiting}
                    onToggleSentProduction={handleToggleSentProduction}
                    onUndoDeliveryCompletion={handleUndoDeliveryCompletion}
                    page="delivery"
                    userRole={userProfile?.role}
                >
                    <div className={fill ? 'h-full flex flex-col' : undefined}>
                        {header}
                        {fill ? <div className="flex-1 min-h-0">{card}</div> : card}
                    </div>
                </AssignmentContextMenu>
            </div>
        );
    };

    // Render assignments split into group containers + ungrouped cards
    const renderGroupedSection = (sortedAssignments) => {
        const { groups, ungrouped } = splitByGroup(sortedAssignments);
        return (
            <>
                {[...groups.entries()].map(([gid, { name, items }]) => renderGroupContainer(gid, name, items))}
                {ungrouped.map(assignment => renderUngroupedCard(assignment))}
            </>
        );
    };

    // Render the playground area — like renderGroupedSection but with a per-card
    // remove-from-playground button for ungrouped cards. Grouped cards move as a
    // unit via their date handle (drag to unscheduled-zone / day column).
    const renderPlaygroundSection = (assignments) => {
        const { groups, ungrouped } = splitByGroup(assignments);

        return (
            <>
                {/* Group containers */}
                {[...groups.entries()].map(([gid, { name, items }]) => (
                    <GroupContainer
                        key={gid}
                        groupId={gid}
                        groupName={name}
                        items={items}
                        isOpen={openGroups.has(gid)}
                        onToggleOpen={() => setOpenGroups(prev => {
                            const next = new Set(prev);
                            next.has(gid) ? next.delete(gid) : next.add(gid);
                            return next;
                        })}
                        onEditGroup={() => {
                            setGroupingMode({ groupId: gid, groupName: name });
                            setOpenGroups(prev => new Set([...prev, gid]));
                        }}
                        isEditing={groupingMode?.groupId === gid}
                        isAdmin={userProfile?.role === 'admin' || userProfile?.role === 'super_admin'}
                        viewingMode={viewingMode}
                        drivers={drivers}
                        onDateSet={handleGroupDateSet}
                        onDriverSet={handleGroupDriverSet}
                        onDisband={handleDisbandGroup}
                        onCommit={handleCommitGrouping}
                        onToggleCard={handleToggleCardInGroup}
                        renderCard={(a) => (
                            <AssignmentContextMenu
                                assignment={a}
                                onOpenAssignment={handleOpenAssignment}
                                onEditAssignment={handleEditAssignment}
                                onForceFinish={handleForceFinish}
                                onToggleWaiting={handleToggleWaiting}
                                onToggleSentProduction={handleToggleSentProduction}
                                onUndoDeliveryCompletion={handleUndoDeliveryCompletion}
                                page="delivery"
                                userRole={userProfile?.role}
                            >
                                {renderStableAssignmentCard(a, { inGroup: true, playgroundMode: true })}
                            </AssignmentContextMenu>
                        )}
                    />
                ))}

                {/* Ungrouped playground cards — keep the remove button */}
                {ungrouped.map(assignment => {
                    const key = assignment.quote_info?.quoteId || assignment.id;
                    const isTargetable = groupingMode && !assignment.quote_info?.group_id;
                    return (
                        <div key={key} className="relative group">
                            {/* Remove from playground × button */}
                            <button
                                onClick={() => handleRemoveFromPlayground(key)}
                                className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-0.5 shadow border border-gray-200 hover:bg-red-50 hover:border-red-300"
                                title="Remove from playground"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>

                            {/* Grouping-mode click overlay */}
                            {isTargetable && (
                                <div
                                    className="absolute inset-0 z-20 rounded-lg cursor-pointer flex items-center justify-center bg-indigo-500/10 hover:bg-indigo-500/20 border-2 border-dashed border-indigo-400 transition-all"
                                    onClick={(e) => { e.stopPropagation(); handleToggleCardInGroup(assignment); }}
                                >
                                    <span className="bg-indigo-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium shadow opacity-0 hover:opacity-100 transition-opacity">Add to group</span>
                                </div>
                            )}

                            <AssignmentContextMenu
                                assignment={assignment}
                                onOpenAssignment={handleOpenAssignment}
                                onEditAssignment={handleEditAssignment}
                                onForceFinish={handleForceFinish}
                                onToggleWaiting={handleToggleWaiting}
                                onToggleSentProduction={handleToggleSentProduction}
                                onUndoDeliveryCompletion={handleUndoDeliveryCompletion}
                                page="delivery"
                                userRole={userProfile?.role}
                            >
                                {renderStableAssignmentCard(assignment, { isUnscheduled: true, playgroundMode: true })}
                            </AssignmentContextMenu>
                        </div>
                    );
                })}
            </>
        );
    };

    // Sort assignments by delivery time slot
    const sortByTimeSlot = (assignments) => {
        // Create a copy to avoid mutating the original array
        const sorted = [...assignments];

        // Define time slot order: First Delivery, Second Delivery, Before Lunch, Lunchtime, After Lunch, Last Delivery, then null/undefined last
        const timeSlotOrder = {
            'First Delivery': 1,
            'Second Delivery': 2,
            'Before Lunch': 3,
            'Lunchtime': 4,
            'After Lunch': 5,
            'Last Delivery': 6
        };

        return sorted.sort((a, b) => {
            // First, sort by time slot
            const slotA = a.quote_info?.delivery_time_slot;
            const slotB = b.quote_info?.delivery_time_slot;

            // Get time slot order values (use 999 for no slot so they appear last)
            const orderA = slotA ? (timeSlotOrder[slotA] || 999) : 999;
            const orderB = slotB ? (timeSlotOrder[slotB] || 999) : 999;

            // If time slots are different, sort by time slot
            if (orderA !== orderB) {
                return orderA - orderB;
            }

            // If time slots are the same (or both don't have time slots),
            // then sort by production status (completed first)
            const aProductionCompleted = getEffectiveProductionStatus(a) === 'completed';
            const bProductionCompleted = getEffectiveProductionStatus(b) === 'completed';

            if (aProductionCompleted && !bProductionCompleted) return -1;
            if (!aProductionCompleted && bProductionCompleted) return 1;

            // If both have same production status, maintain original order
            return 0;
        });
    };

    // Drag and Drop Handlers
    const handleDragStart = (event) => {
        const handleType = event.active.data.current?.handleType;

        if (handleType === 'driver') {
            handleDriverDragStart(event);
        } else if (handleType === 'group-driver') {
            // Group driver drag — open the driver panel so members can be dropped
            setActiveId(event.active.id);
            setDragHandleType('group-driver');
            setDraggedItemCache(event.active.data.current?.groupMembers);
            openPanel();
        } else if (handleType === 'group-date') {
            // Group date drag — drag to a day column to move the whole group
            setActiveId(event.active.id);
            setDragHandleType('group-date');
            setDraggedItemCache(event.active.data.current?.groupMembers);
        } else {
            // Default date drag
            setActiveId(event.active.id);
            setDragHandleType('date');

            const assignment = event.active.data.current?.assignment;
            if (assignment) {
                setDraggedItemCache(assignment);
            }
        }
    };

    const handleDragOver = (event) => {
        if (dragHandleType === 'driver' || dragHandleType === 'group-driver') {
            handleDriverDragOver(event);
        }
    };

    const handleDragEnd = (event) => {
        if (dragHandleType === 'driver' || event.active.data.current?.handleType === 'driver') {
            handleDriverDragEnd(event);
        } else if (dragHandleType === 'group-date' || event.active.data.current?.handleType === 'group-date') {
            handleGroupDateDragEnd(event);
        } else if (dragHandleType === 'group-driver' || event.active.data.current?.handleType === 'group-driver') {
            handleGroupDriverDragEnd(event);
        } else {
            // Original date drag logic
            const { active, over } = event;

            setDragOverWeek(null);
            setSwitchProgress(0);
            const assignmentData = draggedItemCache || active.data.current?.assignment;

            setDraggedItemCache(null);

            if (!over) {
                setActiveId(null);
                return;
            }

            if (over.id.toString().startsWith('week-selector-') || over.id === 'overdue-zone') {
                setActiveId(null);
                return;
            }

            // Handle drop on date picker drop zone - open the modal
            if (over.id === 'date-picker-drop-zone') {
                if (assignmentData) {
                    setDraggedForDatePicker(assignmentData);
                    setShowDragDatePicker(true);
                }
                return;
            }

            // ── Playground card ───────────────────────────────────────────────
            if (active.data.current?.fromPlayground) {
                const quoteId = assignmentData?.quote_info?.quoteId;
                if (over.id === 'unscheduled-zone') {
                    // Drop on waiting zone — just clear the playground flag
                    if (quoteId) handleRemoveFromPlayground(quoteId);
                    setActiveId(null);

                    if (assignmentData) {
                        handleMoveToWaiting(assignmentData);
                    }
                    return;
                }
                // Drop on a day column — clear playground flag, then fall through to schedule it
                if (quoteId) handleRemoveFromPlayground(quoteId);
                // Don't return — fall through to day scheduling below
            }

            if (over.id === 'playground-zone') {
                const quoteId = assignmentData?.quote_info?.quoteId;
                if (quoteId) {
                    const alreadyPinned = playgroundAssignments.some(p => (p.quote_info?.quoteId || p.id) === quoteId);
                    if (!alreadyPinned) {
                        const pinned = { ...assignmentData, quote_info: { ...assignmentData.quote_info, quote_playground: true, delivery_playground: true, expected_delivery_date: null, expected_finish_date: null } };

                        setPlaygroundAssignments(prev => [...prev, pinned]);

                        // Optimistically remove from unscheduled (THIS IS THE FIX)
                        setUnscheduledAssignments(prev =>
                            prev.filter(item => {
                                const itemQuoteId = item.quote_info?.quoteId || item.quote_info?.id;
                                return itemQuoteId !== quoteId;
                            })
                        );

                        // Optimistically remove from schedule (day columns)
                        setScheduleData(prev => {
                            const next = {};
                            for (const dayKey in prev) {
                                next[dayKey] = prev[dayKey].filter(item => {
                                    const itemQuoteId = item.quote_info?.quoteId || item.quote_info?.id;
                                    return itemQuoteId !== quoteId;
                                });
                            }
                            return next;
                        });

                        setOverdueAssignments(prev =>
                            prev.filter(item => (item.quote_info?.quoteId || item.quote_info?.id) !== quoteId)
                        );

                        setDeliveryPlayground(quoteId, true)
                            .then(() => {
                                const displayId = assignmentData?.unique_id || assignmentData?.schedule_row_id || '';
                                createJobActionLog({
                                    job_id: assignmentData?.id,
                                    user_id: userProfile?.id,
                                    user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                                    user_role: userProfile?.role,
                                    action: 'set_playground',
                                    details: `Pinned delivery #${displayId} to playground`,
                                    quote_type: assignmentData?.quote_info?.quote_type || null,
                                    action_origin: 'schedule-delivery',
                                    company_id: company.id
                                }).catch(() => { });
                            })
                            .catch(err => {
                                console.error('Failed to set delivery playground:', err);
                                setPlaygroundAssignments(prev => prev.filter(p => (p.quote_info?.quoteId || p.id) !== quoteId));
                            });
                    }
                }
                setActiveId(null);
                return;
            }

            if (over.id === 'unscheduled-zone') {
                if (assignmentData) {
                    handleMoveToWaiting(assignmentData);
                }
            } else if (over.id.startsWith('day-')) {
                const [, dayKey] = over.id.split('-');
                const dayIndex = days.findIndex(d => d.key === dayKey);

                if (dayIndex !== -1) {
                    const targetDate = getDayDateObject(dayIndex);
                    const newDate = new Date(targetDate);
                    newDate.setUTCHours(10, 0, 0, 0);

                    const isPast = isDateInPast(newDate);

                    setPendingMove({
                        assignment: assignmentData,
                        newDate: newDate,
                        dayKey: dayKey,
                        isPastDate: isPast
                    });

                    setShowConfirmation(true);
                }
            }

            setActiveId(null);
        }
    };

    // Driver panel handlers
    const openPanel = () => {
        setIsPanelOpen(true);
    };

    const closePanel = () => {
        setIsPanelOpen(false);
        setCurrentDropTarget(null);
        setDropTargetRect(null);
    };

    const handleDriverDragStart = (event) => {
        setActiveId(event.active.id);
        setDragHandleType('driver');

        const assignment = event.active.data.current?.assignment;
        if (assignment) {
            setDraggedItemCache(assignment);
            openPanel();
        }
    };

    const handleDriverDragOver = (event) => {
        const { over } = event;

        if (over && over.id.startsWith('driver-')) {
            const driverId = over.id.replace('driver-', '');
            setCurrentDropTarget(driverId);

            // Get the target element's position
            const element = document.getElementById(over.id);
            if (element) {
                const rect = element.getBoundingClientRect();
                setDropTargetRect(rect);
            }
        } else {
            setCurrentDropTarget(null);
            setDropTargetRect(null);
        }
    };

    const handleDriverDragEnd = async (event) => {
        const { active, over } = event;

        setDragOverWeek(null);
        setSwitchProgress(0);
        const assignmentData = draggedItemCache || active.data.current?.assignment;
        setDraggedItemCache(null);


        // If no drop target, just close the panel and exit
        if (!over) {
            setActiveId(null);
            setDragHandleType(null);
            closePanel();
            return;
        }

        if (dragHandleType === 'driver') {
            // Handle remove driver zone
            if (over.id === 'remove-driver-zone') {
                // Use the pre-matched delivery directly — it's already the correct one for this quote
                const delivery = assignmentData.delivery;

                if (delivery && delivery.assigned_driver_id) {
                    // await removeDriver(delivery.id);
                    const promise = removeDriver(delivery.id);

                    Toast.promise(
                        promise,
                        {
                            pending: 'Removing driver...',
                            success: 'Driver removed successfully!',
                            error: 'Failed to remove driver'
                        }
                    );

                }

                closePanel();
            }
            // Handle driver assignment
            else if (over.id.startsWith('driver-')) {
                const driverId = over.id.replace('driver-', '');
                const driver = drivers.find(d => d.id === driverId);

                if (driver && assignmentData) {
                    // IMPORTANT: Pass quote type AND delivery_count to only assign driver to this specific quote and delivery instance
                    const quoteType = assignmentData.quote_info?.quote_type;
                    const deliveryCount = assignmentData.quote_info?.delivery_count ?? 0;
                    const quoteId = assignmentData.quote_info?.quoteId || null;
                    const quoteName =
                        assignmentData.quote_info?.childUniqueId ||
                        assignmentData.unique_id ||
                        null;

                    const promise = assignDriver(assignmentData.id, quoteType, driver.id, driver.full_name || driver.username, deliveryCount, quoteId, quoteName);

                    Toast.promise(
                        promise,
                        {
                            pending: 'Assigning driver...',
                            success: 'Driver assigned successfully!',
                            error: 'Failed to assign driver'
                        }
                    );
                }

                closePanel();
            }
        } else if (dragHandleType === 'date') {
            // Handle date drag normally - existing logic
            if (!over) {
                setActiveId(null);
                setDragHandleType(null);
                return;
            }

            if (over.id.toString().startsWith('week-selector-')) {
                setActiveId(null);
                setDragHandleType(null);
                return;
            }

            if (over.id === 'unscheduled-zone') {
                if (assignmentData) {
                    handleMoveToWaiting(assignmentData);
                }
            } else if (over.id.startsWith('day-')) {
                const [, dayKey] = over.id.split('-');
                const dayIndex = days.findIndex(d => d.key === dayKey);

                if (dayIndex !== -1) {
                    const targetDate = getDayDateObject(dayIndex);
                    const newDate = new Date(targetDate);
                    newDate.setUTCHours(10, 0, 0, 0);

                    const isPast = isDateInPast(newDate);

                    setPendingMove({
                        assignment: assignmentData,
                        newDate: newDate,
                        dayKey: dayKey,
                        isPastDate: isPast
                    });

                    setShowConfirmation(true);
                }
            }
        }

        setActiveId(null);
        setDragHandleType(null);
        closePanel();
    };

    // Drag group date handle to a day column → move all group members to that day
    const handleGroupDateDragEnd = (event) => {
        const { active, over } = event;
        setDragOverWeek(null);
        setSwitchProgress(0);
        const groupMembers = draggedItemCache || active.data.current?.groupMembers;
        const groupId = active.data.current?.groupId;
        setDraggedItemCache(null);
        setActiveId(null);
        setDragHandleType(null);

        if (!over || !groupMembers?.length) return;

        if (over.id === 'date-picker-drop-zone') {
            const groupName = groupMembers[0]?.quote_info?.group_name || 'Group';
            setDraggedGroupForDatePicker({ groupId, groupName, groupMembers });
            setSelectedGroupDateFromPicker(null);
            return;
        }

        if (over.id === 'unscheduled-zone') {
            handleGroupMoveToWaiting(groupId, groupMembers);
            return;
        }

        if (over.id === 'playground-zone') {
            handleGroupMoveToPlayground(groupId, groupMembers);
            return;
        }

        if (over.id.startsWith('day-')) {
            const [, dayKey] = over.id.split('-');
            const dayIndex = days.findIndex(d => d.key === dayKey);
            if (dayIndex !== -1) {
                const targetDate = getDayDateObject(dayIndex);
                handleGroupDateSet(groupId, groupMembers, targetDate);
            }
        }
    };

    // Drag group driver handle to a driver slot → assign that driver to all group members
    const handleGroupDriverDragEnd = async (event) => {
        const { active, over } = event;
        setDragOverWeek(null);
        setSwitchProgress(0);
        const groupMembers = draggedItemCache || active.data.current?.groupMembers;
        const groupId = active.data.current?.groupId;
        setDraggedItemCache(null);
        setActiveId(null);
        setDragHandleType(null);
        closePanel();

        if (!over || !groupMembers?.length) return;

        if (over.id.startsWith('driver-')) {
            const driverId = over.id.replace('driver-', '');
            const driver = drivers.find(d => d.id === driverId);
            if (driver) {
                await handleGroupDriverSet(groupId, groupMembers, driver.id, driver.full_name || driver.username);
            }
        }
    };

    const assignDriver = async (jobId, quoteType, driverId, driverName, deliveryCount, quoteId = null, quoteName = null) => {
        try {
            // Find the assignment (job) to get its delivery records
            const assignment = unscheduledAssignments.find(a => a.id === jobId) ||
                Object.values(scheduleData).flat().find(a => a.id === jobId) ||
                playgroundAssignments.find(a => a.id === jobId) ||
                overdueAssignments.find(a => a.id === jobId);

            if (!assignment) {
                Toast.error(`Assignment not found: ${jobId}`);
                console.error('Assignment not found:', jobId);
                return;
            }

            // Use the passed delivery_count parameter (should come from the dragged card)
            const quoteDeliveryCount = deliveryCount ?? 0;

            // Get delivery record for THIS SPECIFIC QUOTE.
            // New-system: match by child_unique_id (= quote UUID). Old-system: by quote_type + delivery_count.
            let delivery = assignment.job_deliveries?.find(d =>
                quoteId && d.child_unique_id
                    ? d.child_unique_id === quoteId
                    : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
            );

            // If no delivery exists for this quote and count, create it
            if (!delivery) {
                // Verify the quote exists
                const quote = assignment.job_quotes?.find(q =>
                    quoteId ? q.id === quoteId : (q.quote_type === quoteType && q.is_required)
                );
                if (!quote) {
                    console.error('Quote not found or not required:', quoteType);
                    Toast.error(`This quote is not available for delivery`);
                    return;
                }

                // Create delivery record for this specific quote and delivery_count
                const { data, error } = await supabase
                    .from('job_deliveries')
                    .insert({
                        job_id: jobId,
                        quote_type: quoteType,
                        is_required: true,
                        delivery_status: 'not_scheduled',
                        delivery_address: assignment.customer_address,
                        delivery_contact_name: assignment.customer_name,
                        delivery_contact_phone: assignment.customer_number,
                        delivery_count: quoteDeliveryCount,
                        child_unique_id: quoteId || null,  // new-system: display/lookup link to job_quotes UUID
                        child_id: quoteId || null          // new-system: used by unique constraint job_deliveries_unique_child
                    })
                    .select()
                    .single();

                if (error) {
                    console.error('Error creating delivery record:', error);
                    Toast.error(`Error creating delivery record`);
                    throw error;
                }
                delivery = data;
            }

            // Assign driver to ONLY this quote's delivery
            await assignDriverToDelivery(
                delivery.id, // Use job_deliveries.id for this specific quote
                driverId,
                driverName,
                userProfile.id,
                userProfile.full_name || userProfile.username
            );

            let deliveryShortId = uuidToShortId(delivery.id)

            // await sendDriverSMS(assignment, company, driverId, quoteType)

            await createNotificationWithFallback(
                {
                    user_id: driverId,
                    company_id: company.id,
                    type: NOTIFICATION_TYPES.ASSIGNMENT_ASSIGNED,
                    title: 'New Delivery Assignment',
                    message: `${quoteName ? `#${quoteName} - ` : ""}${quoteType?.toUpperCase()} delivery assigned to you!`,
                    priority: NOTIFICATION_PRIORITIES.HIGH,
                    related_type: 'job',
                    related_id: jobId,
                    action_url: `/${companySlug}/assignment/${deliveryShortId}`,
                    metadata: {
                        job_id: jobId,
                        quote_type: quoteType,
                        delivery_count: quoteDeliveryCount,
                        quote_id: quoteId,
                        delivery_id: delivery.id
                    }
                },
                async () => {
                    await sendDriverSMS(assignment, company, driverId, quoteType);
                }
            );


            // Refresh assignments and driver workloads
            await fetchAssignments();
            await fetchDrivers();
            fetchPlaygroundRef.current?.();
            fetchOverdueRef.current?.();
            return true;
        } catch (error) {
            console.error('Error assigning driver:', error);
            Toast.error('Failed to assign driver. Please try again.', {
                title: 'Action Failed'
            });
        }
    };

    const removeDriver = async (deliveryId) => {
        try {
            await removeDriverFromDelivery(deliveryId);

            // Refresh assignments and driver workloads
            await fetchAssignments();
            await fetchDrivers();
            fetchPlaygroundRef.current?.();
            fetchOverdueRef.current?.();
            return true;
        } catch (error) {
            console.error('Error removing driver:', error);
            Toast.error('Failed to remove driver. Please try again.', {
                title: 'Action Failed'
            });
        }
    };

    // Mobile handlers
    const handleMobileMove = (assignment, e) => {
        e.stopPropagation();
        setSelectedAssignment(assignment);
        setShowMoveModal(true);
    };

    const handleMobileMoveConfirm = (newDate) => {
        if (!selectedAssignment || !['admin', 'super_admin'].includes(userProfile?.role)) return;
        const isPast = newDate ? isDateInPast(newDate) : false;
        setPendingMove({ assignment: selectedAssignment, newDate,
            removeDate: !newDate,
            dayKey: newDate?.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
            isPastDate: isPast,  });

        setShowMoveModal(false);
        setShowConfirmation(true);
    };

    // Drag-to-waiting: clear delivery date only
    const handleMoveToWaiting = async (assignment) => {
        const quoteType = assignment.quote_info?.quote_type;
        const deliveryCount = assignment.quote_info?.delivery_count ?? 0;
        const quoteId = assignment.quote_info?.quoteId || null;

        if (!quoteType) return;

        const cardMatches = (item) => quoteId
            ? item.quote_info?.quoteId === quoteId
            : (item.id === assignment.id &&
                item.quote_info?.quote_type === quoteType &&
                (item.quote_info?.delivery_count ?? 0) === deliveryCount);

        // Optimistic: remove from schedule and add to unscheduled immediately
        const waitingCard = {
            ...assignment,
            quote_info: {
                ...assignment.quote_info,
                expected_delivery_date: null,
                expected_finish_date: null,
                waiting_job: true,
            },
        };
        setScheduleData(prev => {
            const newState = {};
            for (const dayKey in prev) {
                newState[dayKey] = prev[dayKey].filter(item => !cardMatches(item));
            }
            return newState;
        });
        setUnscheduledAssignments(prev => {
            if (prev.some(item => cardMatches(item))) return prev;
            return [...prev, waitingCard];
        });
        setOverdueAssignments(prev => prev.filter(item => !cardMatches(item)));

        try {
            const { error } = await updateDeliveryDate(assignment.id, quoteType, null, deliveryCount, quoteId);
            const { waitingError } = await updateWaitingStatus(quoteId, quoteType, assignment.id, deliveryCount);
            if (error) throw error;
            if (waitingError) throw waitingError;
            Toast.success('Delivery date removed');
        } catch (error) {
            console.error('Error removing delivery date:', error);
            Toast.error('Failed to remove delivery date');
            fetchAssignments();
        }
    };

    const handleConfirmMove = async () => {
        if (!pendingMove) return;

        const quoteType = pendingMove.assignment.quote_info?.quote_type;
        const deliveryCount = pendingMove.assignment.quote_info?.delivery_count ?? 0;
        const quoteId = pendingMove.assignment.quote_info?.quoteId || null;

        // Dismiss modal immediately so the UI feels snappy
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);

        if (!quoteType) {
            Toast.error('Failed to update delivery schedule. Please try again.', { title: 'Action Failed' });
            return;
        }

        try {
            const { error } = await updateDeliveryDate(
                pendingMove.assignment.id,
                quoteType,
                pendingMove.removeDate ? null : pendingMove.newDate,
                deliveryCount,
                quoteId
            );

            if (error) throw error;

            // ── Activity log ────────────────────────────────────────────────
            {
                const displayId = pendingMove.assignment?.unique_id || pendingMove.assignment?.schedule_row_id || '';
                const details = pendingMove.removeDate
                    ? `Moved delivery #${displayId} to unscheduled`
                    : `Scheduled delivery #${displayId} to ${pendingMove.dayKey} (${format(pendingMove.newDate, 'd MMM')})`;
                createJobActionLog({
                    job_id: pendingMove.assignment.id,
                    user_id: userProfile?.id,
                    user_name: userProfile?.full_name || userProfile?.username || userProfile?.email || 'Unknown',
                    user_role: userProfile?.role,
                    action: pendingMove.removeDate ? 'unschedule' : 'reschedule',
                    details,
                    quote_type: pendingMove.assignment?.quote_info?.quote_type || null,
                    action_origin: 'schedule-delivery',
                    company_id: company.id
                }).catch(() => { });
            }

            // Optimistic state move — no full refetch, no loading flash
            const newDate = pendingMove.removeDate ? null : pendingMove.newDate;
            const { days: currentDays, weekOffset: wo } = latestRef.current;
            const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Melbourne" }));
            const weekStart = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), wo);

            // Find which day in the current week the new date falls on
            let targetDayKey = null;
            if (newDate) {
                currentDays.forEach((day, dayIndex) => {
                    const dayDate = addDays(weekStart, dayIndex);
                    if (dayDate.getFullYear() === newDate.getUTCFullYear() &&
                        dayDate.getMonth() === newDate.getUTCMonth() &&
                        dayDate.getDate() === newDate.getUTCDate()) {
                        targetDayKey = day.key;
                    }
                });
            }

            const cardMatches = (item) => quoteId
                ? item.quote_info?.quoteId === quoteId
                : (item.id === pendingMove.assignment.id &&
                    item.quote_info?.quote_type === quoteType &&
                    (item.quote_info?.delivery_count ?? 0) === deliveryCount);

            // Remove from unscheduled (waiting) list regardless — card might be coming from there
            let cardFromWaiting = null;
            setUnscheduledAssignments(prev => prev.filter(item => {
                if (cardMatches(item)) { cardFromWaiting = item; return false; }
                return true;
            }));

            // Overdue: keep the card only if the new date is still an overdue day
            setOverdueAssignments(prev => {
                const existing = prev.find(cardMatches);
                const rest = prev.filter(item => !cardMatches(item));
                const newDateIso = newDate ? newDate.toISOString() : null;
                const source = existing || pendingMove.assignment;
                if (!isInOverdueRange(newDateIso) || isCardDelivered(source)) return rest;
                return [...rest, { ...source, quote_info: { ...source.quote_info, expected_finish_date: newDateIso } }];
            });

            // Patch playground in place (card stays in playground, just update the date)
            setPlaygroundAssignments(prev => prev.map(item => {
                if (!cardMatches(item)) return item;
                const newDateIso = newDate ? newDate.toISOString() : null;
                return {
                    ...item,
                    quote_info: { ...item.quote_info, expected_finish_date: newDateIso },
                    job_deliveries: (item.job_deliveries || []).map(d => {
                        const dMatches = quoteId
                            ? d.child_unique_id === quoteId
                            : (d.quote_type === quoteType && (d.delivery_count ?? 0) === deliveryCount);
                        return dMatches
                            ? { ...d, delivery_date: newDate ? newDate.toISOString().slice(0, 10) : null }
                            : d;
                    })
                };
            }));

            setScheduleData(prev => {
                let movedCard = null;
                const newState = {};

                // Remove from any day it currently sits in
                for (const dayKey in prev) {
                    newState[dayKey] = prev[dayKey].filter(item => {
                        if (cardMatches(item)) { movedCard = item; return false; }
                        return true;
                    });
                }

                // If not found in schedule, it came from the waiting section
                const sourceCard = movedCard || cardFromWaiting || pendingMove.assignment;

                if (!targetDayKey) return newState; // date not in the current visible week

                // Patch the date fields on the moved card
                const newDateIso = newDate ? newDate.toISOString() : null;
                const updatedCard = {
                    ...sourceCard,
                    quote_info: {
                        ...sourceCard.quote_info,
                        expected_finish_date: newDateIso,
                    },
                    job_deliveries: (sourceCard.job_deliveries || []).map(d => {
                        const dMatches = quoteId
                            ? d.child_unique_id === quoteId
                            : (d.quote_type === quoteType && (d.delivery_count ?? 0) === deliveryCount);
                        return dMatches
                            ? { ...d, delivery_date: newDate ? newDate.toISOString().slice(0, 10) : null }
                            : d;
                    })
                };

                newState[targetDayKey] = [...(newState[targetDayKey] || []), updatedCard];
                return newState;
            });

        } catch (error) {
            console.error("Failed to update delivery date:", error);
            Toast.error('Failed to update delivery schedule. Please try again.', { title: 'Action Failed' });
            // Revert: full refetch to restore correct state
            fetchAssignments();
        }
    };

    const handleCancelMove = () => {
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);
        setDragOverWeek(null);
        setSwitchProgress(0);
    };

    // Info Tooltip Component
    const InfoTooltip = ({ assignment, quoteType, hasDate, hasDriver, driverName, isDeliveryCompleted, delivery }) => {
        const [isHovered, setIsHovered] = useState(false);
        const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, showAbove: false });
        const [triggerRef, setTriggerRef] = useState(null);

        useEffect(() => {
            if (isHovered && triggerRef) {
                const rect = triggerRef.getBoundingClientRect();
                const tooltipHeight = 280;
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;

                const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight;

                setTooltipPosition({
                    x: rect.left,
                    y: showAbove ? rect.top - 8 : rect.bottom + 8,
                    showAbove
                });
            }
        }, [isHovered, triggerRef]);

        const getStatusDot = (status) => {
            switch (status) {
                case 'completed': return 'bg-green-500';
                case 'delivered': return 'bg-green-500';

                case 'in_progress': return 'bg-yellow-500';
                case 'in_transit': return 'bg-yellow-500';
                case 'in_cutting': return 'bg-yellow-500';
                case 'in_production': return 'bg-yellow-500';

                case 'scheduled': return 'bg-blue-500';
                case 'cutting_ended': return 'bg-blue-500';

                case 'waiting_assignment': return 'bg-red-500';
                case 'waiting_design': return 'bg-red-500';
                case 'not_scheduled': return 'bg-red-500';
                case 'not_started': return 'bg-red-500';

                case 'not_required': return 'bg-gray-400';
                case 'cancelled': return 'bg-gray-600';
                default: return 'bg-gray-400';

            }
        };

        const isFileOnlyQuote =
            quoteType === "beams" ||
            quoteType === "flooring_sheets";

        const TooltipContent = () => (
            <div
                className="fixed z-[9999] w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 pointer-events-none"
                style={{
                    left: `${tooltipPosition.x}px`,
                    top: tooltipPosition.showAbove ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                    transform: `${tooltipPosition.x > window.innerWidth - 320 ? 'translateX(-100%)' : 'translateX(0)'} ${tooltipPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'}`
                }}
            >
                <div
                    className={`absolute w-3 h-3 bg-white border border-gray-200 transform rotate-45 ${tooltipPosition.showAbove ? 'border-t-0 border-l-0' : 'border-b-0 border-r-0'
                        }`}
                    style={{
                        [tooltipPosition.showAbove ? 'bottom' : 'top']: '-6px',
                        left: tooltipPosition.x > window.innerWidth - 320 ? 'calc(100% - 20px)' : '12px'
                    }}
                ></div>

                <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">Job Information</h4>

                    {/* Expected Delivery Date */}
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <FiCalendar className="w-3.5 h-3.5 text-blue-600" />
                            <span className="font-medium">Expected Finish</span>
                        </div>
                        <span className="text-gray-700 font-medium">
                            {hasDate
                                ? new Date(assignment.quote_info.expected_finish_date).toLocaleDateString('en-AU', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric'
                                })
                                : 'Not set'}
                        </span>
                    </div>

                    {/* Design Status */}
                    {!isFileOnlyQuote && (
                        <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                <FiLayers className="w-3.5 h-3.5 text-blue-600" />
                                <span className="font-medium">Design Status</span>
                                <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quote_info?.design_status)}`}></div>
                            </div>
                            <span className="text-gray-700 capitalize">
                                {assignment.quote_info?.design_status?.replace('_', ' ') || 'N/A'}
                            </span>
                        </div>
                    )}

                    {/* Estimation Status */}
                    {!isFileOnlyQuote && (
                        <>
                            {
                                assignment.quote_info?.estimation_status !== "not_required" &&
                                <div className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2">
                                        <FiDollarSign className="w-3.5 h-3.5 text-blue-600" />
                                        <span className="font-medium">Estimation Status</span>
                                        <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quote_info?.estimation_status)}`}></div>
                                    </div>
                                    <span className="text-gray-700 capitalize">
                                        {assignment.quote_info?.estimation_status?.replace('_', ' ') || 'N/A'}
                                    </span>
                                </div>
                            }
                        </>
                    )}

                    {/* Production Status */}
                    {!isFileOnlyQuote && (
                        <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                <FiTool className="w-3.5 h-3.5 text-blue-600" />
                                <span className="font-medium">Production Status</span>
                                <div className={`w-2 h-2 rounded-full ${getStatusDot(assignment.quote_info?.production_status)}`}></div>
                            </div>
                            <span className="text-gray-700 capitalize">
                                {assignment.quote_info?.production_status?.replace('_', ' ') || 'N/A'}
                            </span>
                        </div>
                    )}

                    {/* Delivery Status */}
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <FiTruck className="w-3.5 h-3.5 text-blue-600" />
                            <span className="font-medium">Delivery Status</span>
                            <div className={`w-2 h-2 rounded-full ${getStatusDot(delivery?.delivery_status)}`}></div>
                        </div>
                        <span className="text-gray-700 capitalize">
                            {delivery?.delivery_status?.replace('_', ' ') || 'N/A'}
                        </span>
                    </div>


                    {/* Driver Assignment */}
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                            <FiUsers className="w-3.5 h-3.5 text-blue-600" />
                            <span className="font-medium">Driver</span>
                        </div>
                        <span className="text-gray-700 font-medium">
                            {hasDriver ? driverName : 'Not assigned'}
                        </span>
                    </div>

                    {/* Time Slot */}
                    {assignment.quote_info?.delivery_time_slot && (
                        <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                <FiClock className="w-3.5 h-3.5 text-blue-600" />
                                <span className="font-medium">Time Slot</span>
                            </div>
                            <span className="text-gray-700 font-medium">
                                {assignment.quote_info.delivery_time_slot}
                            </span>
                        </div>
                    )}

                    {/* Delivery Tag Slot */}
                    {assignment.quote_info?.delivery_tag && (
                        <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                <CircleCheck className="w-3.5 h-3.5 text-blue-600" />
                                <span className="font-medium">Delivery Tag</span>
                            </div>
                            <span className="text-gray-700 font-medium capitalize">
                                {assignment.quote_info.delivery_tag}
                            </span>
                        </div>
                    )}

                    {/* Delivery Note */}
                    {assignment.quote_info?.delivery_note && (
                        <div className="text-xs border-t border-gray-100 pt-2 mt-2">
                            <div className="flex items-start gap-2">
                                <FiEdit3 className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <span className="font-medium block mb-1">Delivery Note:</span>
                                    <span className="text-gray-700 text-xs leading-relaxed">
                                        {assignment.quote_info.delivery_note}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delivery Status */}
                    {isDeliveryCompleted && (
                        <div className="border-t border-gray-100 pt-2 mt-2">
                            <div className="flex items-center gap-2 text-xs">
                                <FiCheckCircle className="w-3.5 h-3.5 text-blue-600" />
                                <span className="font-medium text-blue-700">Delivery Completed</span>
                            </div>
                        </div>
                    )}

                    {/* Legend */}
                    {/* <div className="border-t border-gray-100 pt-2 mt-2 flex items-center gap-3 text-[10px] text-gray-500">
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span>Completed</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                            <span>In Progress</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            <span>Waiting</span>
                        </div>
                    </div> */}
                </div>
            </div>
        );

        return (
            <>
                <button
                    ref={setTriggerRef}
                aria-label="Job information"
                    className="p-1.5 rounded-lg bg-white text-gray-600 hover:bg-gray-100 border border-gray-300 transition-all shadow-md"
                    onClick={e => { e.stopPropagation(); if (isMobile) setIsHovered(value => !value); }}
                    onMouseEnter={() => { if (!isMobile) setIsHovered(true); }}
                    onMouseLeave={() => { if (!isMobile) setIsHovered(false); }}
                >
                    <FiInfo className="w-3.5 h-3.5" />
                </button>
                {isHovered && createPortal(<TooltipContent />, document.body)}
            </>
        );
    };

    const CardGeneralStatusStyle = (delivery, quote, type = "bar") => {
        if (!quote) return type === "bar" ? "bg-gray-300" : "text-gray-500";

        const design = quote.design_status;
        const estimation = quote.estimation_status;
        const production = quote.production_status;
        const deliveryStatus = delivery?.delivery_status || "not_scheduled";

        const getColor = (status) => {
            switch (status) {
                case "waiting_assignment":
                case "waiting_design":
                case "not_started":
                case "not_scheduled":
                    return { bar: "bg-gray-300", text: "text-gray-600" };

                case "in_progress":
                case "in_cutting":
                case "in_production":
                case "in_transit":
                case "scheduled":
                    return { bar: "bg-yellow-400", text: "text-yellow-700" };

                case "completed":
                case "delivered":
                    return { bar: "bg-green-500", text: "text-green-700" };

                case "cancelled":
                    return { bar: "bg-red-500", text: "text-red-700" };

                case "not_required":
                    return { bar: "bg-green-200", text: "text-green-600" };

                default:
                    return { bar: "bg-gray-200", text: "text-gray-500" };
            }
        };

        // 1️⃣ DESIGN
        if (design !== "completed" && design !== "not_required" && design !== "waiting_assignment") {
            return getColor(design)[type];
        }

        // 2️⃣ ESTIMATION
        // if (estimation !== "completed" && estimation !== "not_required") {
        //     return getColor(estimation)[type];
        // }

        // 3️⃣ PRODUCTION
        if (production !== "completed" && production !== "not_required") {
            return getColor(production)[type];
        }

        // 4️⃣ DELIVERY
        if (deliveryStatus !== "delivered") {
            return getColor(deliveryStatus)[type];
        }

        // ✅ Everything completed
        return getColor("delivered")[type];
    };


    const formatStatus = (status) => {
        if (!status) return "Pending";

        return status
            .replace(/_/g, " ")
            .replace(/\b\w/g, c => c.toUpperCase());
    };

    // Returns the most up-to-date production status for a card.
    // job_production.phase is the authoritative source (updated by the production schedule).
    // job_quotes.production_status may lag behind, so we check job_production first.
    const getEffectiveProductionStatus = (assignment) => {
        const quoteType = assignment?.quote_info?.quote_type;

        if (quoteType === "beams" || quoteType === "flooring_sheets") {
            return "not_required";
        }

        const prod = assignment?.production;
        if (prod?.is_completed === true || prod?.phase === "completed") return "completed";
        if (prod?.phase) return prod.phase;

        return assignment?.quote_info?.production_status || "not_started";
    };

    // Each card represents a single quote — show only that quote's current status stage.
    const CardGeneralStatusText = (assignment) => {
        const quote = assignment?.quote_info;
        if (!quote) return "No Quote";

        const design = quote.design_status;
        const estimation = quote.estimation_status;
        // Use effective production status — job_production.phase is authoritative
        const productionStatus = getEffectiveProductionStatus(assignment);

        // Match this quote's delivery record from job_deliveries:
        // - New system: delivery.child_unique_id === quote.quoteId (UUID)
        // - Old system: same quote_type + delivery.child_unique_id is null
        const quoteId = quote.quoteId; // job_quotes.id UUID
        const deliveryRecord = (assignment.job_deliveries || []).find(d =>
            quoteId
                ? d.child_unique_id === quoteId
                : (d.quote_type === quote.quote_type && !d.child_unique_id)
        );
        const deliveryStatus = deliveryRecord?.delivery_status ?? 'not_scheduled';

        if (design !== 'completed' && design !== 'not_required' && design !== "waiting_assignment")
            return `Design ${formatStatus(design)}`;

        // if (estimation !== 'completed' && estimation !== 'not_required' &&
        //     assignment.job_type?.toLowerCase()?.includes('estimation'))
        //     return `Estimation ${formatStatus(estimation)}`;

        if (productionStatus !== 'completed' && productionStatus !== 'not_required')
            return `Production ${formatStatus(productionStatus)}`;

        if (deliveryStatus === 'delivered') return 'Completed';

        return `Delivery ${formatStatus(deliveryStatus)}`;
    };



    // Draggable Components
    const DraggableAssignmentCard = ({ assignment, isUnscheduled = false, playgroundMode = false, inGroup = false }) => {
        // Get quote type first - need it for unique IDs
        const quoteType = assignment.quote_info?.quote_type;
        const quoteDeliveryCount = assignment.quote_info?.delivery_count ?? 0;
        const quoteId = assignment.quote_info?.quoteId || null;

        // IMPORTANT: For new-system jobs, use the job_quotes UUID so two children of the
        // same type (e.g. two roof quotes) get distinct draggable IDs. Fall back to the
        // legacy job_id + quote_type + delivery_count composite for old-system jobs.
        const baseCardId = quoteId
            ? `${assignment.id}-${quoteId}`
            : `${assignment.id}-${quoteType}-${quoteDeliveryCount}`;
        const uniqueCardId = playgroundMode ? `playground-${baseCardId}` : baseCardId;

        const notesOpen = openNotesBubble?.quoteId === quoteId;
        const notesBubblePos = notesOpen ? openNotesBubble : { x: 0, y: 0, showAbove: false, flipLeft: false };

        // Date drag handle
        const dateHandle = useDraggable({
            id: `${uniqueCardId}-date`,
            data: { assignment, handleType: 'date', ...(playgroundMode && { fromPlayground: true }) },
            disabled: isMobile || (userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin'),
        });

        // Driver drag handle
        const driverHandle = useDraggable({
            id: `${uniqueCardId}-driver`,
            data: { assignment, handleType: 'driver', ...(playgroundMode && { fromPlayground: true }) },
            disabled: isMobile || (userProfile?.role !== 'admin' && userProfile?.role !== 'super_admin'),
        });

        const isDragging = dateHandle.isDragging || driverHandle.isDragging;

        // Check if assignment has both date and driver
        const hasDate = !!assignment.quote_info?.expected_finish_date;

        // Check if delivery for this quote has an assigned driver.
        // New-system: match by child_unique_id (= job_quotes UUID). Old-system: by quote_type + delivery_count.
        const delivery = assignment.job_deliveries?.find(d =>
            quoteId && d.child_unique_id
                ? d.child_unique_id === quoteId
                : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
        );
        const hasDriver = !!delivery?.assigned_driver_id;
        const driverName = delivery?.assigned_driver_name || 'Set Driver';
        const isDeliveryCompleted = delivery?.delivery_status === 'delivered';

        // Child unique ID for new-system jobs (e.g. "ASDR1", parent is assignment.unique_id)
        const childUniqueId = assignment.quote_info?.childUniqueId || null;

        // Get quote type styling
        const quoteStyle = getQuoteTypeStyle(quoteType);
        const QuoteIcon = quoteStyle.icon;

        // Check if this specific quote's design is completed (or not required)
        const isFileOnlyQuote =
            quoteType === "beams" ||
            quoteType === "flooring_sheets";

        const fileOnlyCompleted =
            assignment?.quote_info?.department_file_status === "completed" ||
            assignment?.quote_info?.departmentFileStatus === "completed" ||
            !!assignment?.quote_info?.department_files_completed_at ||
            !!assignment?.quote_info?.departmentFilesCompletedAt;

        const isDesignCompleted =
            isFileOnlyQuote
                ? fileOnlyCompleted
                : (
                    assignment?.quote_info?.design_status === "completed" ||
                    assignment?.quote_info?.design_status === "not_required"
                );

        const hasAddress =
            assignment?.customer_address &&
            Object.keys(assignment.customer_address).length > 0;

        // Driver can be assigned if: design is completed AND has address
        const canAssignDriver = isDesignCompleted && hasAddress;
        // Use effective production status — job_production.phase takes precedence over job_quotes.production_status
        const effectiveProductionStatus = getEffectiveProductionStatus(assignment);

        const isProductionStarted =
            isFileOnlyQuote ||
            assignment?.job_type === "Timber" ||
            effectiveProductionStatus === "in_cutting" ||
            effectiveProductionStatus === "cutting_ended" ||
            effectiveProductionStatus === "in_production" ||
            effectiveProductionStatus === "completed" ||
            assignment?.production?.is_started_cutting === true ||
            assignment?.production?.is_started_production === true ||
            !!assignment?.production?.start_cutting_at ||
            !!assignment?.production?.start_production_at;

        const isProductionFinished =
            isFileOnlyQuote ||
            assignment?.job_type === "Timber" ||
            effectiveProductionStatus === "completed";

        const isReadyToDeliver = isDesignCompleted && hasAddress && isProductionFinished;

        const notReadyReasons = [
            !isDesignCompleted && (
                isFileOnlyQuote
                    ? `${getQuoteLabel?.(quoteType) || "Files"} waiting`
                    : `Design not completed (${assignment?.quote_info?.design_status?.split("_").join(" ")})`
            ),
            !hasAddress && "Delivery address is not set",
            !isProductionStarted && !isFileOnlyQuote && "Production not started",
            !isProductionFinished && isProductionStarted && !isFileOnlyQuote && "Production started but not finished",
        ].filter(Boolean);


        const deliveryRecord = (assignment.job_deliveries || []).find(d =>
            quoteId
                ? d.child_unique_id === quoteId
                : (d.quote_type === assignment?.quote_info?.quote_type && !d.child_unique_id)
        );
        const deliveryStatus = deliveryRecord?.delivery_status ?? 'not_scheduled';
        const deliveredStatus = deliveryRecord?.delivery_status === "delivered";

        return (
            <div
                className={`relative group flex flex-col bg-white border rounded-lg p-2 shadow-sm hover:shadow-md transition-all h-full overflow-hidden ${deliveredStatus
                    ? 'border-green-300 !bg-green-50'
                    : 'border-gray-200'
                    } ${isDragging ? 'opacity-50' : ''}`}
            >
                {/* Action buttons - appear on hover, absolute positioned */}


                {/* Main content */}
                <div className="w-full">
                    {/* Enrich quote_info with effective production status before passing to style helper */}
                    {(() => {
                        const isFileOnlyQuote =
                            quoteType === "beams" ||
                            quoteType === "flooring_sheets";

                        const effectiveProdStatus = isFileOnlyQuote
                            ? "not_required"
                            : getEffectiveProductionStatus(assignment);

                        const enrichedQuote = assignment.quote_info
                            ? { ...assignment.quote_info, production_status: effectiveProdStatus }
                            : {};
                        return (
                            <div className={`w-full h-[2px] self-center items-center rounded-full mb-2 relative ${CardGeneralStatusStyle(delivery || {}, enrichedQuote, "bar")} ${getMainStatusColor(assignment.main_status)}`}>
                                <div className={`absolute -top-[0.5rem] -left-[0.5rem] text-[11px] font-medium items-center self-center content-center flex-shrink-0 bg-white pr-1 flex flex-row`}>
                                    <span className={`flex flex-row gap-1 px-2 pr-3 py-[1px] rounded-xl text-[11px] font-medium items-center self-center content-center ${quoteStyle.bgColor} ${quoteStyle.textColor} flex-shrink-0 rounded-tl-none rounded-bl-none rounded-tr-none z-20`}>
                                        <QuoteIcon className="w-[11px] h-[11px] self-center" />
                                        {quoteStyle.label}
                                    </span>


                                    {assignment.quote_info?.delivery_count > 0 && (
                                        <span className="inline-flex px-2 py-[1px] self-center content-end justify-end items-center rounded-xl text-[11px] font-medium bg-orange-100 text-orange-800 rounded-bl-none rounded-tr-none rounded-tl-none w-12 -ml-2 z-10 ">
                                            Extra
                                        </span>
                                    )}
                                </div>
                                <span className={`absolute -top-[6.75px] right-0 text-[11px] pl-2 font-semibold ${CardGeneralStatusStyle(delivery || {}, enrichedQuote, "text")} ${deliveredStatus ? "bg-green-50" : "bg-white"}`}>
                                    {CardGeneralStatusText(assignment)}
                                </span>
                            </div>
                        );
                    })()}

                    <div className="flex items-start justify-between mb-1 mt-3 bg-zinc-200 rounded">
                        <div className="flex items-center content-center self-center gap-1.5 min-w-0 flex-1 ">
                            <FiHash className="w-3 h-3 text-gray-600 flex-shrink-0 ml-1" />
                            <TextTooltip text={childUniqueId && `Parent: ${assignment.unique_id}`}>
                                <span
                                    className="font-bold text-gray-900 text-sm truncate cursor-pointer hover:text-teal-600 hover:underline transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // New-system: navigate to the specific department (child) view
                                        if (childUniqueId && assignment.quote_info?.quoteId) {
                                            navigate(`/${companySlug}/assignment/${assignment.id}/${assignment.quote_info.quoteId}`, {
                                                state: { from: location.pathname }
                                            });
                                        } else {
                                            navigate(`/${companySlug}/assignment/${assignment.id}`, {
                                                state: { from: location.pathname }
                                            });
                                        }
                                    }}
                                >
                                    {childUniqueId ? childUniqueId : assignment.unique_id}
                                </span>
                            </TextTooltip>

                            {/* {childUniqueId && (
                                <span className="text-[11px] text-gray-400 font-normal flex-shrink-0">
                                    (PRN: {assignment.unique_id})
                                </span>
                            )} */}
                            {/* 
                            <span className={`inline-flex gap-1 px-2 py-[1px] rounded-full text-[11px] font-medium items-center self-center content-center ${quoteStyle.bgColor} ${quoteStyle.textColor} flex-shrink-0`}>
                                <QuoteIcon className="w-[11px] h-[11px] self-center" />
                                {quoteStyle.label}
                            </span> */}
                            {/* Rescheduled badge - shown if delivery_count > 0 */}
                            {/* {assignment.quote_info?.delivery_count === null && (

                                <span className="inline-flex px-2 py-[1px] self-center content-center items-center rounded-full text-[11px] font-medium bg-orange-100 text-orange-800">
                                    Extra
                                </span>

                            )} */}
                        </div>
                        <div className="flex items-center self-center gap-1">

                            {/* Not ready warning with tooltip */}
                            {!isReadyToDeliver && (
                                <>
                                    {(() => {
                                        let notReadyText = ``
                                        notReadyReasons.forEach((reason, idx) => {
                                            notReadyText += `${reason}\n`
                                        })

                                        return (
                                            <TextTooltip text={notReadyText}>
                                                <IoWarning className="w-2.5 h-2.5 text-red-400 flex-shrink-0" />
                                            </TextTooltip>
                                        )
                                    })()}
                                </>
                            )}

                            {/* Time slot compact indicator */}
                            {assignment.quote_info?.delivery_time_slot && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200 flex-shrink-0 self-center">
                                    <FiClock className="w-2.5 h-2.5" />
                                    {assignment.quote_info.delivery_time_slot}
                                </span>
                            )}

                            {/* {assignment.quote_info?.delivery_tag && (
                                <span className={`inline-flex capitalize items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border  flex-shrink-0 ${assignment?.quote_info?.delivery_tag === "confirmed" ? "bg-green-50 text-green-700 border-green-200" : "bg-orange-50 text-orange-600 border-orange-200"}`}>
                                    {assignment?.quote_info?.delivery_tag === "confirmed" ?
                                        <CircleCheck className="w-2.5 h-2.5" />
                                        :
                                        <Flag className="w-2.5 h-2.5" />
                                    }
                                    {assignment.quote_info.delivery_tag}
                                </span>
                            )} */}


                            {/* {(assignment.urgent_quote || assignment.urgent_design) && (
                                <span className="bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                                    URGENT
                                </span>
                            )} */}

                        </div>
                    </div>

                    <hr className="mb-1" />

                    <div className="delivery-mobile-card-details">
                    {!viewingMode && <MobileJobActions job={assignment} triggerClassName="delivery-mobile-actions-trigger" className="absolute top-1 left-[50%] -translate-x-[50%] flex flex-row gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 bg-white py-0.5 px-0.5 border rounded-lg">
                        {/* Note button */}
                        <TextTooltip text={assignment.quote_info?.delivery_note ? 'Edit note' : 'Add note'}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenNoteModal(assignment, quoteType, assignment.quote_info?.delivery_note || '', quoteDeliveryCount);
                                }}
                                className={`p-1.5 rounded-lg transition-all shadow-md ${assignment.quote_info?.delivery_note
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                    }`}
                            >
                                <FiEdit3 className="w-3.5 h-3.5" />
                            </button>
                        </TextTooltip>

                        {/* Time slot button */}
                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                            <TextTooltip text={assignment.quote_info?.delivery_time_slot ? `Time slot: ${assignment.quote_info.delivery_time_slot}` : 'Set time slot'}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenTimeSlotModal(assignment, quoteType, assignment.quote_info?.delivery_time_slot, quoteDeliveryCount);
                                    }}
                                    className={`p-1.5 rounded-lg transition-all shadow-md ${assignment.quote_info?.delivery_time_slot
                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                        }`}
                                >
                                    <FiClock className="w-3.5 h-3.5" />
                                </button>
                            </TextTooltip>
                        )}

                        {/* Reminder button */}
                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && (
                            <TextTooltip text={assignment.quote_info?.delivery_tag ? `Delivery Tag: ${assignment.quote_info.delivery_tag}` : 'Set delivery tag'}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenDeliveryTagModal(assignment, quoteType, assignment.quote_info?.delivery_tag, quoteDeliveryCount);
                                    }}
                                    className={`p-1.5 rounded-lg transition-all shadow-md ${assignment.quote_info?.delivery_tag
                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                        }`}
                                >
                                    <ClipboardCheck className="w-3.5 h-3.5" />
                                </button>
                            </TextTooltip>
                        )}



                        {/* Create/join group button - admin only, not already in a group */}
                        {hasPermission(userProfile?.role, ["super_admin", "admin"]) && !inGroup && !assignment.quote_info?.group_id && (
                            <TextTooltip text={groupingMode ? `Add to "${groupingMode.groupName}"` : 'Create a group'}>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (groupingMode) {
                                            handleToggleCardInGroup(assignment);
                                        } else {
                                            handleCreateGroup(assignment);
                                        }
                                    }}
                                    className="p-1.5 rounded-lg bg-white text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 border border-gray-300 hover:border-indigo-300 transition-all shadow-md"
                                >
                                    <Layers className="w-3.5 h-3.5" />
                                </button>
                            </TextTooltip>
                        )}

                        {/* Info button with tooltip */}
                        <InfoTooltip aria-label="Job information"
                            assignment={assignment}
                            quoteType={quoteType}
                            hasDate={hasDate}
                            hasDriver={hasDriver}
                            driverName={driverName}
                            isDeliveryCompleted={isDeliveryCompleted}
                            delivery={delivery}
                        />

                        {/* Quick notes button */}
                        {isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                            <button
                                onClick={(e) => handleMobileMove({ ...assignment, mobileCanAssignDriver: canAssignDriver }, e)}
                                type="button" aria-label="Move job" title="Move job" className="p-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-all shadow-md"
                            >
                                <FiMove className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <QuickNotesButton aria-label="Quick notes"
                            assignment={assignment}
                            userProfile={userProfile}
                            isOpen={notesOpen}
                            onToggle={(val, pos) => setOpenNotesBubble(val ? { quoteId, ...pos } : null)}
                            bubblePos={notesBubblePos}
                            setScheduleData={setScheduleData}
                            setUnscheduledAssignments={setUnscheduledAssignments}
                            setPlaygroundAssignments={setPlaygroundAssignments}
                            noteDraftText={quickNoteDrafts[quoteId] || ''}
                            setNoteDraftText={(text) => setQuickNoteDrafts(prev => ({ ...prev, [quoteId]: text }))}
                        />

                    </MobileJobActions>}
                    <div className="flex items-center justify-between flex-row w-full">
                        <div className="flex items-center flex-row gap-1.5">
                            <FiUser className="w-2.5 h-2.5 text-blue-700 flex-shrink-0" />
                            <span className="text-xs text-gray-700 truncate font-medium cursor-default">
                                {assignment.customer_name}
                            </span>

                        </div>
                        {assignment.quote_info?.delivery_tag && (
                            <span className={`inline-flex capitalize items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border  flex-shrink-0 ${assignment?.quote_info?.delivery_tag === "confirmed" ? "bg-green-50 text-green-700 border-green-200" : "bg-orange-50 text-orange-600 border-orange-200"}`}>
                                {assignment?.quote_info?.delivery_tag === "confirmed" ?
                                    <CircleCheck className="w-2.5 h-2.5" />
                                    :
                                    <Flag className="w-2.5 h-2.5" />
                                }
                                {assignment.quote_info.delivery_tag}
                            </span>
                        )}
                    </div>


                    {/* <div className="flex flex-row gap-1">
                        <div className="mb-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                                <FiTruck className="w-3 h-3 mr-1" />
                                {assignment.job_type?.replace('_', ' & ')}
                            </span>
                        </div> */}

                    {/* Rescheduled badge - shown if delivery_count > 0 */}
                    {/* {assignment.quote_info?.delivery_count > 0 && (
                            <div className="mb-3">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    Rescheduled
                                </span>
                            </div>
                        )} */}
                    {/* </div> */}



                    {assignment?.customer_address && (
                        <div className="flex flex-row items-center min-w-0">
                            <p
                                className="text-xs flex items-center gap-1 mt-1 truncate w-full min-w-0"
                            >
                                <MapIcon className="w-3 h-3 mr-1 text-blue-700 shrink-0" />
                                <span className="truncate" title={assignment?.customer_address}>
                                    {assignment?.customer_address}
                                </span>
                            </p>
                        </div>
                    )}

                    {/* Dual handle system for all items (admin only) */}
                </div>

                    <>
                        {/* Always show buttons */}
                        {!inGroup && !viewingMode &&
                            <div className="flex items-center gap-1.5 pt-1.5 border-t border-gray-100 mt-1.5">
                                {/* Date Handle - Always enabled */}
                                <div
                                    ref={dateHandle.setNodeRef}
                                    {...(isMobile ? {} : dateHandle.attributes)}
                                    {...(notesOpen ? {} : dateHandle.listeners)}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs ${hasDate ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                        } cursor-grab active:cursor-grabbing hover:shadow-sm transition-all ${(deliveredStatus && userProfile?.role !== "super_admin") && "!pointer-events-none !select-none"}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <FiCalendar className="w-3 h-3 flex-shrink-0" />
                                    <span className="font-medium truncate">
                                        {hasDate
                                            ? new Date(assignment.quote_info.expected_finish_date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
                                            : 'Set Date'
                                        }
                                    </span>
                                </div>

                                {/* Driver Handle - Disabled when not ready or address not set */}
                                <div
                                    ref={canAssignDriver ? driverHandle.setNodeRef : null}
                                    {...(!isMobile && canAssignDriver ? driverHandle.attributes : {})}
                                    {...(canAssignDriver && !notesOpen ? driverHandle.listeners : {})}
                                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded text-xs ${!canAssignDriver
                                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                                        : hasDriver
                                            ? 'bg-blue-100 text-blue-700 cursor-grab active:cursor-grabbing hover:shadow-sm'
                                            : 'bg-gray-100 text-gray-600 cursor-grab active:cursor-grabbing hover:shadow-sm'
                                        } transition-all ${(deliveredStatus && userProfile?.role !== "super_admin") && "!pointer-events-none !select-none"}`}
                                    onClick={(e) => e.stopPropagation()}
                                    title={
                                        !hasAddress
                                            ? 'Address must be set before assigning driver'
                                            : !isDesignCompleted
                                                ? 'Design must be completed before assigning driver'
                                                : ''
                                    }
                                >
                                    <FiUsers className="w-3 h-3 flex-shrink-0" />
                                    <span className="font-medium truncate">
                                        {driverName}
                                    </span>
                                </div>
                            </div>
                        }

                        {/* Delivery Info Display */}
                        {!inGroup && !viewingMode &&
                            <div className="mt-1 space-y-1">
                                {/* Selected Units */}
                                {assignment.quote_info?.selected_units?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {assignment.quote_info.selected_units.map(unitNum => (
                                            <span
                                                key={unitNum}
                                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-800"
                                            >
                                                Unit {unitNum}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Note - only show if exists */}
                                {assignment.quote_info?.delivery_note && (
                                    <div className="flex items-start gap-1.5 px-1.5 py-1 rounded bg-blue-50 border border-blue-200">
                                        <FiEdit3 className="w-2.5 h-2.5 flex-shrink-0 mt-0.5 text-blue-600" />
                                        <span className="text-[12px] line-clamp-2 text-blue-900">
                                            {assignment.quote_info.delivery_note}
                                        </span>
                                    </div>
                                )}
                            </div>
                        }

                    </>

                </div>
            </div>
        );
    };

    const renderStableAssignmentCard = (assignment, options = {}) => (
        <DeliveryDraggableAssignmentCard
            key={assignment.quote_info?.quoteId || `${assignment.id}-${assignment.quote_info?.quote_type}-${assignment.quote_info?.delivery_count ?? 0}`}
            assignment={assignment}
            isUnscheduled={options.isUnscheduled ?? false}
            playgroundMode={options.playgroundMode ?? false}
            inGroup={options.inGroup ?? false}
            idPrefix={options.idPrefix ?? ''}
            onLocate={options.onLocate ?? null}
            locateLabel={options.locateLabel ?? ''}
            openNotesBubble={openNotesBubble}
            userProfile={userProfile}
            viewingMode={viewingMode}
            groupingMode={groupingMode}
            setOpenNotesBubble={setOpenNotesBubble}
            setScheduleData={setScheduleData}
            setUnscheduledAssignments={setUnscheduledAssignments}
            setPlaygroundAssignments={setPlaygroundAssignments}
            handleOpenNoteModal={handleOpenNoteModal}
            handleOpenTimeSlotModal={handleOpenTimeSlotModal}
            handleOpenDeliveryTagModal={handleOpenDeliveryTagModal}
            handleToggleCardInGroup={handleToggleCardInGroup}
            handleCreateGroup={handleCreateGroup}
            getQuoteTypeStyle={getQuoteTypeStyle}
            getEffectiveProductionStatus={getEffectiveProductionStatus}
            CardGeneralStatusStyle={CardGeneralStatusStyle}
            getMainStatusColor={getMainStatusColor}
            CardGeneralStatusText={CardGeneralStatusText}
            InfoTooltip={InfoTooltip}
            handleMobileMove={handleMobileMove}
            navigate={navigate}
            companySlug={companySlug}
            isMobile={isMobile}
            bypassAssignDriverRequirements={company?.settings?.bypassAssignDriverRequirements === true}
            noteDraftText={quickNoteDrafts[assignment.quote_info?.quoteId || assignment.quote_info?.id] || ''}
            setNoteDraftText={(text) => {
                const qid = assignment.quote_info?.quoteId || assignment.quote_info?.id;
                setQuickNoteDrafts(prev => ({ ...prev, [qid]: text }));
            }}
        />
    );

    const DroppableWaitingJobs = ({ children, className, disabled }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: 'unscheduled-zone',
        });
        return (
            <div ref={setNodeRef} className={`${className} ${isOver && !disabled ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>
                {children}
            </div>
        );
    };

    const DroppablePlayground = ({ children }) => {
        const { isOver, setNodeRef } = useDroppable({ id: 'playground-zone' });
        return (
            <div
                ref={setNodeRef}
                className={`bg-white border-2 border-dashed ${isOver ? 'border-purple-400 bg-purple-50' : 'border-gray-300'} rounded-xl p-4 hover:border-purple-300 transition-colors h-full`}
            >
                {children}
            </div>
        );
    };

    // Droppable zone for driver panel
    const DroppableDriverZone = ({ id, className, children, disabled }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: id,
            disabled: disabled,
        });

        return (
            <div
                id={id}
                ref={setNodeRef}
                className={`${className} ${isOver && !disabled ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
            >
                {children}
            </div>
        );
    };

    // Droppable zone for date picker
    const DroppableDatePickerZone = ({ children }) => {
        const { isOver, setNodeRef } = useDroppable({
            id: 'date-picker-drop-zone',
        });
        return (
            <div ref={setNodeRef} className={`flex-1 text-center min-w-0 transition-all ${isOver ? 'ring-4 ring-teal-400 ring-opacity-50 bg-teal-50 rounded-lg' : ''
                }`}>
                {children}
            </div>
        );
    };

    // Mobile List View
    const MobileListView = () => {
        return (
            <div className="space-y-3">
                {days.map(({ label, key }, dayIndex) => {
                    const dayAssignments = sortByTimeSlot(scheduleData[key] || []);
                    const dayIsToday = isDayToday(dayIndex);
                    const dayIsPast = isDateInPast(getDayDateObject(dayIndex));

                    // Hide day if no assignments match search
                    // if (debouncedSearchQuery.trim() && dayAssignments.length === 0) {
                    //     return null;
                    // }

                    return (
                        <div key={key} className={`bg-white rounded-lg border ${dayIsPast ? 'border-gray-300 bg-gray-50/50' : 'border-gray-200'} shadow-sm`}>
                            <div
                                className={`px-4 py-3 ${dayIsPast ? 'bg-gray-100' : 'bg-gray-50'} rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors`}
                                onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 ${dayIsPast ? 'bg-gray-200' : 'bg-teal-100'} rounded-lg flex items-center justify-center`}>
                                            <span className={`font-bold ${dayIsPast ? 'text-gray-600' : 'text-teal-600'} text-sm`}>{label}</span>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{`${label}, ${getDayDate(dayIndex)}`}</h3>
                                            <p className="text-sm text-gray-500">{dayAssignments.length} delivery{dayAssignments.length !== 1 ? 's' : ''}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {dayIsToday && (
                                            <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                                Today
                                            </span>
                                        )}
                                        {dayIsPast && !dayIsToday && (
                                            <span className="bg-gray-200 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                                                Past
                                            </span>
                                        )}
                                        <FiChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${selectedDay === key ? 'rotate-90' : ''
                                            }`} />
                                    </div>
                                </div>
                            </div>

                            {selectedDay === key && (
                                <div className="p-4 border-t border-gray-100 space-y-3">
                                    {dayAssignments.length > 0 ? dayAssignments.map((assignment) => (
                                        <AssignmentContextMenu
                                            key={assignment.quote_info?.quoteId || assignment.id}
                                            assignment={assignment}
                                            onOpenAssignment={handleOpenAssignment}
                                            onEditAssignment={handleEditAssignment}
                                            // onAssignDesigner={handleAssignDesigner}
                                            onForceFinish={handleForceFinish}
                                            onToggleWaiting={handleToggleWaiting}
                                            onToggleSentProduction={handleToggleSentProduction}
                                            onUndoDeliveryCompletion={handleUndoDeliveryCompletion}
                                            // onDuplicateJob={onDuplicateJob}
                                            // availableDesigners={availableDesigners}
                                            page="delivery"
                                            userRole={userProfile?.role}
                                        >
                                            <div key={(assignment.quote_info?.quoteId || assignment.id) + "-div"}>
                                                {renderStableAssignmentCard(assignment)}
                                            </div>
                                        </AssignmentContextMenu>
                                    )) : (
                                        <div className="text-center py-6">
                                            <FiTruck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                            <p className="text-gray-500">No deliveries scheduled</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // When dragging group handles, draggedItemCache is an array — don't pass that to DragOverlay
    const draggedAssignment = (draggedItemCache && !Array.isArray(draggedItemCache))
        ? draggedItemCache
        : (activeId
            ? [
                // Flatten all days from scheduleData
                ...Object.values(scheduleData || {}).flat(),
                ...unscheduledAssignments
            ].find(a => a.id === activeId)
            : null);

    return (
        <div className="delivery-page contents space-y-8">
            <DndContext
                sensors={isMobile ? [] : sensors}
                collisionDetection={deliveryScheduleCollision}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                measuring={{
                    droppable: {
                        strategy: MeasuringStrategy.WhileDragging,
                    },
                }}
            >
                <PanelRemeasurer />
                <>
                    {/* Header */}
                    <div className="delivery-header flex flex-row w-full justify-between gap-4">
                        <div className="flex flex-col items-start gap-4">
                            <div className="flex flex-row gap-2 items-center">
                                <BackButton
                                    text="Back to Schedule Hub"
                                    variant="ghost"
                                    size="sm"
                                    iconType="chevron"
                                    className="text-blue-600 hover:text-blue-800"
                                />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/${companySlug}/schedule/delivery/finished`, {
                                            state: { from: location.pathname }
                                        });
                                    }}
                                    className="inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 bg-transparent hover:bg-gray-100 text-gray-700 border-transparent px-3 py-1.5 text-sm hover:text-blue-800"
                                >
                                    <Truck className="w-4 h-4 mr-2" />
                                    Finished Deliveries
                                </button>
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                                    <FiTruck className="w-8 h-8 text-teal-600" />
                                    Delivery Schedule
                                </h1>
                                <p className="text-gray-600 mt-1">Manage and track delivery timelines</p>
                            </div>
                        </div>
                        {/* ── Schedule activity log ── */}
                        <ScheduleActivityLog actionOrigin="schedule-delivery" />
                    </div>

                    {/* Grouping mode banner */}
                    {groupingMode && (
                        <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium shadow-lg">
                            <div className="flex items-center gap-2">
                                <Layers className="w-4 h-4" />
                                <span>Grouping: <strong>{groupingMode.groupName}</strong> — click cards to add them</span>
                            </div>
                            <button
                                onClick={handleCommitGrouping}
                                className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition-all"
                            >
                                <FiX className="w-3 h-3" />
                                Done
                            </button>
                        </div>
                    )}

                    {/* Filters - compact one-liner */}
                    <div className="delivery-toolbar flex flex-wrap items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl shadow-sm">
                        {/* Search */}
                        <div className="relative min-w-[160px] max-w-xs flex-1">
                            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search…"
                                className="w-full h-7 pl-7 pr-6 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <FiX className="w-3 h-3" />
                                </button>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="h-5 w-px bg-gray-200 hidden sm:block" />

                        {/* Quote type chips */}
                        {quoteTypeOptions.map(({ key, icon: Icon, label }) => (
                            <button
                                key={key}
                                onClick={() => setQuoteTypeFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                className={`inline-flex items-center gap-1 h-7 px-2 text-[11px] font-medium rounded border transition-all ${quoteTypeFilters[key]
                                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                                    : 'bg-gray-50 text-gray-400 border-gray-200 opacity-60'
                                    }`}
                            >
                                <Icon className="w-3 h-3" />
                                {label}
                            </button>
                        ))}

                        {/* Divider */}
                        <div className="h-5 w-px bg-gray-200 hidden sm:block" />

                        {/* Production filter */}
                        {[
                            { value: 'all', label: 'All Prod.' },
                            { value: 'finished', label: 'Prod. Done', activeClass: 'bg-green-600 text-white border-green-600' },
                            { value: 'not_finished', label: 'Prod. Pending', activeClass: 'bg-orange-500 text-white border-orange-500' },
                        ].map(({ value, label, activeClass }) => (
                            <button
                                key={value}
                                onClick={() => setProductionFilter(value)}
                                className={`h-7 px-2 text-[11px] font-medium rounded border transition-all ${productionFilter === value
                                    ? (activeClass || 'bg-blue-600 text-white border-blue-600')
                                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                                    }`}
                            >
                                {label}
                            </button>
                        ))}

                        {/* Divider */}
                        <div className="h-5 w-px bg-gray-200 hidden sm:block" />

                        {/* Viewing mode toggle */}
                        <button
                            onClick={() => setViewingMode(!viewingMode)}
                            className={`h-7 px-2 text-[11px] font-medium rounded border transition-all ${viewingMode
                                ? 'bg-green-50 text-green-700 border-green-300'
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                                }`}
                            title="Viewing mode hides drag handles"
                        >
                            {viewingMode ? 'View ON' : 'View OFF'}
                        </button>

                        {/* Saturday toggle */}
                        <button
                            onClick={() => setShowSaturday(!showSaturday)}
                            className={`h-7 px-2 text-[11px] font-medium rounded border transition-all ${showSaturday
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                                }`}
                            title="Show/hide Saturday column"
                        >
                            {showSaturday ? 'Sat ON' : 'Sat OFF'}
                        </button>

                        {/* Driver filter (admin only) */}
                        {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && availableDrivers.length > 0 && (
                            <select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                className="h-7 text-[11px] border border-gray-200 rounded-md px-1.5 focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                            >
                                <option value="">All drivers</option>
                                {availableDrivers.map(driver => (
                                    <option key={driver.id} value={driver.id}>
                                        {driver.username || driver.full_name}
                                    </option>
                                ))}
                            </select>
                        )}

                        {/* Driver: show only mine */}
                        {userProfile?.role === 'driver' && (
                            <label className="inline-flex items-center gap-1.5 h-7 px-2 text-[11px] font-medium rounded border border-gray-200 cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={showOnlyMine}
                                    onChange={(e) => setShowOnlyMine(e.target.checked)}
                                    className="h-3 w-3 text-teal-600 rounded"
                                />
                                My deliveries
                            </label>
                        )}


                        <div className="ml-auto flex flex-row gap-2 items-center">
                            {/* Reset */}
                            <button
                                onClick={() => {
                                    setProductionFilter('all');
                                    setQuoteTypeFilters({ roof: true, wall: true, floor: true, timber: true });
                                    setSearchQuery('');
                                }}
                                className="h-7 px-2 text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-transparent transition-all ml-auto"
                            >
                                Reset Filters
                            </button>

                            {/* On-Hold Panel — lives inside DndContext so cards are draggable */}
                            <OnHoldPanel
                                onMobileMove={["admin", "super_admin"].includes(userProfile?.role) ? handleMobileMove : undefined}
                                page="delivery"
                                quoteType="delivery"
                                companyId={company?.id}
                                userId={userProfile?.id}
                                showOnlyMine={showOnlyMine}
                            />
                        </div>
                    </div>

                    {/* Week Navigation */}
                    <div className="delivery-week-nav bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2 !mt-3">
                        <div className="flex items-center justify-between gap-2">
                            {/* Previous Month */}
                            <button
                                onClick={() => setWeekOffset(weekOffset - 4)}
                                className="flex items-center gap-1 px-2 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all group"
                                title="Go back 4 weeks"
                            >
                                <FiChevronsLeft className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                <span className="hidden sm:inline text-xs font-medium">Month</span>
                            </button>

                            {/* Previous Week */}
                            <button
                                onClick={() => setWeekOffset(weekOffset - 1)}
                                className="flex items-center gap-1 px-2 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all group"
                                title="Previous week"
                            >
                                <FiChevronLeft className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                <span className="hidden md:inline text-xs">Week</span>
                            </button>

                            {/* Current Week Display - Droppable Zone */}
                            <DroppableDatePickerZone>
                                <button
                                    onClick={handleOpenDatePicker}
                                    className="w-full hover:bg-gray-50 rounded-md px-2 py-1 transition-colors group"
                                    title="Pick a specific week or drop assignment here"
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <span className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                            {getMonthLabel()}
                                        </span>
                                        <span className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors hidden sm:inline">
                                            {getWeekLabel()}
                                        </span>
                                        {weekOffset === 0 && (
                                            <span className="bg-green-100 text-green-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                This Week
                                            </span>
                                        )}
                                        {weekOffset < 0 && (
                                            <span className="bg-gray-200 text-gray-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                {Math.abs(weekOffset)}w ago
                                            </span>
                                        )}
                                        {weekOffset > 0 && (
                                            <span className="bg-blue-100 text-blue-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                                +{weekOffset}w
                                            </span>
                                        )}
                                        {weekOffset !== 0 && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setWeekOffset(0); }}
                                                className="flex items-center gap-1 px-2 py-0.5 bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-semibold rounded-full transition-colors whitespace-nowrap"
                                                title="Jump to this week"
                                            >
                                                <FiCalendar className="w-3 h-3" />
                                                Today
                                            </button>
                                        )}
                                    </div>
                                </button>
                            </DroppableDatePickerZone>

                            {/* Next Week */}
                            <button
                                onClick={() => setWeekOffset(weekOffset + 1)}
                                className="flex items-center gap-1 px-2 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all group"
                                title="Next week"
                            >
                                <span className="hidden md:inline text-xs">Week</span>
                                <FiChevronRight className="w-4 h-4 group-hover:scale-110 transition-transform" />
                            </button>

                            {/* Next Month */}
                            <button
                                onClick={() => setWeekOffset(weekOffset + 4)}
                                className="flex items-center gap-1 px-2 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all group"
                                title="Go forward 4 weeks"
                            >
                                <span className="hidden sm:inline text-xs font-medium">Month</span>
                                <FiChevronsRight className="w-4 h-4 group-hover:scale-110 transition-transform" />
                            </button>
                        </div>
                    </div>

                    {/* Content - Grid or List View */}
                    {isMobile && mobileViewMode === 'list' ? (
                        <MobileListView />
                    ) : isLoading ? (
                        <div
                            className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 ${showSaturday ? '2xl:grid-cols-6' : '2xl:grid-cols-5'
                                } gap-2 mt-3 items-start`}
                        >
                            {days.map(({ label, key }, dayIndex) => (
                                <DayColumnSkeleton key={key} dayLabel={label} dayDate={getDayDate(dayIndex)} />
                            ))}
                        </div>
                    ) : expandedDay ? (
                        /* Expanded view: Show expanded day full width + collapsed days as small rectangles */
                        <div className="space-y-6 !mt-3">
                            {/* Collapsed days as small cards with animation */}
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 shadow-inner">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                        <FiCalendar className="w-4 h-4 text-teal-600" />
                                        Other Days
                                    </h3>
                                    <span className="text-xs text-gray-500 font-medium">
                                        Click any day to expand
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                    {days.map(({ label, key }, dayIndex) => {
                                        if (key === expandedDay) return null;
                                        // Apply search filter for count
                                        const dayAssignments = sortByTimeSlot(filterAssignments(scheduleData[key] || []));
                                        const completedCount = dayAssignments.filter(a => {
                                            const status = getJobStatusSummary(a, "delivery")?.toLowerCase();
                                            return status === "completed" || status === "delivered";
                                        }).length;


                                        return (
                                            <DeliveryDroppableDay
                                                key={key}
                                                dayKey={key}
                                                dayLabel={label}
                                                dayDate={getDayDate(dayIndex)}
                                                dayIsToday={isDayToday(dayIndex)}
                                                dayIsPast={isDateInPast(getDayDateObject(dayIndex))}
                                                dayHolidays={getHolidaysForDate(getDayDateObject(dayIndex))}
                                                isCollapsed={true}
                                                onToggleExpand={setExpandedDay}
                                                assignmentCount={dayAssignments.length}
                                                completedCount={completedCount}
                                                disabled={isPanelOpen}
                                            >
                                                {/* Children not rendered in collapsed view */}
                                            </DeliveryDroppableDay>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Expanded day - full width with enhanced styling */}
                            {days.map(({ label, key }, dayIndex) => {
                                if (key !== expandedDay) return null;
                                // Apply search filter here
                                const dayAssignments = sortByTimeSlot(filterAssignments(scheduleData[key] || []));
                                const completedCount = dayAssignments.filter(a => {
                                    const status = getJobStatusSummary(a, "delivery")?.toLowerCase();
                                    return status === "completed" || status === "delivered";
                                }).length;


                                return (
                                    <div key={key} className="animate-fadeIn">
                                        <DeliveryDroppableDay
                                            dayKey={key}
                                            dayLabel={label}
                                            dayDate={getDayDate(dayIndex)}
                                            dayIsToday={isDayToday(dayIndex)}
                                            dayIsPast={isDateInPast(getDayDateObject(dayIndex))}
                                            dayHolidays={getHolidaysForDate(getDayDateObject(dayIndex))}
                                            isExpanded={true}
                                            onToggleExpand={setExpandedDay}
                                            assignmentCount={dayAssignments.length}
                                            completedCount={completedCount}
                                            disabled={isPanelOpen}
                                        >
                                            {dayAssignments.length > 0 ? (
                                                renderGroupedSection(dayAssignments)
                                            ) : (
                                                <div className="text-center py-16 w-full bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-dashed border-gray-300">
                                                    <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                                        <FiTruck className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-500 mb-1">No deliveries scheduled</p>
                                                    <p className="text-xs text-gray-400">Drop assignments here to schedule</p>
                                                </div>
                                            )}
                                        </DeliveryDroppableDay>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        /* Normal grid view */
                        <div
                            className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 ${showSaturday ? '2xl:grid-cols-6' : '2xl:grid-cols-5'
                                } gap-2 mt-3 items-start`}
                        >
                            {days.map(({ label, key }, dayIndex) => {
                                // Apply search filter here
                                const dayAssignments = sortByTimeSlot(filterAssignments(scheduleData[key] || []));

                                // Hide day column if no assignments match search and we have a search query
                                // if (debouncedSearchQuery.trim() && dayAssignments.length === 0) {
                                //     return null;
                                // }
                                const completedCount = dayAssignments.filter(a => {
                                    const status = getJobStatusSummary(a, "delivery")?.toLowerCase();
                                    return status === "completed" || status === "delivered";
                                }).length;

                                return (
                                    <DeliveryDroppableDay
                                        key={key}
                                        dayKey={key}
                                        dayLabel={label}
                                        dayDate={getDayDate(dayIndex)}
                                        dayIsToday={isDayToday(dayIndex)}
                                        dayIsPast={isDateInPast(getDayDateObject(dayIndex))}
                                        dayHolidays={getHolidaysForDate(getDayDateObject(dayIndex))}
                                        onToggleExpand={setExpandedDay}
                                        assignmentCount={dayAssignments.length}
                                        completedCount={completedCount}
                                        disabled={isPanelOpen}
                                    >
                                        {dayAssignments.length > 0 ? (
                                            renderGroupedSection(dayAssignments)
                                        ) : (
                                            <div className="text-center py-8">
                                                <FiTruck className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                                                <p className="text-xs text-gray-500">No deliveries</p>
                                            </div>
                                        )}
                                    </DeliveryDroppableDay>
                                );
                            })}
                        </div>
                    )}

                    {/* Overdue Deliveries — recent past, scheduled but not delivered */}
                    {(() => {
                        const rangeOption = OVERDUE_RANGE_OPTIONS.find(o => o.key === overdueRange) || OVERDUE_RANGE_OPTIONS[0];
                        const rangeStart = getOverdueRange(rangeOption.key).start;
                        const sortOption = OVERDUE_SORT_OPTIONS.find(o => o.key === overdueSort) || OVERDUE_SORT_OPTIONS[0];

                        const inRange = overdueAssignments.filter(a =>
                            (a.quote_info?.expected_finish_date || '').slice(0, 10) >= rangeStart
                        );
                        // Driver options come from the jobs in range (with counts). A remembered
                        // driver with nothing in range stays selectable so the choice isn't lost.
                        const driverCounts = new Map();
                        let unassignedCount = 0;
                        inRange.forEach(a => {
                            const delivery = findCardDelivery(a);
                            if (!delivery?.assigned_driver_id) { unassignedCount++; return; }
                            const entry = driverCounts.get(delivery.assigned_driver_id)
                                || { name: delivery.assigned_driver_name || 'Unknown driver', count: 0 };
                            entry.count++;
                            driverCounts.set(delivery.assigned_driver_id, entry);
                        });
                        if (overdueDriver !== 'all' && overdueDriver !== 'none' && !driverCounts.has(overdueDriver)) {
                            const known = drivers.find(d => d.id === overdueDriver);
                            driverCounts.set(overdueDriver, { name: known?.full_name || known?.username || 'Selected driver', count: 0 });
                        }
                        const driverOptions = [...driverCounts.entries()]
                            .map(([id, { name, count }]) => ({ id, name, count }))
                            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

                        const matchesDriver = (a) => {
                            if (overdueDriver === 'all') return true;
                            const driverId = findCardDelivery(a)?.assigned_driver_id;
                            return overdueDriver === 'none' ? !driverId : driverId === overdueDriver;
                        };

                        // Group members share a driver, so filtering per card keeps groups whole
                        const { groups, ungrouped } = splitByGroup(filterAssignments(inRange.filter(matchesDriver)));

                        // Groups and single jobs sort together; a group sorts by its first job
                        const units = [
                            ...(showOverdueGroups
                                ? [...groups.entries()].map(([gid, { name, items }]) => ({ gid, name, items, rep: items[0] }))
                                : []),
                            ...ungrouped.map(a => ({ rep: a })),
                        ].sort((x, y) => sortOption.compare(x.rep, y.rep));

                        const visibleCount = units.reduce((n, u) => n + (u.items?.length || 1), 0);
                        const hasGroupsInRange = inRange.some(a => a.quote_info?.group_id);

                        const today = startOfDay(getAustralianDate());
                        const renderOverdueDate = (a) => {
                            const dateStr = a.quote_info?.expected_finish_date?.slice(0, 10);
                            if (!dateStr) return null;
                            const [y, m, d] = dateStr.split('-').map(Number);
                            const date = new Date(y, m - 1, d);
                            const daysAgo = Math.round((today - date) / 86400000);
                            return (
                                <div className="flex items-center justify-between px-1 mb-1 text-[11px] font-medium">
                                    <span className="text-red-600">{format(date, 'EEE d MMM')}</span>
                                    <span className="text-gray-400">{daysAgo === 1 ? 'yesterday' : `${daysAgo} days ago`}</span>
                                </div>
                            );
                        };

                        // Groups first: same sort within each part, groups above a divider, singles below
                        const groupsFirstActive = overdueGroupsFirst && showOverdueGroups;
                        const groupUnits = units.filter(u => u.gid);
                        const singleUnits = units.filter(u => !u.gid);
                        const groupJobCount = groupUnits.reduce((n, u) => n + u.items.length, 0);

                        const renderUnitGrid = (list) => list.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                                {list.map(u => u.gid ? (
                                    // Members share a date and driver, so the date line sits on the group
                                    <div key={u.gid} className="h-full flex flex-col">
                                        {renderOverdueDate(u.rep)}
                                        <div className="flex-1 min-h-0">
                                            {renderGroupContainer(u.gid, u.name, u.items, {
                                                idPrefix: 'overdue-',
                                                compact: true,
                                                onLocate: handleLocateOverdue,
                                                locateLabel: getLocateLabel(u.rep),
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    renderUngroupedCard(u.rep, {
                                        idPrefix: 'overdue-',
                                        header: renderOverdueDate(u.rep),
                                        fill: true,
                                        onLocate: handleLocateOverdue,
                                        locateLabel: getLocateLabel(u.rep),
                                    })
                                ))}
                            </div>
                        );

                        const emptyMessage = inRange.length === 0
                            ? `Nothing overdue in the last ${rangeOption.label} — every delivery is complete`
                            : debouncedSearchQuery.trim()
                                ? 'No matching overdue deliveries'
                                : overdueDriver !== 'all' && !inRange.some(matchesDriver)
                                    ? overdueDriver === 'none'
                                        ? 'Every overdue job has a driver'
                                        : 'No overdue jobs for this driver'
                                    : 'Only grouped jobs are overdue — show groups to see them';

                        const selectClass = "text-xs text-gray-700 bg-white border border-gray-300 rounded-md py-1 pl-2 pr-7 focus:outline-none focus:ring-2 focus:ring-red-300 cursor-pointer";

                        return (
                            <OverdueDropGuard className="bg-white border border-red-200 rounded-xl" disabled={isPanelOpen}>
                                <div className="flex flex-wrap items-center gap-2 pr-2 hover:bg-red-50/50 rounded-xl transition-colors">
                                    <button
                                        type="button"
                                        onClick={() => setIsOverdueOpen(open => !open)}
                                        aria-expanded={isOverdueOpen}
                                        className="flex-1 min-w-0 flex items-center gap-3 pl-4 py-3 text-left"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <IoWarning className="w-5 h-5 text-red-500 flex-shrink-0" />
                                            <h3 className="text-base font-semibold text-gray-900 whitespace-nowrap">Overdue Deliveries</h3>
                                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${inRange.length > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
                                                {visibleCount !== inRange.length ? `${visibleCount}/${inRange.length}` : inRange.length}
                                            </span>
                                            <span className="hidden md:inline text-xs text-gray-400 truncate">
                                                Scheduled in the last {rangeOption.label}, not yet delivered
                                            </span>
                                        </div>
                                    </button>
                                    {isOverdueOpen && (
                                        <div className="flex flex-wrap items-center gap-2 pl-4 sm:pl-0 pb-2 sm:pb-0">
                                            <select
                                                value={overdueRange}
                                                onChange={(e) => setOverdueRange(e.target.value)}
                                                aria-label="Overdue time range"
                                                className={selectClass}
                                            >
                                                {OVERDUE_RANGE_OPTIONS.map(o => (
                                                    <option key={o.key} value={o.key}>Last {o.label}</option>
                                                ))}
                                            </select>
                                            <select
                                                value={overdueDriver}
                                                onChange={(e) => setOverdueDriver(e.target.value)}
                                                aria-label="Filter overdue deliveries by driver"
                                                className={`${selectClass} max-w-[11rem] ${overdueDriver !== 'all' ? '!border-red-300 !bg-red-50' : ''}`}
                                            >
                                                <option value="all">All drivers</option>
                                                <option value="none">No driver ({unassignedCount})</option>
                                                {driverOptions.map(d => (
                                                    <option key={d.id} value={d.id}>{d.name} ({d.count})</option>
                                                ))}
                                            </select>
                                            <select
                                                value={overdueSort}
                                                onChange={(e) => setOverdueSort(e.target.value)}
                                                aria-label="Sort overdue deliveries"
                                                className={selectClass}
                                            >
                                                {OVERDUE_SORT_OPTIONS.map(o => (
                                                    <option key={o.key} value={o.key}>{o.label}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => setShowOverdueGroups(show => !show)}
                                                aria-pressed={showOverdueGroups}
                                                disabled={!hasGroupsInRange}
                                                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${showOverdueGroups
                                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <Layers className="w-3.5 h-3.5" />
                                                {showOverdueGroups ? 'Hide groups' : 'Show groups'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setOverdueGroupsFirst(first => !first)}
                                                aria-pressed={overdueGroupsFirst}
                                                disabled={!hasGroupsInRange || !showOverdueGroups}
                                                title="Show groups before single jobs"
                                                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${groupsFirstActive
                                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <FiChevronsLeft className="w-3.5 h-3.5 rotate-90" />
                                                Groups first
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setIsOverdueOpen(open => !open)}
                                        aria-label={isOverdueOpen ? 'Collapse overdue deliveries' : 'Expand overdue deliveries'}
                                        className="p-1.5 rounded-md"
                                    >
                                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOverdueOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>

                                {isOverdueOpen && (
                                    <div className="px-4 pb-4">
                                        {units.length === 0 ? (
                                            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                                <FiCheckCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                                <p className="text-gray-500 font-medium text-sm">{emptyMessage}</p>
                                            </div>
                                        ) : (
                                            groupsFirstActive ? (
                                                <>
                                                    {renderUnitGrid(groupUnits)}
                                                    {groupUnits.length > 0 && singleUnits.length > 0 && (
                                                        <div className="flex items-center gap-3 my-4" role="separator">
                                                            <hr className="flex-1 border-gray-200" />
                                                            <span className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 whitespace-nowrap">
                                                                <Layers className="w-3 h-3 text-indigo-400" />
                                                                {groupUnits.length} {groupUnits.length === 1 ? 'group' : 'groups'} ({groupJobCount} {groupJobCount === 1 ? 'job' : 'jobs'}) above
                                                                <span className="text-gray-300">·</span>
                                                                {singleUnits.length} single {singleUnits.length === 1 ? 'job' : 'jobs'} below
                                                            </span>
                                                            <hr className="flex-1 border-gray-200" />
                                                        </div>
                                                    )}
                                                    {renderUnitGrid(singleUnits)}
                                                </>
                                            ) : (
                                                renderUnitGrid(units)
                                            )
                                        )}
                                    </div>
                                )}
                            </OverdueDropGuard>
                        );
                    })()}

                    {/* Waiting Jobs + Playground Section */}
                    <div className="flex flex-col lg:flex-row gap-4 items-stretch">
                        {/* Left: Waiting Jobs */}
                        <div className="w-full lg:w-1/2">
                            <DroppableWaitingJobs
                                id="unscheduled-zone"
                                className={`bg-white border-2 border-dashed ${isUnscheduledOver ? 'border-orange-300 bg-orange-50' : 'border-gray-300'} rounded-xl p-4 hover:border-orange-300 transition-colors h-full`}
                                disabled={false}
                            >
                                {(() => {
                                    const allFiltered = sortByTimeSlot(filterAssignments(unscheduledAssignments));
                                    const visibleUnscheduled = allFiltered.slice(0, unscheduledPage * UNSCHEDULED_PAGE_SIZE);
                                    const hasMore = visibleUnscheduled.length < allFiltered.length;
                                    return (
                                        <>
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-base font-semibold text-gray-900">Waiting Jobs</h3>
                                                <div className="flex items-center gap-2">
                                                    <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-1 rounded-full">
                                                        {visibleUnscheduled.length}/{allFiltered.length}
                                                        {debouncedSearchQuery.trim() && <span className="text-orange-600 ml-1">(filtered)</span>}
                                                    </span>
                                                    {!isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                                                        <span className="text-xs text-gray-400">Drop here to unschedule</span>
                                                    )}
                                                </div>
                                            </div>
                                            {groupingMode && (
                                                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700 font-medium">
                                                    <Layers className="w-3.5 h-3.5 flex-shrink-0" />
                                                    <span>Click any card to add to <strong>{groupingMode.groupName}</strong></span>
                                                </div>
                                            )}

                                            {allFiltered.length === 0 ? (
                                                <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                                    <FiPackage className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                                    <p className="text-gray-500 font-medium text-sm">
                                                        {debouncedSearchQuery.trim() ? 'No matching unscheduled deliveries' : 'All deliveries have been scheduled'}
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div
                                                        ref={unscheduledContainerRef}
                                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto pr-1"
                                                        onScroll={handleUnscheduledScroll}
                                                    >
                                                        {renderGroupedSection(visibleUnscheduled)}
                                                    </div>
                                                    {hasMore && (
                                                        <div className="text-center pt-3">
                                                            <button
                                                                onClick={() => setUnscheduledPage(prev => prev + 1)}
                                                                className="text-xs text-gray-500 hover:text-teal-600 px-3 py-1.5 rounded border border-gray-200 hover:border-teal-300 transition-all"
                                                            >
                                                                Load more ({allFiltered.length - visibleUnscheduled.length} remaining)
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </DroppableWaitingJobs>
                        </div>{/* end waiting jobs wrapper */}

                        {/* Right: Playground */}
                        <div className="w-full lg:w-1/2">
                            <DroppablePlayground>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-base font-semibold text-gray-900">Playground</h3>
                                    <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-1 rounded-full">
                                        {playgroundAssignments.length} pinned
                                    </span>
                                </div>
                                {groupingMode && (
                                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700 font-medium">
                                        <Layers className="w-3.5 h-3.5 flex-shrink-0" />
                                        <span>Click any card to add to <strong>{groupingMode.groupName}</strong></span>
                                    </div>
                                )}
                                {playgroundAssignments.length === 0 ? (
                                    <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                        <FiPackage className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                                        <p className="text-gray-500 font-medium text-sm">Drop deliveries here</p>
                                        <p className="text-xs text-gray-400 mt-1">Drag any card into this area to pin it</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto pr-1">
                                        {renderPlaygroundSection(playgroundAssignments)}
                                    </div>
                                )}
                            </DroppablePlayground>
                        </div>
                    </div>{/* end flex row */}
                </>

                {showMoveModal && <MobileScheduleMoveDialog companyState={company?.state} loadCounts={async month => {
                        const start = new Date(month.getFullYear(), month.getMonth(), 1);
                        start.setDate(start.getDate() - (start.getDay() + 6) % 7);
                        const end = new Date(start); end.setDate(end.getDate() + 41);
                        const result = await getDeliveriesInRange(company.id, calendarDateKey(start), calendarDateKey(end), {
                            showOnlyMine: showOnlyMine ? userProfile.id : null, userId: selectedUser || null,
                            productionFilter, quoteTypeFilters,
                        });
                        if (result.error) throw result.error;
                        const counts = {};
                        for (let i = 0; i < 42; i++) { const day = new Date(start); day.setDate(day.getDate() + i); counts[calendarDateKey(day)] = 0; }
                        (result.data || []).forEach(card => { const key = card.quote_info?.expected_finish_date?.slice(0, 10); if (key in counts) counts[key]++; });
                        return counts;
                    }}
                    title="Move delivery"
                    allowUnscheduled={!!(selectedAssignment?.quote_info?.expected_finish_date)}
                    drivers={drivers}
                    onAssignDriver={selectedAssignment?.mobileCanAssignDriver ? async driverId => {
                        if (!['admin', 'super_admin'].includes(userProfile?.role)) return;
                        const driver = drivers.find(item => item.id === driverId);
                        if (!driver) return;
                        const info = selectedAssignment.quote_info;
                        const assigned = await assignDriver(selectedAssignment.id, info.quote_type, driver.id,
                            driver.full_name || driver.username, info.delivery_count ?? 0,
                            info.quoteId || null, info.childUniqueId || selectedAssignment.unique_id || null);
                        if (!assigned) throw new Error("Driver assignment failed");
                        setShowMoveModal(false);
                        Toast.success('Driver assigned successfully');
                    } : undefined}
                    jobLabel={selectedAssignment?.schedule_row_id || selectedAssignment?.unique_id}
                    onClose={() => setShowMoveModal(false)} onSelect={handleMobileMoveConfirm}
                />}

                <ConfirmationPopup
                    isOpen={showConfirmation}
                    title="Confirm Delivery Schedule Change"
                    message={pendingMove
                        ? `Are you sure you want to reschedule delivery for "${pendingMove.assignment.unique_id}" to ${pendingMove?.newDate ? pendingMove.newDate.toLocaleDateString('en-AU') : "unscheduled"
                        }?`
                        : ""
                    }
                    confirmText="Reschedule Delivery"
                    cancelText="Cancel"
                    onConfirm={handleConfirmMove}
                    onCancel={handleCancelMove}
                />

                {/* Undo Completion Confirmation */}
                <ConfirmationPopup
                    isOpen={!!pendingUndoCompletion}
                    title="Undo Delivery Completion"
                    message={pendingUndoCompletion
                        ? `Are you sure you want to mark "${pendingUndoCompletion.assignment?.quote_info?.childUniqueId || pendingUndoCompletion.assignment?.unique_id}" (${pendingUndoCompletion.assignment?.quote_info?.quote_type}) as not completed? The delivery will go back to ${pendingUndoCompletion.deliveryRecord?.assigned_driver_id ? 'scheduled' : 'not scheduled'}.`
                        : ""
                    }
                    confirmText="Undo Completion"
                    cancelText="Cancel"
                    type="danger"
                    loading={isUndoingCompletion}
                    onConfirm={handleConfirmUndoCompletion}
                    onCancel={() => { if (!isUndoingCompletion) setPendingUndoCompletion(null); }}
                />

                {/* Date Picker Modal */}
                {showDatePicker && (
                    <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-teal-100">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Select Week</h3>
                                    <p className="text-sm text-gray-600 mt-1">Choose any date to jump to that week</p>
                                </div>
                                <button
                                    onClick={() => setShowDatePicker(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Date Picker */}
                            <div className="p-6 flex justify-center">
                                <DatePicker
                                    selected={datePickerValue}
                                    onChange={handleDatePickerSelect}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    calendarStartDay={1}
                                    dateFormat="dd/MM/yyyy"
                                    highlightDates={[getAustralianDate()]}
                                    dayClassName={getDayClassName}
                                    renderDayContents={renderDayContents}
                                    filterDate={isDateDisabled}
                                    className="date-picker-custom"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                                <button
                                    onClick={() => setShowDatePicker(false)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                {weekOffset !== 0 && (
                                    <button
                                        onClick={() => {
                                            setWeekOffset(0);
                                            setShowDatePicker(false);
                                        }}
                                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
                                    >
                                        <FiCalendar className="w-4 h-4" />
                                        Jump to This Week
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Drag Date Picker Modal */}
                {showDragDatePicker && draggedForDatePicker && (
                    <div
                        className="mobile-dialog-shell fixed inset-0 flex items-center justify-center z-[60] p-4"
                        style={{ pointerEvents: 'auto' }}
                    >
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={() => {
                                setShowDragDatePicker(false);
                                setDraggedForDatePicker(null);
                                setSelectedDateFromPicker(null);
                                setActiveId(null);
                            }}
                        />

                        {/* Modal Content */}
                        <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                                <div className="flex-1 min-w-0 mr-4">
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">Schedule Delivery</h3>
                                    <p className="text-sm text-gray-600 truncate">
                                        Dragging: <span className="font-semibold">{draggedForDatePicker.unique_id}</span>
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowDragDatePicker(false);
                                        setDraggedForDatePicker(null);
                                        setSelectedDateFromPicker(null);
                                        setActiveId(null);
                                    }}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors flex-shrink-0"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Date Picker */}
                            <div className="p-6 flex justify-center bg-gray-50">
                                <DatePicker
                                    onChange={handleDragDatePickerSelect}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    calendarStartDay={1}
                                    dateFormat="dd/MM/yyyy"
                                    dayClassName={getDragDatePickerDayClass}
                                    renderDayContents={renderDayContents}
                                    filterDate={isDateDisabled}
                                    className="date-picker-custom"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white gap-3">
                                <button
                                    onClick={() => {
                                        setShowDragDatePicker(false);
                                        setDraggedForDatePicker(null);
                                        setSelectedDateFromPicker(null);
                                        setActiveId(null);
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDropOnDatePickerDay}
                                    disabled={!selectedDateFromPicker}
                                    className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${selectedDateFromPicker
                                        ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 shadow-lg'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    <FiCalendar className="w-4 h-4" />
                                    {selectedDateFromPicker
                                        ? `Schedule for ${selectedDateFromPicker.toLocaleDateString('en-AU')}`
                                        : 'Select a Date'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Group Drag-to-Date-Picker Modal */}
                {draggedGroupForDatePicker && (
                    <div
                        className="mobile-dialog-shell fixed inset-0 flex items-center justify-center z-[60] p-4"
                        style={{ pointerEvents: 'auto' }}
                    >
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50"
                            onClick={() => {
                                setDraggedGroupForDatePicker(null);
                                setSelectedGroupDateFromPicker(null);
                            }}
                        />

                        {/* Modal Content */}
                        <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full">
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-indigo-100">
                                <div className="flex-1 min-w-0 mr-4">
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">Schedule Group</h3>
                                    <p className="text-sm text-gray-600 truncate">
                                        <span className="font-semibold">{draggedGroupForDatePicker.groupName}</span>
                                        <span className="text-gray-400 ml-1">· {draggedGroupForDatePicker.groupMembers.length} {draggedGroupForDatePicker.groupMembers.length === 1 ? 'job' : 'jobs'}</span>
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setDraggedGroupForDatePicker(null);
                                        setSelectedGroupDateFromPicker(null);
                                    }}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors flex-shrink-0"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Date Picker */}
                            <div className="p-6 flex justify-center bg-gray-50">
                                <DatePicker
                                    onChange={handleGroupDragDatePickerSelect}
                                    inline
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    calendarStartDay={1}
                                    dateFormat="dd/MM/yyyy"
                                    dayClassName={getDragDatePickerDayClass}
                                    renderDayContents={renderDayContents}
                                    filterDate={isDateDisabled}
                                    className="date-picker-custom"
                                />
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white gap-3">
                                <button
                                    onClick={() => {
                                        setDraggedGroupForDatePicker(null);
                                        setSelectedGroupDateFromPicker(null);
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleGroupDropOnDatePickerDay}
                                    disabled={!selectedGroupDateFromPicker}
                                    className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${selectedGroupDateFromPicker
                                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800 shadow-lg'
                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    <FiCalendar className="w-4 h-4" />
                                    {selectedGroupDateFromPicker
                                        ? `Schedule for ${selectedGroupDateFromPicker.toLocaleDateString('en-AU')}`
                                        : 'Select a Date'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Driver Assignment Panel */}
                <div className={`fixed inset-0 z-50 ${isPanelOpen ? '' : 'pointer-events-none'}`}>
                    {/* Backdrop */}
                    {isPanelOpen && (
                        <div
                            onClick={closePanel}
                            className="fixed inset-0 bg-black/40 transition-opacity duration-300 backdrop-blur-sm"
                        />
                    )}

                    <div
                        ref={panelRef}
                        className="fixed left-0 right-0 h-[85vh] max-h-[85vh] transition-all duration-500 ease-out pointer-events-auto"
                        style={{
                            bottom: isPanelOpen ? 0 : '-85vh'
                        }}
                    >
                        <div className="bg-white rounded-t-3xl shadow-2xl h-full flex flex-col relative overflow-hidden pointer-events-auto">
                            {/* Header */}
                            <div className={`relative px-6 py-4 transition-colors duration-300 ${currentDropTarget?.startsWith('driver-')
                                ? 'bg-gradient-to-br from-green-900 via-emerald-800 to-green-900'
                                : 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900'
                                }`}>
                                {/* Background Pattern */}
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute inset-0" style={{
                                        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                                        backgroundSize: '32px 32px'
                                    }}></div>
                                </div>

                                <div className="max-w-7xl mx-auto relative z-10">
                                    <div className="flex items-center justify-between gap-6">
                                        {/* Title */}
                                        <div className="flex-1">
                                            <h3 className="text-2xl font-bold text-white mb-1">
                                                Assign Driver
                                            </h3>
                                            <p className="text-white/70 text-sm">
                                                Select a driver for this delivery
                                            </p>
                                        </div>

                                        {/* Assignment Info */}
                                        {draggedItemCache && (() => {
                                            const cacheQuoteStyle = getQuoteTypeStyle(draggedItemCache.quote_info?.quote_type);
                                            const CacheQuoteIcon = cacheQuoteStyle.icon;
                                            return (
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-white rounded-xl shadow-lg px-4 py-2.5 border border-gray-100 min-w-[250px]">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <FiHash className="w-4 h-4 text-gray-400" />
                                                            <span className="font-bold text-sm text-gray-900">
                                                                {draggedItemCache.unique_id}
                                                            </span>
                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cacheQuoteStyle.bgColor} ${cacheQuoteStyle.textColor}`}>
                                                                <CacheQuoteIcon className="w-3 h-3" />
                                                                {cacheQuoteStyle.label}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <FiUser className="w-3 h-3 text-gray-400" />
                                                            <span className="text-xs text-gray-600 truncate">
                                                                {draggedItemCache.customer_name}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Close Button */}
                                                    <button
                                                        onClick={closePanel}
                                                        className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/10"
                                                    >
                                                        <FiX className="w-5 h-5 text-white" />
                                                    </button>
                                                </div>
                                            );
                                        })()}

                                        {/* Close Button when no assignment */}
                                        {!draggedItemCache && (
                                            <button
                                                onClick={closePanel}
                                                className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/10"
                                            >
                                                <FiX className="w-5 h-5 text-white" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Remove Driver Zone */}
                            {draggedItemCache && draggedItemCache.delivery?.assigned_driver_id && (
                                <div className="px-8 pt-6 pb-4 border-b border-gray-200 bg-gray-50">
                                    <div className="max-w-7xl mx-auto">
                                        <DroppableDriverZone
                                            id="remove-driver-zone"
                                            className="border-2 border-dashed border-red-300 bg-white rounded-lg p-4 text-center hover:bg-red-50 hover:border-red-400 transition-all group cursor-pointer"
                                            disabled={false}
                                        >
                                            <div className="flex items-center justify-center gap-3">
                                                <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                                                    <FiX className="w-5 h-5 text-red-600" />
                                                </div>
                                                <div className="text-left">
                                                    <p className="text-sm font-semibold text-red-900">Remove Driver</p>
                                                    <p className="text-xs text-red-600">Unassign driver from this delivery</p>
                                                </div>
                                            </div>
                                        </DroppableDriverZone>
                                    </div>
                                </div>
                            )}

                            {/* Driver List */}
                            <div className="flex-1 overflow-y-auto p-8">
                                <div className="max-w-7xl mx-auto">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                <FiUsers className="w-5 h-5 text-gray-600" />
                                                Available Drivers
                                            </h4>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Workload shown is for {new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                                            </p>
                                        </div>
                                        <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                            {drivers.length} {drivers.length === 1 ? 'driver' : 'drivers'}
                                        </span>
                                    </div>

                                    {drivers.length === 0 ? (
                                        <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                                            <div className="p-6 bg-gray-100 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                                                <FiTruck className="w-12 h-12 text-gray-400" />
                                            </div>
                                            <p className="text-lg font-semibold text-gray-700 mb-2">No drivers available</p>
                                            <p className="text-sm text-gray-500">Add drivers to start assigning deliveries</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                            {drivers.map((driver) => {
                                                const workload = driverWorkloads[driver.id] || 0;
                                                // IMPORTANT: Check if THIS SPECIFIC QUOTE's delivery is already assigned to this driver
                                                const quoteType = draggedItemCache?.quote_info?.quote_type;
                                                const quoteDeliveryCount = draggedItemCache?.quote_info?.delivery_count ?? 0;
                                                const draggedQuoteId = draggedItemCache?.quote_info?.quoteId;
                                                const thisQuoteDelivery = draggedItemCache?.job_deliveries?.find(d =>
                                                    draggedQuoteId && d.child_unique_id
                                                        ? d.child_unique_id === draggedQuoteId
                                                        : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
                                                );
                                                const alreadyAssigned = thisQuoteDelivery?.assigned_driver_id === driver.id || false;

                                                return (
                                                    <DroppableDriverZone
                                                        key={driver.id}
                                                        id={`driver-${driver.id}`}
                                                        className={`border-2 rounded-2xl p-5 transition-all cursor-pointer ${alreadyAssigned
                                                            ? 'border-gray-300 bg-gray-50 opacity-50 cursor-not-allowed'
                                                            : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-lg'
                                                            }`}
                                                        disabled={alreadyAssigned}
                                                    >
                                                        <div className="pointer-events-none">
                                                            <div className="flex items-start justify-between mb-4">
                                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                                                                        <FiTruck className="w-6 h-6 text-blue-600" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="font-semibold text-base text-gray-900 truncate">
                                                                            {driver.full_name || driver.username}
                                                                        </p>
                                                                        <p className="text-sm text-gray-500 truncate">
                                                                            {driver.phone || 'No phone'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-xs font-bold px-3 py-1.5 rounded-full flex-shrink-0 ${workload === 0 ? 'bg-green-100 text-green-700' :
                                                                    workload < 5 ? 'bg-yellow-100 text-yellow-700' :
                                                                        'bg-red-100 text-red-700'
                                                                    }`}>
                                                                    {workload} {workload === 1 ? 'Job' : 'Jobs'}
                                                                </span>
                                                                <span className={`text-xs font-bold flex-shrink-0 ${workload === 0 ? 'text-green-700' :
                                                                    workload < 5 ? 'text-yellow-700' :
                                                                        'text-red-700'
                                                                    }`}>
                                                                    {workload === 0 ? "Available" : workload < 5 ? "Medium" : "High"} workload
                                                                </span>
                                                            </div>

                                                            {alreadyAssigned && (
                                                                <div className="mt-3 text-xs text-gray-500 text-center font-medium">
                                                                    Already assigned
                                                                </div>
                                                            )}
                                                        </div>
                                                    </DroppableDriverZone>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Drag Overlay */}
                <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={dropAnimationConfig}>
                    {(dragHandleType === 'group-date' || dragHandleType === 'group-driver') && Array.isArray(draggedItemCache) ? (() => {
                        const groupName = draggedItemCache[0]?.quote_info?.group_name || 'Group';
                        const jobCount = draggedItemCache.length;
                        return (
                            <div className="flex items-center gap-2.5 px-3.5 py-2.5 bg-indigo-600 text-white rounded-xl shadow-2xl pointer-events-none border border-indigo-400/40">
                                <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-white/20 flex-shrink-0">
                                    {dragHandleType === 'group-date'
                                        ? <FiCalendar className="w-3.5 h-3.5" />
                                        : <FiUsers className="w-3.5 h-3.5" />
                                    }
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="font-bold text-[13px] leading-tight truncate max-w-[160px]">{groupName}</span>
                                    <span className="text-indigo-200 text-[11px] leading-tight">
                                        {jobCount} {jobCount === 1 ? 'job' : 'jobs'} · {dragHandleType === 'group-date' ? 'drop on day' : 'drop on driver'}
                                    </span>
                                </div>
                            </div>
                        );
                    })() : draggedAssignment ? (() => {
                        const overlayQuoteStyle = getQuoteTypeStyle(draggedAssignment.quote_info?.quote_type);
                        const OverlayQuoteIcon = overlayQuoteStyle.icon;
                        return (
                            <div
                                className="bg-white border-2 border-teal-500 rounded-lg p-3 shadow-2xl pointer-events-none"
                                style={{ opacity: 0.9 }}
                            >
                                <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(draggedAssignment.main_status)}`}></div>

                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                        <span className="font-semibold text-gray-900 text-sm truncate">
                                            {draggedAssignment.unique_id}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${overlayQuoteStyle.bgColor} ${overlayQuoteStyle.textColor} flex-shrink-0`}>
                                            <OverlayQuoteIcon className="w-3 h-3" />
                                            {overlayQuoteStyle.label}
                                        </span>
                                    </div>
                                    {(draggedAssignment.urgent_quote || draggedAssignment.urgent_design) && (
                                        <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">
                                            URGENT
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 mb-3">
                                    <FiUser className="w-3 h-3 text-teal-500 flex-shrink-0" />
                                    <span className="text-sm text-gray-700 truncate font-medium">
                                        {draggedAssignment.customer_name}
                                    </span>
                                </div>
                            </div>
                        );
                    })() : null}
                </DragOverlay>


                {/* Note Modal - Rendered in portal to prevent re-renders */}
                {showNoteModal && createPortal(
                    <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">Delivery Note</h3>
                                    <button
                                        onClick={() => {
                                            setShowNoteModal(false);
                                            setCurrentEditingQuote(null);
                                            setNoteText('');
                                        }}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <FiX className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                                {currentEditingQuote && (
                                    <p className="text-sm text-gray-600 mt-2">
                                        Job: <span className="font-semibold">{currentEditingQuote.assignment.unique_id}</span>
                                        {' - '}
                                        <span className="capitalize">{currentEditingQuote.quoteType?.replace('_', ' ')}</span>
                                    </p>
                                )}
                            </div>
                            <div className="p-6">
                                <textarea
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Enter delivery note..."
                                    className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                    autoFocus
                                />
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowNoteModal(false);
                                        setCurrentEditingQuote(null);
                                        setNoteText('');
                                    }}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveNote}
                                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors font-medium"
                                >
                                    Save Note
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Time Slot Modal - Rendered in portal to prevent re-renders */}
                {showTimeSlotModal && createPortal(
                    <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">Delivery Time Slot</h3>
                                    <button
                                        onClick={() => {
                                            setShowTimeSlotModal(false);
                                            setCurrentEditingQuote(null);
                                            setSelectedTimeSlot(null);
                                        }}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <FiX className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                                {currentEditingQuote && (
                                    <p className="text-sm text-gray-600 mt-2">
                                        Job: <span className="font-semibold">{currentEditingQuote.assignment.unique_id}</span>
                                        {' - '}
                                        <span className="capitalize">{currentEditingQuote.quoteType?.replace('_', ' ')}</span>
                                    </p>
                                )}
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 gap-3">
                                    {['First Delivery', 'Second Delivery', 'Before Lunch', 'Lunchtime', 'After Lunch', 'Last Delivery'].map((slot) => (
                                        <button
                                            key={slot}
                                            onClick={() => handleSaveTimeSlot(slot)}
                                            className={`p-4 rounded-lg border-2 transition-all text-left font-medium ${selectedTimeSlot === slot || currentEditingQuote?.assignment?.quote_info?.delivery_time_slot === slot
                                                ? 'border-purple-600 bg-purple-50 text-purple-900'
                                                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-lg">{slot}</span>
                                                <FiClock className="w-5 h-5" />
                                            </div>
                                        </button>
                                    ))}

                                    {/* Reset/Clear option */}
                                    {currentEditingQuote?.assignment?.quote_info?.delivery_time_slot && (
                                        <button
                                            onClick={() => handleSaveTimeSlot(null)}
                                            className="p-4 rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 transition-all text-left font-medium text-red-700"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-lg">Clear Time Slot</span>
                                                <FiX className="w-5 h-5" />
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowTimeSlotModal(false);
                                        setCurrentEditingQuote(null);
                                        setSelectedTimeSlot(null);
                                    }}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Time Slot Modal - Rendered in portal to prevent re-renders */}
                {showDeliveryTagModal && createPortal(
                    <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">Set Delivery Tag</h3>
                                    <button
                                        onClick={() => {
                                            setShowDeliveryTagModal(false);
                                            setCurrentEditingQuote(null);
                                            setSelectedDeliveryTag(null);
                                        }}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <FiX className="w-5 h-5 text-gray-500" />
                                    </button>
                                </div>
                                {currentEditingQuote && (
                                    <p className="text-sm text-gray-600 mt-2">
                                        Job: <span className="font-semibold">{currentEditingQuote.assignment.unique_id}</span>
                                        {' - '}
                                        <span className="capitalize">{currentEditingQuote.quoteType?.replace('_', ' ')}</span>
                                    </p>
                                )}
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 gap-3">
                                    <button
                                        key={"confirmed"}
                                        onClick={() => handleSaveDeliveryTag("confirmed")}
                                        className={`p-4 rounded-lg border-2 transition-all text-left font-medium ${selectedDeliveryTag === "confirmed" || currentEditingQuote?.assignment?.quote_info?.delivery_tag === "confirmed"
                                            ? 'border-purple-600 bg-purple-50 text-purple-900'
                                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-lg">Confirmed</span>
                                            <CircleCheck className="w-5 h-5" />
                                        </div>
                                    </button>
                                    <button
                                        key={"doublecheck"}
                                        onClick={() => handleSaveDeliveryTag("doublecheck")}
                                        className={`p-4 rounded-lg border-2 transition-all text-left font-medium ${selectedDeliveryTag === "doublecheck" || currentEditingQuote?.assignment?.quote_info?.delivery_tag === "doublecheck"
                                            ? 'border-purple-600 bg-purple-50 text-purple-900'
                                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-lg">Double Check</span>
                                            <Flag className="w-5 h-5" />
                                        </div>
                                    </button>

                                    {/* Reset/Clear option */}
                                    {currentEditingQuote?.assignment?.quote_info?.delivery_tag && (
                                        <button
                                            onClick={() => handleSaveDeliveryTag(null)}
                                            className="p-4 rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 transition-all text-left font-medium text-red-700"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-lg">Clear Delivery Tag</span>
                                                <FiX className="w-5 h-5" />
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeliveryTagModal(false);
                                        setCurrentEditingQuote(null);
                                        setSelectedDeliveryTag(null);
                                    }}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}


                {/* Edit Assignment Modal */}
                {editOpen && editJobId && (
                    <EditAssignment
                        modalJobId={editJobId}
                        modalCompanySlug={companySlug}
                        onClose={() => {
                            setEditOpen(false);
                            setEditJobId(null);
                        }}
                    />
                )}

            </DndContext>
        </div>
    );
}

export default DeliverySchedule;
